import { useRef, useState, useEffect } from "react";
import { appendDrawnLine, nextLineTapState } from "../utils/lineTapState";
import { Box, ToggleButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Typography } from "@mui/material";
import { resolveQuestionHtmlAssetUrl } from "../../../api";
import ConstructGeometryBoard from "../../../components/measurement/ConstructGeometryBoard";

/**
 * Shared interactive answer widget for "drawing" questions, graded deterministically (no AI).
 * Renders the question image with an SVG overlay (viewBox 0..100, fractional coords) and supports:
 *   - kind 'shade'       : tap region polygons to fill them. value = array of region ids.
 *   - kind 'points'      : tap grid intersections to place points (snap). value = array of [gx,gy].
 *   - kind 'drag-shape'  : drag a copy of the original shape, snapping to the grid. value = vertices.
 *   - kind 'rect'        : tap two opposite grid corners to draw an axis-aligned rectangle. value = [[x1,y1],[x2,y2]].
 *   - kind 'lines'       : tap two points to draw each line; snaps to spec.grid dots when a 2D grid
 *                          is authored (dotty-paper tessellations); otherwise free fractional taps.
 *                          value = array of [[x1,y1],[x2,y2]].
 *   - kind 'tessellation-grid': same interaction as gridded lines, but semantically a dot-grid
 *                          edge-drawing task rather than symmetry / mirror lines.
 *
 * Same component is used by the student (editable), the verify/result reveal (readOnly + revealValue),
 * and the admin authoring editor (admin taps the correct answer = the value it stores as the target).
 */
const SHADE_FILL = "rgba(25,118,210,0.45)";
const REVEAL_FILL = "rgba(46,125,50,0.45)";
const REVEAL_STROKE = "#2e7d32";
const ACTIVE_STROKE = "#1976d2";
const GIVEN_FILL = "rgba(120,120,120,0.5)"; // pre-coloured "given" shade cells (locked, not tappable)
/**
 * Dense multi-cell shade overlays (polyomino / cm grids) draw every idle cell border in blue.
 * Below this count keep the stronger pie/sector guide stroke; at/above it, thin + fade idle
 * borders so printed black patterns stay readable (e.g. NLG 11+ 2016 Q41a, 18×6).
 */
const DENSE_SHADE_REGION_MIN = 24;

// Glyph symbols that look identical under any rotation or mirror — offering rotate/flip buttons for
// these is pure clutter since neither action changes the picture.
const ROTATION_INVARIANT_CHARS = new Set(["+", "○", "●", "•", "✕", "✖", "X", "×"]);

// Distinct fill/stroke pairs cycled across labelled region GROUPS (all regions sharing one `group`,
// or — when no group is authored — one colour per distinct `label` string) so a multi-region answer
// like a pie chart's activity wedges reads as visually separate captioned areas instead of one flat
// grey mass. Picked for contrast against both the grey GIVEN_FILL and each other.
const LABEL_GROUP_PALETTE = [
  { fill: "rgba(76,175,80,0.55)", stroke: "#2e7d32" },   // green
  { fill: "rgba(255,152,0,0.55)", stroke: "#e65100" },   // orange
  { fill: "rgba(156,39,176,0.5)", stroke: "#6a1b9a" },   // purple
  { fill: "rgba(3,169,244,0.5)", stroke: "#0277bd" },    // sky blue
  { fill: "rgba(233,30,99,0.5)", stroke: "#ad1457" },    // pink
  { fill: "rgba(205,220,57,0.55)", stroke: "#827717" },  // lime
  { fill: "rgba(0,150,136,0.5)", stroke: "#00695c" },    // teal
  { fill: "rgba(121,85,72,0.5)", stroke: "#4e342e" },    // brown
];

const eqPt = (a, b) => Number(a[0]) === Number(b[0]) && Number(a[1]) === Number(b[1]);

// Split a polygon (fractional coords) by the infinite line through a–b. Returns the two halves
// ordered [smaller-centroid-x first] or null when the line misses the polygon. Used by kind
// 'cut-drag': the student cuts the shape, then drags the pieces to reassemble them.
const splitPolyByLine = (poly, a, b) => {
  const side = (p) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
  const neg = [], pos = [];
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const p = poly[i], q = poly[(i + 1) % n];
    const sp = side(p), sq = side(q);
    if (sp <= 1e-9) neg.push(p);
    if (sp >= -1e-9) pos.push(p);
    if ((sp > 1e-9 && sq < -1e-9) || (sp < -1e-9 && sq > 1e-9)) {
      const t = sp / (sp - sq);
      const ix = [p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])];
      neg.push(ix); pos.push(ix);
    }
  }
  if (neg.length < 3 || pos.length < 3) return null;
  const cx = (pts2) => pts2.reduce((s, p) => s + p[0], 0) / pts2.length;
  return cx(neg) <= cx(pos) ? [neg, pos] : [pos, neg];
};

// Pictogram icon shapes, keyed by spec.icon. Each has its own viewBox, on-screen aspect (h/w),
// path, and the x-extent of the "left half" (for the 0.5 clip). Default is the bottle.
const PICTO_ICONS = {
  bottle: { vb: "0 0 24 60", aspect: 2.2, halfW: 12,
    d: "M9 3 L15 3 L15 11 C15 13 16 14 18 16 C19.5 17.5 20 19 20 22 L20 52 C20 55 18 57 15 57 L9 57 C6 57 4 55 4 52 L4 22 C4 19 4.5 17.5 6 16 C8 14 9 13 9 11 Z" },
  lorry: { vb: "0 0 60 32", aspect: 0.52, halfW: 30,
    d: "M3 6 L36 6 L36 14 L46 14 L52 20 L52 25 L3 25 Z M8 25 A3 3 0 1 0 8.01 25 Z M44 25 A3 3 0 1 0 44.01 25 Z" },
  circle: { vb: "0 0 40 40", aspect: 1, halfW: 20, d: "M20 3 A17 17 0 1 1 19.99 3 Z" },
  star: { vb: "0 0 40 40", aspect: 1, halfW: 20,
    d: "M20 2 L25 15 L39 15 L28 24 L32 38 L20 30 L8 38 L12 24 L1 15 L15 15 Z" },
  square: { vb: "0 0 40 40", aspect: 1, halfW: 20, d: "M4 4 L36 4 L36 36 L4 36 Z" },
  // Arm/crossbar proportions traced from the ISEB sports-injury key so the drawn icon matches the
  // ones already printed in the paper's table.
  cross: { vb: "0 0 34 34", aspect: 1, halfW: 17,
    d: "M12 1 L22 1 L22 10 L33 10 L33 24 L22 24 L22 33 L12 33 L12 24 L1 24 L1 10 L12 10 Z" },
};
const pictoIcon = (key) => PICTO_ICONS[key] || PICTO_ICONS.bottle;
// Part-filled icons are drawn by quadrant, matching how the printed papers draw them: a half is the
// left half, a quarter the top-left quadrant, three-quarters everything but the bottom-right.
const PICTO_FILLS = [1, 0.75, 0.5, 0.25];
const PICTO_CYCLE = [0, 1, 0.75, 0.5, 0.25];
const pictoClipRects = (fill, halfW, halfH) => {
  if (fill === 0.25) return [[0, 0, halfW, halfH]];
  if (fill === 0.5) return [[0, 0, halfW, halfH * 2]];
  if (fill === 0.75) return [[0, 0, halfW, halfH * 2], [halfW, 0, halfW, halfH]];
  return null;
};

/** Signed shortest delta from angle `from` to `to`, in (-π, π]. Image y-down atan2. */
export const markerAngleDelta = (from, to) => {
  let d = to - from;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d <= -Math.PI) d += 2 * Math.PI;
  return d;
};

// A gauge's printed scale is a curve, and two different curves turn up in the papers. A true dial's ticks
// sit on a circle about the needle hub, so `pivot` plus the two end ticks fix it. A shallow printed scale
// (the water-tank gauges) is an arc whose centre is nowhere near the hub — a hub-centred circle misses the
// printed curve badly at the ends, so the arrow can never be put ON the scale. For those the author also
// taps a third point on the curve (`arc`, the middle tick) and the circle is fitted through the three
// ticks instead; the hub is then only where the needle is drawn from. Fractional x/y are not isotropic on
// a non-square figure, so everything is fitted in x*aspect space to match the printed pixel angles.
export const fitMarkerScale = (spec, aspectFallback = 1) => {
  const axis = Array.isArray(spec?.axis) && spec.axis.length >= 2 && Array.isArray(spec.axis[0]) && Array.isArray(spec.axis[1])
    ? spec.axis
    : null;
  if (!axis) return null;
  const aspect = Number(spec?.aspect) > 0 ? Number(spec.aspect) : (Number(aspectFallback) > 0 ? Number(aspectFallback) : 1);
  const A = [Number(axis[0][0]) * aspect, Number(axis[0][1])];
  const B = [Number(axis[1][0]) * aspect, Number(axis[1][1])];
  const via = Array.isArray(spec?.arc) && spec.arc.length >= 2 && Number.isFinite(Number(spec.arc[0]))
    ? [Number(spec.arc[0]) * aspect, Number(spec.arc[1])]
    : null;
  if (via) {
    const det = 2 * (A[0] * (via[1] - B[1]) + via[0] * (B[1] - A[1]) + B[0] * (A[1] - via[1]));
    if (Math.abs(det) < 1e-9) return null; // three collinear taps — a straight scale, not an arc
    const sA = A[0] * A[0] + A[1] * A[1], sV = via[0] * via[0] + via[1] * via[1], sB = B[0] * B[0] + B[1] * B[1];
    const cx = (sA * (via[1] - B[1]) + sV * (B[1] - A[1]) + sB * (A[1] - via[1])) / det;
    const cy = (sA * (B[0] - via[0]) + sV * (A[0] - B[0]) + sB * (via[0] - A[0])) / det;
    const r = Math.hypot(A[0] - cx, A[1] - cy);
    if (!(r > 0)) return null;
    const a0 = Math.atan2(A[1] - cy, A[0] - cx);
    const mid = markerAngleDelta(a0, Math.atan2(via[1] - cy, via[0] - cx));
    let span = markerAngleDelta(a0, Math.atan2(B[1] - cy, B[0] - cx));
    if (Math.abs(span) < 1e-12) return null;
    // The middle tick also says which way round the circle the scale runs: when it does not lie between
    // the ends on the short way, the scale is the reflex arc.
    if (Math.sign(mid) !== Math.sign(span) || Math.abs(mid) > Math.abs(span)) {
      span -= Math.sign(span) * 2 * Math.PI;
    }
    return { cx, cy, r, a0, span, aspect };
  }
  const pivot = Array.isArray(spec?.pivot) && spec.pivot.length >= 2 ? spec.pivot : null;
  if (!pivot) return null;
  const cx = Number(pivot[0]) * aspect, cy = Number(pivot[1]);
  const r = Math.hypot(A[0] - cx, A[1] - cy) || 0.4;
  const a0 = Math.atan2(A[1] - cy, A[0] - cx);
  const span = markerAngleDelta(a0, Math.atan2(B[1] - cy, B[0] - cx));
  if (Math.abs(span) < 1e-12) return null;
  return { cx, cy, r, a0, span, aspect };
};

/** Fraction along the scale (0..1) of a tapped fractional point. */
export const markerScaleT = (fit, p) => Math.max(0, Math.min(1,
  markerAngleDelta(fit.a0, Math.atan2(Number(p[1]) - fit.cy, Number(p[0]) * fit.aspect - fit.cx)) / fit.span));

/** The point on the scale at fraction t, back in image fractions. */
export const markerScalePointAt = (fit, t) => {
  const ang = fit.a0 + Math.max(0, Math.min(1, t)) * fit.span;
  return [(fit.cx + fit.r * Math.cos(ang)) / fit.aspect, fit.cy + fit.r * Math.sin(ang)];
};

const InteractiveDiagramCanvasInner = ({
  spec,
  imageUrl,
  value,
  onChange,
  readOnly = false,
  // Student take only. A short drag along a symmetry line is extended across the figure.
  // Off in the question editor, where the stroke being drawn is the stored mark scheme.
  quickSymmetryLines = false,
  revealValue = null,
  // The colour this part's own marks are drawn in. On a shared grid every part has its own colour and
  // the legend names them, so the part holding the tools must be drawn in ITS colour — painting it the
  // generic blue made it indistinguishable from whichever part owns the blue swatch, and the marks
  // appeared to swap parts as the student switched (Merchant Taylors 2023 Q16 a/b).
  activeColor = null,
  gridMovable = false,
  onMoveGrid = null,
  // Editor-only: faint lines through the grid dots so an author can check the overlay against the
  // printed lines / dotted paper. Never set on student pages.
  showGridLines = false,
  onToggleGiven = null, // editor-only: tap any shade region to flag/unflag it as pre-coloured "given"
  onCellChange = null, // editor-only: (index, {x,y,w,h}) — index -1 appends a new tally cell
  cellPlaceMode = false, // editor-only: click-drag on the image to draw a new tally cell
  maxWidth = null, // cap the rendered width (px) so grids aren't huge on desktop; still full-width on mobile
  // Other sub-questions' committed work on a shared grid, drawn read-only in one colour per part so the
  // student can tell which part each mark belongs to. Each entry:
  // { id, label, color, points:[[gx,gy,lbl?]], edges:[[[gx,gy],[gx,gy]]], lines:[[[fx,fy],[fx,fy]]] }
  contextLayers = null,
}) => {
  const activeStroke = activeColor || ACTIVE_STROKE;

  const wrapRef = useRef(null);
  const uid = useRef(Math.random().toString(36).slice(2, 8)).current; // unique clipPath id prefix
  const tallyClickTimerRef = useRef(null);
  const tallyLastTapRef = useRef({ t: 0, index: -1 });
  const tallyCountsRef = useRef([]);
  const tallyLastIndexRef = useRef(0);
  const tallyCellDragRef = useRef(null); // { mode, index, corner?, startX, startY, orig, moved }
  const [drag, setDrag] = useState(null); // { startX, startY, baseOffset:[ox,oy] }
  const [gridDrag, setGridDrag] = useState(null); // { lastX, lastY } while dragging the whole grid
  const [imgFailed, setImgFailed] = useState(false);
  const [usedRaw, setUsedRaw] = useState(false);
  const [pendingPt, setPendingPt] = useState(null); // first tapped endpoint of an in-progress line (kind 'lines')
  const pendingPtRef = useRef(null);
  pendingPtRef.current = pendingPt;
  const lineGestureRef = useRef(null);
  const lineSkipClickRef = useRef(false);
  const [dragLineEnd, setDragLineEnd] = useState(null);
  const [pendingNode, setPendingNode] = useState(null); // first tapped node index of an in-progress join (kind 'segments')
  const [shadeTool, setShadeTool] = useState("shade"); // shade+lines questions: which of the two tools is active
  const [box, setBox] = useState({ w: 0, h: 0 }); // rendered size, for sizing HTML glyph overlays in px
  const [selGlyph, setSelGlyph] = useState(null); // index of the glyph the single shared toolbar edits
  const [clockDrag, setClockDrag] = useState(null); // 'hour' | 'minute' while dragging a clock hand
  const [showConnect, setShowConnect] = useState(true); // reflect/rotate: join the placed corners with an outline
  const [manualFill, setManualFill] = useState(false); // reflect/rotate: colour the finished shape in
  // When labels are REQUIRED, tapping a point opens a modal that forces the student to type its label
  // (the inline on-grid box is too easy to miss — students plotted correctly but left it blank). Holds
  // { index, value, isNew } for the point being labelled; a blank confirm on a new point removes it.
  const [labelPrompt, setLabelPrompt] = useState(null);
  // "Label the triangle A": after the shape is complete, force a popup for the shape's letter (not a
  // corner). Holds { value } while open; empty means not prompting.
  const [shapeLabelPrompt, setShapeLabelPrompt] = useState(null);
  const shapeLabelPromptedRef = useRef(false);
  // Letter-entry cells ("write C in this section"): tapping the shape opens a forced popup for its
  // letter, same pattern as the point/shape label prompts above — an always-visible floating input
  // box was too easy to mis-attribute to the wrong region when several sit close together in one figure.
  // Holds { id, value } while open.
  const [letterPrompt, setLetterPrompt] = useState(null);
  // Multi-group shading: which category the next tapped region gets coloured as. Defaults to the first.
  const [activeGroup, setActiveGroup] = useState(null);
  // Manual edge drawing for transform/polygon shapes: auto-connect can't reconstruct a concave outline
  // (an L / arrow) from an unordered corner set, so the student draws each edge by hand. No mode toggle
  // — corners are always tappable handles: tap one (it highlights) then another to join them; tap an
  // empty grid spot to add a corner; tap a drawn edge to remove it. Edges are a VISUAL AID only; grading
  // still uses the corner set, so edges live in local state (undirected corner-coord keys) and never
  // change the submitted value.
  const [edgePending, setEdgePending] = useState(null); // index of the first tapped corner, or null
  const [manualEdgesLocal, setManualEdgesLocal] = useState([]); // ["gx,gy|gx,gy", ...] local mirror of value.edges
  // Source of truth for the armed corner during an event: state reads are stale if one tap reaches two
  // handlers in the same event (both would read the pre-tap value and the second call re-toggles,
  // undoing the first). The ref updates synchronously so a duplicate call is harmless; edgePending
  // state mirrors it purely for rendering the highlight.
  const edgePendingRef = useRef(null);
  /** Drag-to-paint for shade: first cell sets add vs remove; later cells follow that mode. */
  const shadePaintRef = useRef(null); // { mode: 'add'|'remove', lastId: string|null }
  const shadeSkipClickRef = useRef(false);
  /** Latest shade entries during a paint gesture — avoids stale closure when dragging across cells. */
  const shadeEntriesRef = useRef([]);

  const kind = spec?.kind || "shade";
  const regions = Array.isArray(spec?.regions) ? spec.regions : [];
  const grid = spec?.grid || null;
  const connect = spec?.connect !== false;
  const isRect = kind === "rect";
  const isPoly = kind === "polygon";
  const isGlyph = kind === "glyph";
  // reflect/rotate place the transformed shape's vertices exactly like 'points', with the original
  // shape + mirror line / rotation centre drawn as a guide overlay on top.
  const isReflect = kind === "reflect";
  const isRotate = kind === "rotate";
  const isTransform = isReflect || isRotate;
  // Kinds where the student places every corner first, then explicitly joins them and colours the
  // shape in with the toolbar (instead of the outline auto-drawing/auto-filling on each tap). This
  // keeps tap order — so a concave shape traced corner-by-corner fills cleanly — and never shows a
  // half-drawn bowtie mid-placement. reflect/rotate already worked this way; points/polygon join it.
  const isClock = kind === "clock";
  const isMarker = kind === "marker";
  // "Complete the drawing to show an angle of 157°": one printed vertex, an arm already on the figure
  // (given_ray) or none, and the student drags/taps out the arm(s). Only the ANGLE between the arms is
  // graded, so arm length is free — see gradeInteractiveAngle.
  const isAngle = kind === "angle";
  const asPoint = (p) => (Array.isArray(p) && p.length >= 2 && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1]))
    ? [Number(p[0]), Number(p[1])]
    : null);
  const angleVertex = isAngle ? asPoint(spec?.vertex) : null;
  const angleGivenRay = isAngle ? asPoint(spec?.given_ray) : null;
  // With an arm already printed the student draws the other one; with none they draw both.
  const angleRayCount = isAngle
    ? Math.max(1, Math.round(Number(spec?.ray_count) || (angleGivenRay ? 1 : 2)))
    : 0;
  const angleRaysOf = (v) => (v && !Array.isArray(v) && Array.isArray(v.rays)
    ? v.rays.map(asPoint).filter(Boolean)
    : []);
  const angleRays = isAngle ? angleRaysOf(value) : [];
  const revealAngleRays = isAngle && revealValue ? angleRaysOf(revealValue) : [];
  // polygon draws a closed shape by tapping grid points, graded by area — renders like connected points.
  // The cap on how many points can be placed: point_count when authored, else (for reflect/rotate) the
  // number of vertices in the original shape — so a reflect can never receive an extra stray point even
  // if point_count was omitted from the spec.
  // grade_as_line grades the line the marks describe, not how many marks were made, so the cap that
  // stops further taps once point_count is reached must not apply — the student draws the line with as
  // many points as they need.
  const pointCount = isRect ? 2
    : (spec?.grade_as_line ? 0
      : (Number(spec?.point_count) || (isTransform ? (Array.isArray(spec?.original_shape) ? spec.original_shape.length : 0) : 0)));
  // A polygon question can ask for several separate closed shapes on one grid ("sketch the next two
  // rectangles"): shape_count>1 means the placed points are grouped into consecutive runs of
  // (point_count / shape_count) corners, each drawn/filled as its own quad rather than one big polygon.
  // Open 'points' plots use the same grouping to draw SEPARATE lines ("join K to L, then reflect that
  // line in p" = two 2-point lines): without it the four plotted points chain into one zig-zag.
  const shapeCount = (isPoly || kind === "points") ? Math.max(1, Math.round(Number(spec?.shape_count) || 1)) : 1;
  const minPerShape = spec?.closed === false ? 2 : 3;
  const cornersPerShape = shapeCount > 1 && pointCount > 0 ? Math.max(minPerShape, Math.round(pointCount / shapeCount)) : pointCount;
  const original = Array.isArray(spec?.original_shape) ? spec.original_shape : [];
  // Read-only guide points carried over from an earlier part of the same grouped question that shares
  // this grid (e.g. part b plotted A/B/C; part c reflects them on the SAME figure). Each entry is
  // [gx, gy, label?] in grid coords; drawn grey, never tappable, never graded.
  // given_points are corners already PRINTED on the diagram (kite ABCD's A and C, drawn on the paper).
  // The student joins to them but never plots them — same read-only, joinable treatment as a carried-over
  // context point, but authored on the question and part of the graded shape.
  const contextPoints = [
    ...(Array.isArray(spec?.given_points) ? spec.given_points : []),
    ...(Array.isArray(spec?.context_points) ? spec.context_points : []),
  ].filter((p) => Array.isArray(p) && p.length >= 2);
  // The guide outline has to VISIT the printed corners in cyclic order. given_points are authored in
  // label order ("A, B, C, D"), which for the rectangle A(2,0) B(0,4) D(8,8) C(10,4) is a self-crossing
  // bowtie — James Allen's 2025 Q12(d) drew a shaded cross straight through the middle of the
  // rectangle. Sorting by angle about the centroid recovers the simple polygon for any convex figure
  // (the ones this guide is drawn for) and leaves an already-cyclic list in place. Same ordering
  // interactiveRevealValue's orderClosedBoundary applies to the model answer.
  const cyclicBoundary = (points) => {
    if (points.length < 3) return points;
    const cx = points.reduce((t, p) => t + Number(p[0]), 0) / points.length;
    const cy = points.reduce((t, p) => t + Number(p[1]), 0) / points.length;
    return [...points].sort(
      (a, b) => Math.atan2(Number(a[1]) - cy, Number(a[0]) - cx) - Math.atan2(Number(b[1]) - cy, Number(b[0]) - cx)
    );
  };
  const pointLabels = Array.isArray(spec?.point_labels) ? spec.point_labels : [];
  // How many points the question NAMES. When the labels are graded the server sends only this count
  // (the labels themselves are the answer — see interactiveStudentSpec), so read it first and fall back
  // to counting the authored labels for the admin editor, which holds the full spec.
  const namedPointCount = Number.isFinite(Number(spec?.point_label_count))
    ? Number(spec.point_label_count)
    : pointLabels.filter(Boolean).length;
  // reflect/rotate answers are just corner placements — labelled only when the spec opts in
  // (allow_labels: true), e.g. "reflect the shape and label the new shape B". Plot-point / drag-shape
  // questions keep labels on by default.
  const allowLabels = spec?.allow_labels === true || (!isTransform && spec?.allow_labels !== false);
  // Label is graded, not optional: tapping a point opens a forced label prompt instead of the easy-to-
  // miss inline box. Only for tappable point kinds (not transforms, which label the whole shape).
  // Mirror backend shouldEnforcePlotPointLabels: plot-only (connect:false) with named corners must
  // open the Dialog even when require_point_labels was never backfilled on the authored spec.
  const enforcePlotPointLabels =
    kind === "points" &&
    spec?.connect === false &&
    namedPointCount > 0;
  const labelRequired =
    allowLabels &&
    (kind === "points" || kind === "drag-shape" || isPoly) &&
    (!!spec?.require_point_labels || enforcePlotPointLabels);
  // Whole-shape letter ("Label the triangle A" / "Label your shape B") — separate from corner labels.
  const requireShapeLabel = !!spec?.require_shape_label && (kind === "points" || kind === "drag-shape" || isPoly || isTransform);
  // grade_as_line answers ARE an open line ("draw the line showing Autohire's cost"), so they are
  // never closed back on themselves even when the author left `closed` at its default.
  const closed = spec?.closed !== false && !spec?.grade_as_line;
  // Open lines anchored to a point already GIVEN on the diagram — e.g. Perse "complete rectangle
  // ABCD" (plot D, draw AD/DC). Authoring uses connect:false so tap-order does not auto-join;
  // that must NOT disable hand-drawn edges when join_context_edges / required_edges is set.
  // `required_edges` as an array (even []) is the opt-in signal, not its length: the admin authoring
  // editor starts one at [] the moment a given point is added, before any edge has been drawn yet, and
  // needs this mode live immediately in order to draw the first one.
  // `connect` is deliberately NOT consulted: it means "auto-join the plotted points in tap order", which
  // a join-to-given question switches OFF precisely because the student draws the lines by hand. Gating
  // on it removed the only way to draw them, so every such spec authored with connect:false graded as
  // "the lines have not been drawn" no matter what the student did.
  // An OPEN figure whose whole outline is graded — "reflect this zig-zag": 8 corners, 7 edges, the two
  // ends never meet — names its lines in required_edges but has no printed corner to anchor to. Gating
  // the edge tool on contextPoints left those specs with no way to draw the outline at all, so they had
  // to be authored closed, and the canvas then auto-closed and filled them into a different shape than
  // the one being reflected.
  // The student spec never carries required_edges (it is answer geometry) — interactiveStudentSpec
  // replaces it with required_edge_count, so both spellings count here.
  const drawsOwnRequiredEdges = kind === "points"
    && ((Array.isArray(spec?.required_edges) && spec.required_edges.length > 0)
      || Number(spec?.required_edge_count) > 0);
  const joinContextEdges = kind === "points"
    && (contextPoints.length > 0 || drawsOwnRequiredEdges)
    && (Array.isArray(spec?.required_edges) || spec?.join_context_edges === true);
  // points/polygon that draw a closed shape get the manual Connect/Fill workflow too. Open plots
  // (connect:false, i.e. "just mark these points, tap order carries meaning") keep auto-connecting.
  const isManualFill = isTransform || ((isPoly || (kind === "points" && connect)) && closed) || joinContextEdges;
  // Manual edge-drawing is offered on single closed shapes where auto-ordering can go wrong (a concave
  // reflection/rotation/polygon). Multi-shape polygons ("sketch N rectangles") stay on tap-order
  // chunking — their corners split into separate quads, which hand-drawn edges can't express.
  const edgesAvailable = isManualFill && shapeCount === 1;
  // "Fill in" colours the enclosed region, which only exists for a closed shape — an open join-to-context
  // line (joinContextEdges) has no area, so it must not offer the toggle at all.
  const fillEligible = isManualFill && closed;
  const cornerKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
  const edgeKey = (a, b) => [cornerKey(a), cornerKey(b)].sort().join("|");
  const isLines = kind === "lines" || kind === "tessellation-grid";
  const isLine = (l) => Array.isArray(l) && l.length >= 2 && Array.isArray(l[0]) && Array.isArray(l[1]);
  // "Shade one square to give one line of symmetry AND draw on the line of symmetry" — one question
  // asking for both drawings. The line rides in the same value array as { line: [[x,y],[x,y]] } so the
  // shaded-region entries (plain ids / {id,label}) are untouched by it.
  const shadeLinesSpec = kind === "shade" && spec?.lines && typeof spec.lines === "object" && !Array.isArray(spec.lines)
    ? spec.lines
    : null;
  const lineEntriesOf = (v) => (Array.isArray(v) ? v : []).filter((e) => e && !Array.isArray(e) && isLine(e.line)).map((e) => e.line);
  // The admin editor renders the FULL spec (target_lines present) — the drawing tool is for the
  // student spec only, so an authoring tap can never write a {line} entry into target_regions.
  const shadeLineTool = !!shadeLinesSpec && !Array.isArray(spec.lines.target_lines);
  const lineToolActive = isLines || (shadeLineTool && shadeTool === "line");
  // "Draw any lines of symmetry — some shapes may have none." Drawing nothing is the answer on such a
  // shape, and an empty array cannot be told apart from a canvas the student never touched, so the
  // declaration rides on the value as { lines: [...], none: true }. Both shapes are accepted here.
  const linesOf = (v) => (Array.isArray(v)
    ? v.filter(isLine)
    : (v && Array.isArray(v.lines) ? v.lines.filter(isLine) : []));
  const placedLines = shadeLinesSpec ? lineEntriesOf(value) : (isLines ? linesOf(value) : []);
  const revealLines = shadeLinesSpec
    ? (Array.isArray(revealValue) ? lineEntriesOf(revealValue) : null)
    : (isLines && (Array.isArray(revealValue) || revealValue?.lines) ? linesOf(revealValue) : null);
  // The reveal for a "no lines of symmetry" shape is an empty line list, so the panel needs to SAY so —
  // an empty canvas next to the words "Correct solution" reads as a rendering failure.
  const declaredNone = isLines && !!value && !Array.isArray(value) && value.none === true;
  const noneAllowed = kind === "lines" && spec?.allow_none !== false && !shadeLinesSpec;
  const noneLabel = typeof spec?.none_label === "string" && spec.none_label.trim() !== ""
    ? spec.none_label.trim()
    : (/symmetr/i.test(String(spec?.instructions ?? "")) ? "No lines of symmetry" : "Nothing to draw");
  // cut-drag: the student cuts spec.shape (fractional polygon) with one straight line, the shape
  // splits into two pieces, and they drag a piece so the two reassemble into the target shape
  // (graded server-side on the pieces' relative displacement = the shape's base vector BL→BR).
  // value = { cut: [[x1,y1],[x2,y2]], offsets: [[dxL,dyL],[dxR,dyR]] } (offsets ordered left, right).
  const isCutDrag = kind === "cut-drag";
  const cdShape = isCutDrag && Array.isArray(spec?.shape)
    ? spec.shape.filter((p) => Array.isArray(p) && p.length >= 2).map((p) => [Number(p[0]), Number(p[1])])
    : [];
  const cdValue = isCutDrag && value && !Array.isArray(value) && Array.isArray(value.cut) && isLine(value.cut) ? value : null;
  const cdReveal = isCutDrag && revealValue && !Array.isArray(revealValue) && Array.isArray(revealValue.cut) && isLine(revealValue.cut) ? revealValue : null;
  const cdShow = cdReveal || cdValue;
  const cdPieces = cdShow && cdShape.length >= 3 ? splitPolyByLine(cdShape, cdShow.cut[0], cdShow.cut[1]) : null;
  const cdOffsets = cdShow && Array.isArray(cdShow.offsets) && cdShow.offsets.length >= 2 ? cdShow.offsets : [[0, 0], [0, 0]];
  const cdBase = cdShape.length >= 4 ? [cdShape[2][0] - cdShape[3][0], cdShape[2][1] - cdShape[3][1]] : [0, 0];
  const cdSnapTol = Number(spec?.snap_tol) || 0.05;
  // segments: fixed nodes (crosses) drawn on the image; the student joins pairs with straight lines.
  const isSegments = kind === "segments";
  const nodes = isSegments && Array.isArray(spec?.nodes) ? spec.nodes.filter((n) => Array.isArray(n) && n.length >= 2) : [];
  const placedSegs = isSegments && Array.isArray(value) ? value.filter(isLine) : [];
  const revealSegs = isSegments && Array.isArray(revealValue) ? revealValue.filter(isLine) : null;
  // pictogram: a row of icon slots the student fills in quarters (0 / 0.25 / 0.5 / 0.75 / 1). value = fills.
  const isPictogram = kind === "pictogram";
  // tally: single tap adds a mark; double tap removes one. value = [count, …] aligned with cells.
  const isTally = kind === "tally";
  const tallyGroup = Math.max(2, Math.round(Number(spec?.group_size) || 5));
  const tallyCells = (() => {
    if (!isTally) return [];
    if (Array.isArray(spec?.cells) && spec.cells.length > 0) {
      return spec.cells.filter((c) => c && typeof c === "object").map((c) => ({
        x: Number(c.x) || 0,
        y: Number(c.y) || 0,
        w: Math.max(0.02, Number(c.w) || 0.12),
        h: Math.max(0.02, Number(c.h) || 0.3),
        max_marks: Math.max(1, Math.round(Number(c.max_marks) || Number(spec?.max_marks) || 20)),
      }));
    }
    if (spec?.cell && typeof spec.cell === "object") {
      return [{
        x: Number(spec.cell.x) || 0,
        y: Number(spec.cell.y) || 0,
        w: Math.max(0.02, Number(spec.cell.w) || 0.12),
        h: Math.max(0.02, Number(spec.cell.h) || 0.3),
        max_marks: Math.max(1, Math.round(Number(spec?.max_marks) || Number(spec?.target_marks) || 20)),
      }];
    }
    return [];
  })();
  const tallyCounts = isTally
    ? tallyCells.map((c, i) => {
      const raw = Array.isArray(value) ? value[i] : (i === 0 ? value : 0);
      return Math.max(0, Math.min(c.max_marks, Math.round(Number(raw) || 0)));
    })
    : [];
  tallyCountsRef.current = tallyCounts;
  const revealTallyCounts = isTally && revealValue != null
    ? tallyCells.map((_, i) => Math.max(0, Math.round(Number(Array.isArray(revealValue) ? revealValue[i] : (i === 0 ? revealValue : 0)) || 0)))
    : null;
  const pictoRow = spec?.row || {};
  const pictoCount = Math.max(0, Math.round(Number(pictoRow.count) || 0));
  const normFill = (v) => (PICTO_FILLS.includes(v) ? v : 0);
  const pictoStates = isPictogram ? Array.from({ length: pictoCount }, (_, i) => normFill(Number(Array.isArray(value) ? value[i] : 0))) : [];
  const revealPicto = isPictogram && Array.isArray(revealValue) ? Array.from({ length: pictoCount }, (_, i) => normFill(Number(revealValue[i]))) : null;

  const src = imageUrl || spec?.image_url || "";
  const resolved = resolveQuestionHtmlAssetUrl(src) || src;
  const activeSrc = usedRaw ? src : resolved;

  useEffect(() => { setImgFailed(false); setUsedRaw(false); }, [src]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [src]);

  // Plain shade questions store value as an array of region-id strings. `require_labels` shade
  // questions (e.g. proportional pie sectors the student must shade AND caption) instead store
  // `[{id, label}, ...]` so each shaded region's typed text travels with it.
  const requireLabels = kind === "shade" && !!spec?.require_labels;
  // `label_only` shade questions: the picture already shows the correct shading/division (e.g. a pie
  // chart whose wedges are all pre-drawn) — every region is `given` and there is nothing to tap. The
  // student's only task is to type the caption for each labelled region, so label boxes show up
  // immediately (not gated behind shading a region first) for every `given` region that carries a
  // `spec.regions[].label`.
  const labelOnly = kind === "shade" && !!spec?.label_only;
  // Multi-group shading ("complete the pie chart"): the student picks a category, then taps regions to
  // colour them in that category's colour — working each category's SIZE out of a data table. Each
  // category gets its own palette colour so the finished chart reads as distinct labelled sectors.
  // value = [{id, group}, …]. spec.groups = [{key, label}] (the answer — how many regions each takes —
  // is stripped server-side).
  const shadeGroups = kind === "shade" && Array.isArray(spec?.groups) ? spec.groups.filter((g) => g && g.key) : [];
  const isGroupShade = shadeGroups.length > 0;
  // A region id, or a { id, label?, group? } entry. Anything else is a value stored for a DIFFERENT kind
  // (a question re-authored from points to shade keeps its old [[col,row], …] placements): those entries
  // can't name a region, and echoing them back into the next onChange submitted arrays where the grader
  // expects ids.
  const shadedEntries = (Array.isArray(value) ? value : []).filter(
    (v) => typeof v === "string" || typeof v === "number" || (v && typeof v === "object" && !Array.isArray(v) && v.id != null)
  );
  shadeEntriesRef.current = shadedEntries;
  // `letter_regions`: regions that take a TYPED LETTER without being shaded ("write C in this section,
  // shade that one"). Their {id,label} entries live in the same value array but never count as shading.
  const letterRegionIds = new Set(
    (Array.isArray(spec?.letter_regions) ? spec.letter_regions : []).map((lr) => String(lr?.id))
  );
  // `char` is stripped from the student-facing spec; it survives only in the full reveal spec
  // (correct_interactive), where it supplies the letter the correct-solution canvas must show.
  const letterRegionChar = Object.fromEntries(
    (Array.isArray(spec?.letter_regions) ? spec.letter_regions : [])
      .filter((lr) => lr && lr.char != null)
      .map((lr) => [String(lr.id), String(lr.char)])
  );
  // Optional per-region authoring overrides (`color`, `size`) so a specific letter-entry cell can be
  // made visually distinct from the default blue box — e.g. when several regions in one figure need
  // different treatment and the shared group colour isn't enough to tell them apart at a glance.
  const letterRegionStyle = Object.fromEntries(
    (Array.isArray(spec?.letter_regions) ? spec.letter_regions : [])
      .filter((lr) => lr && (lr.color || lr.size))
      .map((lr) => [String(lr.id), { color: lr.color || null, size: Number(lr.size) || null }])
  );
  const idOf = (v) => String(v && typeof v === "object" ? v.id : v);
  // A letter region's entry is a typed annotation, not shading — keep it out of the shaded set so its
  // cell never fills and its tap never toggles.
  const shadedIds = shadedEntries.filter((v) => !letterRegionIds.has(idOf(v))).map(idOf);
  const shadeLabels = (requireLabels || labelOnly || letterRegionIds.size > 0)
    ? Object.fromEntries(shadedEntries.filter((v) => v && typeof v === "object").map((v) => [idOf(v), v.label || ""]))
    : {};
  // region id -> the group the student assigned it to (group-shade mode).
  const groupOfRegion = isGroupShade
    ? Object.fromEntries(shadedEntries.filter((v) => v && typeof v === "object" && v.group).map((v) => [idOf(v), String(v.group)]))
    : {};
  const groupColorByKey = Object.fromEntries(
    shadeGroups.map((g, i) => [String(g.key), LABEL_GROUP_PALETTE[i % LABEL_GROUP_PALETTE.length]])
  );
  const revealIds = Array.isArray(revealValue)
    ? revealValue.filter((v) => !(v && !Array.isArray(v) && v.line)).map(idOf)
    : null;
  // Shade writes go through here so a drawn line survives every region tap (and vice versa).
  const emitShade = (entries) => {
    if (!onChange) return;
    onChange(shadeLinesSpec ? [...entries, ...placedLines.map((l) => ({ line: l }))] : entries);
  };
  const emitLines = (lines) => {
    if (!onChange) return;
    if (shadeLinesSpec) {
      onChange([...shadedEntries, ...lines.map((l) => ({ line: l }))]);
      return;
    }
    // Drawing a line withdraws a "there are none" declaration — they are opposite answers.
    onChange(noneAllowed && declaredNone && lines.length > 0 ? { lines, none: false } : lines);
  };
  const toggleNoneDeclared = () => {
    if (readOnly || !onChange) return;
    setPendingPt(null);
    onChange(declaredNone ? [] : { lines: [], none: true });
  };
  // The reveal (correct answer) for group-shade carries its own assignment, so the solution renders
  // with the same per-category colours the student was working with.
  const revealGroupOfRegion = isGroupShade && Array.isArray(revealValue)
    ? Object.fromEntries(revealValue.filter((v) => v && typeof v === "object" && v.group).map((v) => [idOf(v), String(v.group)]))
    : {};
  // Transform/closed-polygon edge shapes now carry the student-drawn outline in the value so it can be
  // graded (the outline is the answer, not just the corners). Shape: { points:[[x,y]…], edges:["gx,gy|gx,gy"…] }.
  // Legacy answers are a bare point array (no edges) and still read correctly here. shape_label rides on
  // the same object when the question asks the student to name the finished shape ("Label triangle A").
  const valueHasEdges = value && !Array.isArray(value) && Array.isArray(value.points);
  const valueHasShapeLabel = value && !Array.isArray(value) && typeof value.shape_label === "string";
  const placedPts = valueHasEdges
    ? value.points.filter((v) => Array.isArray(v))
    : (Array.isArray(value) && value.every((v) => Array.isArray(v)) ? value : []);
  const currentShapeLabel = valueHasShapeLabel
    ? value.shape_label.trim()
    : "";
  // A reveal value may be a bare point array OR the { points, edges } object an edge-drawn shape submits
  // (the review page replays the student's own drawing through revealValue). Unwrap both; the edges are
  // drawn as the revealed outline below.
  const revealRaw = revealValue && !Array.isArray(revealValue) && Array.isArray(revealValue.points)
    ? revealValue.points
    : revealValue;
  const revealEdges = revealValue && !Array.isArray(revealValue) && Array.isArray(revealValue.edges)
    ? revealValue.edges
    : null;
  const revealPts = Array.isArray(revealRaw) && revealRaw.every((v) => Array.isArray(v)) ? revealRaw : null;
  // Point labels are drawn as crisp HTML overlays (below) whenever the inline input box / label chip is
  // NOT on screen — i.e. the correct-answer reveal and the read-only review. SVG <text> is stretched into
  // an unreadable smear by the non-uniform viewBox on a wide figure (a probability scale is ~10:1), which
  // is why the A/B/C letters never showed on the scale reveal. When this is on, the SVG label is skipped.
  const labelOverlayActive = !!revealPts || !!readOnly;
  // The spec-level fallback only applies when the question actually ASKS the student to name the shape.
  // A leftover `shape_label` on a question with require_shape_label off is authoring residue, not an
  // answer — James Allen's 2025 Q12(a) ("plot A, B and C") carried one and painted a stray "A" in the
  // middle of the plotted triangle on every reveal, next to the real point labels.
  const revealShapeLabel = revealValue && !Array.isArray(revealValue) && typeof revealValue.shape_label === "string"
    ? revealValue.shape_label.trim()
    : ((labelOverlayActive || readOnly) && spec?.require_shape_label && typeof spec?.shape_label === "string"
        ? spec.shape_label.trim()
        : "");
  // Given corners count toward a fillable shape: "plot D and draw the parallelogram ABCD" has the student
  // place ONE point, so gating on placedPts alone would leave "Fill in" disabled forever.
  const canFill = placedPts.length + (edgesAvailable ? contextPoints.length : 0) >= 3;

  // manualEdges: for graded edge shapes it lives in the value (value.edges) so it round-trips and can be
  // graded; for anything else it's purely local visual state. setManualEdges routes writes to the right
  // place. emitEdgeValue writes the {points, edges} pair — corner changes go through it too so edges are
  // never lost when a corner is added/removed on a graded edge shape. shape_label is preserved whenever
  // the question requires naming the finished shape (or the student already typed one).
  const manualEdges = valueHasEdges && Array.isArray(value.edges) ? value.edges : manualEdgesLocal;
  const emitValue = (nextPoints, nextEdges = undefined, shapeLabel = undefined) => {
    if (!onChange) return;
    const keepLabel = shapeLabel !== undefined ? shapeLabel : currentShapeLabel;
    // Wherever the edge tool is offered, the outline is part of the answer, so the value must always
    // carry an `edges` array — empty included. A bare point list means something different to the
    // grader: "this UI cannot draw edges" (a mobile geoboard), which it forgives. So corners placed and
    // nothing joined was submitted as a bare array and marked fully correct on the very questions that
    // ask for a drawing — "draw a hexagon with area 12" scored 2/2 from six dots (North London 2015 Q26).
    const needsObject = requireShapeLabel || keepLabel !== "" || nextEdges !== undefined
      || valueHasEdges || edgesAvailable;
    if (!needsObject) {
      onChange(nextPoints);
      return;
    }
    const out = { points: nextPoints };
    if (nextEdges !== undefined) out.edges = nextEdges;
    else if (valueHasEdges && Array.isArray(value.edges)) out.edges = value.edges;
    else if (edgesAvailable) out.edges = manualEdges;
    if (requireShapeLabel || keepLabel !== "") out.shape_label = keepLabel;
    onChange(out);
  };
  const emitEdgeValue = (nextPoints, nextEdges) => {
    emitValue(nextPoints, nextEdges);
  };
  const setManualEdges = (updater) => {
    const next = typeof updater === "function" ? updater(manualEdges) : updater;
    if (edgesAvailable) {
      emitEdgeValue(placedPts, next);
    } else {
      setManualEdgesLocal(next);
    }
  };
  const setShapeLabel = (label) => {
    emitValue(placedPts, valueHasEdges ? manualEdges : undefined, (label || "").trim());
  };

  // Drop the colour-fill again whenever there are too few corners to enclose a shape (a corner was
  // removed), so the student re-applies it after finishing.
  useEffect(() => { if (!canFill) setManualFill(false); }, [canFill]);

  // Commit the healed glyph value (see placedGlyphs above): rendering it corrected is not enough, because
  // an answer SUBMITTED without touching a letter would still carry the stale flipH+flipV and grade wrong.
  useEffect(() => {
    if (!isGlyph || readOnly || !onChange || !Array.isArray(value)) return;
    if (!value.some((g) => g && typeof g === "object" && g.flipH && g.flipV)) return;
    onChange(value.map((g) => (g && typeof g === "object" && g.flipH && g.flipV ? { ...g, flipH: false } : g)));
  }, [isGlyph, readOnly, onChange, value]);

  // ---- coordinate helpers (grid index <-> fractional 0..1) ----
  const gx0 = Number(grid?.x0 ?? 0.1), gy0 = Number(grid?.y0 ?? 0.1);
  const gdx = Number(grid?.dx ?? 0.1) || 0.1, gdy = Number(grid?.dy ?? 0.1) || 0.1;
  const minGridIntervals = kind === "points" ? 0 : 1;
  const cols = Math.max(minGridIntervals, Math.round(Number(grid?.cols ?? 8)));
  const rows = Math.max(minGridIntervals, Math.round(Number(grid?.rows ?? 8)));
  // Isometric (triangular / staggered) dot paper: odd rows are shifted right by half a column, so the
  // dots form the quincunx of triangular dotty paper. `fx` then depends on the row.
  const isoGrid = grid?.iso === true;
  // Hexagonal ("honeycomb") grid paper, flat-top orientation (flat edges top/bottom, points left/right
  // — the shape this component's `grid` already renders for every other kind). With unit column
  // spacing gdx = R/2 and row spacing gdy = R*sqrt(3)/2 (R = hex centre-to-vertex radius), a hexagon
  // centred on vertex-column c has its 6 vertices at gx = 3c-2, 3c-1, 3c+1, 3c+2 — i.e. every vertex
  // satisfies gx mod 3 != 0 (the 3c columns are hex CENTRES, never vertices). This holds for every row,
  // independent of gy — a hex tiling's vertex columns line up straight; only the row spacing zigzags
  // per hexagon, which the row grid (gdy) already captures directly rather than needing a formula.
  // `grid.honeycomb_offset` (0, 1 or 2) shifts which residue is the removed one, in case a diagram's
  // authored x0 lands gx=0 on a hex vertex column rather than a hex centre column.
  const honeycombGrid = grid?.type === "hex";
  const honeycombOffset = Number.isInteger(grid?.honeycomb_offset) ? ((grid.honeycomb_offset % 3) + 3) % 3 : 0;
  const onHoneycomb = (gx) => !honeycombGrid || ((Math.round(gx) % 3 + 3) % 3) !== honeycombOffset;
  // Diagonal (45°-rotated) squared paper: its printed intersections are the checkerboard half of a
  // square lattice of half the spacing — only cells whose (col + row) matches `parity` exist on the
  // paper. Without the mask half the dots would sit in the middle of a printed diamond.
  const gridParity = grid?.parity === 0 || grid?.parity === 1 ? Number(grid.parity) : null;
  const onParity = (gx, gy) => (gridParity === null || Math.abs((gx + gy) % 2) === gridParity) && onHoneycomb(gx);
  const fx = (gx, gy) => gx0 + gx * gdx + (isoGrid && Math.abs(Math.round(Number(gy)) % 2) === 1 ? gdx / 2 : 0);
  const fy = (gy) => gy0 + gy * gdy;

  // reflect/rotate: the correct shape is the mirror/rotation of the (visible) original, so its outline
  // order is derivable here. We use it only to draw the student's placed corners in the right boundary
  // order when they colour the shape in — so a concave reflection fills cleanly no matter what order
  // the corners were tapped. It never affects grading (server-side, order-independent).
  const fracToGridPt = ([x, y]) => [(Number(x) - gx0) / gdx, (Number(y) - gy0) / gdy];
  const expectedTransformOrder = () => {
    if (!isTransform || original.length < 1) return [];
    if (isReflect) {
      if (!Array.isArray(spec?.mirror) || spec.mirror.length < 2) return [];
      const a = fracToGridPt(spec.mirror[0]), b = fracToGridPt(spec.mirror[1]);
      const dx = b[0] - a[0], dy = b[1] - a[1], dd = dx * dx + dy * dy || 1;
      return original.map(([px, py]) => {
        const t = ((px - a[0]) * dx + (py - a[1]) * dy) / dd;
        return [Math.round(2 * (a[0] + t * dx) - px), Math.round(2 * (a[1] + t * dy) - py)];
      });
    }
    if (!Array.isArray(spec?.center) || spec.center.length < 2) return [];
    const c = fracToGridPt(spec.center), ang = spec.angle || "cw90";
    return original.map(([px, py]) => {
      const dx = px - c[0], dy = py - c[1];
      const [nx, ny] = ang === "cw90" ? [-dy, dx] : ang === "ccw90" ? [dy, -dx] : [-dx, -dy];
      return [Math.round(c[0] + nx), Math.round(c[1] + ny)];
    });
  };
  // Reorder placed corners to follow the expected boundary sequence (nearest expected vertex first).
  const orderTransformPts = (placed) => {
    const exp = expectedTransformOrder();
    if (exp.length === 0 || placed.length === 0) return placed;
    const used = new Array(placed.length).fill(false);
    const out = [];
    exp.forEach((e) => {
      let bi = -1, bd = Infinity;
      placed.forEach((p, i) => {
        if (used[i]) return;
        const d = (Number(p[0]) - e[0]) ** 2 + (Number(p[1]) - e[1]) ** 2;
        if (d < bd) { bd = d; bi = i; }
      });
      if (bi >= 0) { used[bi] = true; out.push(placed[bi]); }
    });
    placed.forEach((p, i) => { if (!used[i]) out.push(p); });
    return out;
  };

  const clientToGrid = (clientX, clientY) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r || r.width === 0) return null;
    const fxv = (clientX - r.left) / r.width;
    const fyv = (clientY - r.top) / r.height;
    const gy = Math.round((fyv - gy0) / gdy);
    // Isometric dots: the row is snapped first, then the column is measured relative to that row's
    // half-column offset, so taps land on the visible staggered dots.
    const rowShift = isoGrid && Math.abs(gy % 2) === 1 ? gdx / 2 : 0;
    const gx = Math.round((fxv - gx0 - rowShift) / gdx);
    if (gx < 0 || gx > cols || gy < 0 || gy > rows) return null;
    if (!onParity(gx, gy)) {
      // Rounding landed on a cell the paper has no dot at. Take whichever of the four neighbours is
      // nearest the tap, measured in fractional image units so the x/y spacings weigh correctly.
      const near = [[gx - 1, gy], [gx + 1, gy], [gx, gy - 1], [gx, gy + 1]]
        .filter(([x, y]) => x >= 0 && x <= cols && y >= 0 && y <= rows)
        .sort((a, b) => Math.hypot(fx(a[0], a[1]) - fxv, fy(a[1]) - fyv) - Math.hypot(fx(b[0], b[1]) - fxv, fy(b[1]) - fyv))[0];
      return near || null;
    }
    return [gx, gy];
  };

  // ---- shade interaction ----
  const currentGroup = isGroupShade ? (activeGroup ?? String(shadeGroups[0].key)) : null;
  const pointInRegion = (fx, fy, points) => {
    if (!Array.isArray(points) || points.length < 3) return false;
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = Number(points[i]?.[0]);
      const yi = Number(points[i]?.[1]);
      const xj = Number(points[j]?.[0]);
      const yj = Number(points[j]?.[1]);
      if (![xi, yi, xj, yj].every(Number.isFinite)) continue;
      const intersect = (yi > fy) !== (yj > fy) && fx < ((xj - xi) * (fy - yi)) / (yj - yi + Number.EPSILON) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };
  const findShadeRegionAtFrac = (fx, fy) => {
    for (const rg of regions) {
      const id = rg?.id;
      if (id == null || id === "") continue;
      if (rg.r != null) {
        const cx = Number(rg.cx);
        const cy = Number(rg.cy);
        const r = Number(rg.r);
        if (Number.isFinite(cx) && Number.isFinite(cy) && Number.isFinite(r)
          && Math.hypot(fx - cx, fy - cy) <= r) {
          return String(id);
        }
        continue;
      }
      if (pointInRegion(fx, fy, rg.points)) return String(id);
    }
    return null;
  };
  const paintShadeRegion = (id, opts = {}) => {
    if (readOnly || !onChange || !id) return;
    const sid = String(id);
    if (regions.some((rg) => String(rg.id) === sid && rg.given)) return;
    const entries = shadeEntriesRef.current;
    const ids = entries.filter((v) => !letterRegionIds.has(idOf(v))).map(idOf);
    const groups = isGroupShade
      ? Object.fromEntries(entries.filter((v) => v && typeof v === "object" && v.group).map((v) => [idOf(v), String(v.group)]))
      : {};
    if (letterRegionIds.has(sid)) {
      if (opts.startPaint) {
        const existing = entries.find((v) => idOf(v) === sid);
        setLetterPrompt({ id: sid, value: (existing && typeof existing === "object" ? existing.label : "") || "" });
      }
      return;
    }
    const paint = shadePaintRef.current;
    const commit = (next) => {
      shadeEntriesRef.current = next;
      emitShade(next);
    };
    if (isGroupShade) {
      const existing = groups[sid];
      if (opts.startPaint || !paint) {
        const mode = existing === currentGroup ? "remove" : "add";
        shadePaintRef.current = { mode, lastId: sid };
        if (mode === "remove") commit(entries.filter((v) => idOf(v) !== sid));
        else if (existing) commit(entries.map((v) => (idOf(v) === sid ? { id: sid, group: currentGroup } : v)));
        else commit([...entries, { id: sid, group: currentGroup }]);
        return;
      }
      if (paint.lastId === sid) return;
      shadePaintRef.current = { mode: paint.mode, lastId: sid };
      if (paint.mode === "remove") commit(entries.filter((v) => idOf(v) !== sid));
      else if (groups[sid] && groups[sid] !== currentGroup) {
        commit(entries.map((v) => (idOf(v) === sid ? { id: sid, group: currentGroup } : v)));
      } else if (!groups[sid]) {
        commit([...entries, { id: sid, group: currentGroup }]);
      }
      return;
    }
    if (opts.startPaint || !paint) {
      const mode = ids.includes(sid) ? "remove" : "add";
      shadePaintRef.current = { mode, lastId: sid };
      if (mode === "remove") commit(entries.filter((v) => idOf(v) !== sid));
      else if (requireLabels) commit([...entries, { id: sid, label: "" }]);
      else commit([...entries, sid]);
      return;
    }
    if (paint.lastId === sid) return;
    shadePaintRef.current = { mode: paint.mode, lastId: sid };
    if (paint.mode === "remove") {
      if (ids.includes(sid)) commit(entries.filter((v) => idOf(v) !== sid));
    } else if (!ids.includes(sid)) {
      if (requireLabels) commit([...entries, { id: sid, label: "" }]);
      else commit([...entries, sid]);
    }
  };
  const toggleRegion = (id) => {
    if (shadeSkipClickRef.current) {
      shadeSkipClickRef.current = false;
      return;
    }
    paintShadeRegion(id, { startPaint: true });
    shadePaintRef.current = null;
  };

  // ---- typed label for a shaded region (require_labels shade questions) ----
  // For `label_only` questions the region was never shaded/added to the value (it's `given`, not
  // tappable) — so the first keystroke must ADD its {id, label} entry rather than only updating an
  // existing one, or the label would have nowhere to be stored.
  const setRegionLabel = (id, label) => {
    if (readOnly || !onChange) return;
    const sid = String(id);
    // Check the ENTRIES, not shadedIds — a letter region is excluded from shadedIds by design, and
    // checking there would append a fresh {id,label} on every keystroke.
    if (shadedEntries.some((v) => idOf(v) === sid)) {
      emitShade(shadedEntries.map((v) => (v && typeof v === "object" && idOf(v) === sid ? { ...v, label } : v)));
    } else {
      emitShade([...shadedEntries, { id: sid, label }]);
    }
  };

  // Assigns each labelled region a distinct colour so a multi-region answer (e.g. a pie chart's
  // several activity wedges) reads as visually separate captioned areas. Regions sharing the same
  // `spec.regions[].group` (or, lacking that, the same `label` text) share a colour; each distinct
  // group gets the next palette slot in first-seen order, cycling if there are more groups than colours.
  // Colour is per GROUP, not per labelled region: in a label_only pie chart only one slice of each
  // activity carries the caption, but every slice of that activity must share the group's colour (else
  // the run of uncaptioned slices stays grey and the wedge doesn't read as one area). Keyed by `group`
  // when authored, else by the region's own label/id.
  const groupKeyOf = (rg) => String(rg?.group ?? rg?.label ?? rg?.id ?? "");
  const labelGroupColorOf = (() => {
    const order = [];
    regions.forEach((rg) => {
      const k = groupKeyOf(rg);
      if (k !== "" && !order.includes(k)) order.push(k);
    });
    return (rg) => {
      const k = groupKeyOf(rg);
      const idx = order.indexOf(k);
      return idx < 0 ? null : LABEL_GROUP_PALETTE[idx % LABEL_GROUP_PALETTE.length];
    };
  })();

  // A region takes a typed/dropped caption. The student spec never carries the answer text, so it
  // marks these with `labelable` (see InteractiveDiagramGrading::interactiveStudentSpec); the admin
  // editor holds the full spec and still has the authored `label`.
  const isLabelable = (rg) => !!(rg?.labelable || rg?.label);
  // Candidate captions for the drag chips — a word bank. Published by the server as `label_options`
  // (answer-order-independent); in the admin editor, derived from the authored region labels.
  const labelOptions = (() => {
    if (Array.isArray(spec?.label_options) && spec.label_options.length) return spec.label_options;
    return Array.from(new Set(regions.filter((rg) => rg.label).map((rg) => rg.label)));
  })();

  // A region's points-array is already in fractional 0..1 image coords (no grid transform needed).
  const regionCentroid = (rg) => {
    if (rg.r != null) return [Number(rg.cx), Number(rg.cy)];
    const pts = rg.points || [];
    if (!pts.length) return [0.5, 0.5];
    return [
      pts.reduce((s, p) => s + Number(p[0]), 0) / pts.length,
      pts.reduce((s, p) => s + Number(p[1]), 0) / pts.length,
    ];
  };

  // ---- per-point typed label (3rd element of each [gx, gy, label]) ----
  const setPointLabel = (i, label) => {
    if (readOnly || !onChange) return;
    const next = placedPts.map((p, j) => (j === i ? [p[0], p[1], label] : p));
    // On an edge-drawing shape the value is { points, edges } — emitting the bare point array here wiped
    // the outline the student had just drawn every time they typed a corner's letter.
    emitValue(next, edgesAvailable ? manualEdges : undefined);
  };

  // ---- manual edge drawing (tap two corners to join them) ----
  // Corners you can JOIN are the student's placed points PLUS the read-only context points (A/B/C given
  // on the printed grid). "Plot D and draw the parallelogram ABCD" only asks the student to place ONE
  // point, so without this the three sides that touch a given corner could never be drawn and the shape
  // could never be closed. Context corners are joinable but never movable, removable, or graded — edges
  // are keyed by grid coordinate ("gx,gy|gx,gy"), so an edge to a given corner serialises like any other.
  const edgeCorners = edgesAvailable && contextPoints.length > 0
    ? [...placedPts, ...contextPoints]
    : placedPts;

  const setPendingBoth = (v) => { edgePendingRef.current = v; setEdgePending(v); };
  // True once the drawn edges form one closed ring — every corner they touch has exactly two edges and
  // the edge count matches the corner count. This is what "the shape is finished" means on an
  // edge-drawing question, and it is when the shape-label popup may interrupt.
  const edgesCloseTheShape = (edgeKeys) => {
    if (!Array.isArray(edgeKeys) || edgeKeys.length < 3) return false;
    const deg = new Map();
    edgeKeys.forEach((k) => {
      const [a, b] = String(k).split("|");
      if (!a || !b) return;
      deg.set(a, (deg.get(a) || 0) + 1);
      deg.set(b, (deg.get(b) || 0) + 1);
    });
    return deg.size === edgeKeys.length && [...deg.values()].every((d) => d === 2);
  };
  const toggleCornerForEdge = (existing) => {
    const pending = edgePendingRef.current;
    if (pending === null) {
      setPendingBoth(existing);
      return;
    }
    if (pending === existing) {
      setPendingBoth(null); // tapped the same corner again → cancel
      return;
    }
    if (!edgeCorners[pending] || !edgeCorners[existing]) {
      setPendingBoth(null);
      return;
    }
    const key = edgeKey(edgeCorners[pending], edgeCorners[existing]);
    // Add-only: an edge is removed by tapping the drawn line, never by re-committing its corner pair —
    // so a duplicated call within one event can't add-then-remove the edge it just drew.
    const nextEdges = manualEdges.includes(key) ? manualEdges : [...manualEdges, key];
    setManualEdges(() => nextEdges);
    setPendingBoth(null);
    // The forced shape-label popup fires on the edge that FINISHES the drawing, never on the corner
    // that finishes the corners: "reflect shape A, label it B" placed the last of seven corners, threw
    // the modal up, and then parked the label chip in the middle of the shape — over the very corners
    // the student still had to tap to join. Two ways to be finished: the drawn edges close into one
    // ring, or the question states how many edges it wants (edges-only "join the three points", where
    // no corner is ever placed).
    const neededEdges = Number(spec?.required_edge_count) || 0;
    const drawingFinished = (neededEdges > 0 && nextEdges.length >= neededEdges)
      || (closed && edgesCloseTheShape(nextEdges) && (pointCount <= 0 || placedPts.length >= pointCount));
    if (drawingFinished && fillEligible) setManualFill(true);
    if (
      nextEdges !== manualEdges && drawingFinished
      && requireShapeLabel && !currentShapeLabel && !shapeLabelPromptedRef.current
    ) {
      shapeLabelPromptedRef.current = true;
      setShapeLabelPrompt({ value: currentShapeLabel });
    }
  };
  const removeCorner = (i) => {
    if (readOnly || !onChange) return;
    const goneKey = cornerKey(placedPts[i]);
    const nextPts = placedPts.filter((_, j) => j !== i);
    const nextEdges = manualEdges.filter((k) => !k.split("|").includes(goneKey));
    setPendingBoth(null);
    if (edgesAvailable) emitEdgeValue(nextPts, nextEdges); // atomic {points,edges} — never split into two emits
    else emitValue(nextPts);
    if (nextPts.length < 3) shapeLabelPromptedRef.current = false;
  };

  // Single deterministic tap handler for edge-drawing shapes (reflect/rotate/closed polygon). Six
  // earlier attempts routed corner joins through per-element SVG hit-circles and kept breaking on
  // z-order/pointer-events: horizontal/vertical joins failed while diagonals worked, depending on which
  // sibling happened to sit on the lattice line. This resolves the tap in JS with no dependence on
  // paint order — one capture rect on top of everything snaps the tap to a grid cell, then acts on the
  // cell's contents: an occupied cell → arm/join/cancel that corner; an empty cell → place a corner.
  // Orientation is irrelevant because nothing is hit-tested against overlapping siblings.
  const tapEdgeCanvas = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    // Snap the tap to a grid cell FIRST, then decide by cell contents — never by a distance-to-corner
    // tolerance. A tolerance that was ~half a cell created a dead band exactly on the boundary between
    // two orthogonally-adjacent corners: a tap aimed at the target but landing past the midpoint
    // resolved to the ARMED corner and cancelled the join, so horizontal/vertical joins failed while
    // diagonals (corners farther apart, no boundary in the way) still worked. Snapping to the cell the
    // student actually tapped removes the ambiguity — any tap inside the target cell joins it.
    const g = clientToGrid(clientX, clientY);
    if (!g) return;
    // A given (context) corner is joinable too, so resolve the tap against placed AND context corners.
    const existing = edgeCorners.findIndex((p) => eqPt(p, g));
    if (existing >= 0) {
      toggleCornerForEdge(existing); // arm / join / cancel on an existing corner
      return;
    }
    // Edges-only questions ("join the three points" / "Join ABCD"): every corner is already on the
    // grid, so an empty-cell tap must not plant a stray corner the grader will then reject.
    if (spec?.no_new_points) return;
    // Join-ABCD after shared-grid carry: corners live in context_points and point_count is 0 —
    // never plant + auto-chain edges in tap order (that draws the wrong kite).
    const edgesOnlyJoin =
      (spec?.join_context_edges === true || Number(spec?.required_edge_count) > 0) &&
      contextPoints.length > 0 &&
      !(Number(spec?.point_count) > 0) && !spec?.plots_new_points;
    if (edgesOnlyJoin) return;
    if (pointCount > 0 && placedPts.length >= pointCount) return;
    const pending = edgePendingRef.current;
    let nextEdges = manualEdges;
    // Chain: an armed corner auto-joins to each newly placed corner (place→place→place).
    // Skip for edges-only join parts — students must tap two existing corners deliberately.
    if (pending != null && edgeCorners[pending] && !edgesOnlyJoin) {
      const key = edgeKey(edgeCorners[pending], g);
      if (!nextEdges.includes(key)) nextEdges = [...nextEdges, key];
    }
    const next = [...placedPts, g];
    // Last required corner closes last→first and fills, so students skip the final join + Fill in taps.
    // Not in "pairs" mode: there every side is the student's own two taps, and an automatic closing
    // side joined whichever corners happened to be placed first and last (Oundle 2016 Q9).
    if (closed && pointCount > 0 && next.length === pointCount && next.length >= 3 && spec?.edge_mode !== "pairs") {
      const closeKey = edgeKey(next[next.length - 1], next[0]);
      if (!nextEdges.includes(closeKey)) nextEdges = [...nextEdges, closeKey];
      setPendingBoth(null);
      emitEdgeValue(next, nextEdges);
      if (fillEligible && edgesCloseTheShape(nextEdges)) setManualFill(true);
    } else {
      emitEdgeValue(next, nextEdges);
      // `edge_mode: "pairs"` — "tap two dots to draw a line": the second tap finishes that line instead
      // of arming the next one. Chaining drew a stray line from the last corner to wherever the student
      // started the next line — across to the other shape on Kent 2024 Q10.
      const finishedPair = spec?.edge_mode === "pairs" && pending != null && edgeCorners[pending];
      setPendingBoth(finishedPair ? null : next.length - 1);
    }
    // A graded-label question (e.g. "plot D and draw the parallelogram ABCD") still needs its edges,
    // so labelling runs alongside edge-drawing rather than replacing it: placing a corner opens the
    // label prompt, while taps on existing corners keep arming/joining edges. Re-label via the corner's
    // inline tag.
    if (labelRequired) {
      setLabelPrompt({ index: next.length - 1, value: "", isNew: true });
    } else if (requireShapeLabel && !edgesAvailable && pointCount > 0 && next.length >= pointCount) {
      shapeLabelPromptedRef.current = true;
      setShapeLabelPrompt({ value: currentShapeLabel });
    }
  };

  // ---- point interaction ----
  const tapGrid = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    const g = clientToGrid(clientX, clientY);
    if (!g) return;
    const existing = placedPts.findIndex((p) => eqPt(p, g));
    // Always-on edge drawing (closed transform/polygon shapes): tapping an EXISTING corner wires an
    // edge — first tap arms it (highlights), second tap on a different corner joins them, a repeat tap
    // on the armed corner cancels. A corner is removed only via its inline control, never by a plain
    // tap, so building the outline never accidentally deletes a corner.
    if (edgesAvailable) {
      // ONE tap path for corners: the generous grid hit-circles route every corner tap here. (Earlier
      // versions layered a second per-corner hit circle on top — the two handlers either both fired,
      // re-toggling and undoing the join, or left a dead ring where neither fired.)
      if (existing >= 0) {
        toggleCornerForEdge(existing);
        return;
      }
      if (spec?.no_new_points) return;
      const edgesOnlyJoin =
        (spec?.join_context_edges === true || Number(spec?.required_edge_count) > 0) &&
        contextPoints.length > 0 &&
        !(Number(spec?.point_count) > 0) && !spec?.plots_new_points;
      if (edgesOnlyJoin) return;
      if (pointCount > 0 && placedPts.length >= pointCount) return;
      const nextPts = [...placedPts, g];
      emitValue(nextPts, manualEdges);
      // Shape-label popup deliberately NOT fired here — on an edge-drawing shape the corners are only
      // half the drawing. It fires from toggleCornerForEdge once the outline closes.
      if (labelRequired) {
        setLabelPrompt({ index: nextPts.length - 1, value: "", isNew: true });
      }
      return;
    }
    if (existing >= 0) {
      // On a label-required question, tapping an existing point re-opens its label prompt (so a
      // mislabelled point can be corrected) rather than removing it. The prompt's "Remove" clears it.
      if (labelRequired) {
        setLabelPrompt({ index: existing, value: typeof placedPts[existing][2] === "string" ? placedPts[existing][2] : "", isNew: false });
        return;
      }
      // Removing a corner also drops any manual edges touching it.
      const goneKey = cornerKey(placedPts[existing]);
      setManualEdges((es) => es.filter((k) => !k.split("|").includes(goneKey)));
      setEdgePending(null);
      const nextPts = placedPts.filter((_, i) => i !== existing);
      emitValue(nextPts);
      if (nextPts.length < 3) shapeLabelPromptedRef.current = false;
      return;
    }
    // At the point limit, tapping a new spot must NOT silently drop an earlier point — the student
    // removes a point only by tapping it. Ignore the extra tap instead of replacing the oldest.
    if (pointCount > 0 && placedPts.length >= pointCount) return;
    // Manual-fill kinds keep tap order so a concave outline traced corner-by-corner joins and fills
    // in the sequence the student intends — the outline is only drawn when they hit "Connect corners",
    // so there is never a half-drawn bowtie mid-placement and no auto-fill on each tap.
    const next = [...placedPts, g];
    emitValue(next);
    // Force the label immediately on a graded-label question so the point is never left unlabelled.
    if (labelRequired) {
      setLabelPrompt({ index: next.length - 1, value: "", isNew: true });
    } else if (requireShapeLabel && pointCount > 0 && next.length >= pointCount) {
      // Once every corner is down, force the shape-name popup (e.g. "Label the triangle A").
      // Only when the spec says how many corners there are. `pointCount <= 0` used to count as "done",
      // so a seven-point plot threw the modal up on the THIRD tap and then parked the label chip in the
      // middle of the half-finished figure. With no corner count to wait for, the chip beside the shape
      // and the Fill in / Connect buttons are the way in — neither interrupts the drawing.
      shapeLabelPromptedRef.current = true;
      setShapeLabelPrompt({ value: currentShapeLabel });
    }
  };

  // ---- lines interaction (free tap, or snap to grid dots when spec.grid is set) ----
  const clientToFrac = (clientX, clientY) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r || r.width === 0 || r.height === 0) return null;
    return [(clientX - r.left) / r.width, (clientY - r.top) / r.height];
  };
  const snapLinePoint = (clientX, clientY) => {
    if (grid && rows > 0) {
      const g = clientToGrid(clientX, clientY);
      if (!g) return null;
      return [fx(g[0], g[1]), fy(g[1])];
    }
    return clientToFrac(clientX, clientY);
  };
  // A free-tap line ends WHERE IT WAS TAPPED. This used to rotate the second endpoint onto the nearest
  // multiple of 45 degrees whenever it fell within 15 of one, which silently moved the line off the
  // point the student had aimed at — "I plotted the point in one location, but the system placed it in
  // a different location" (King's 2011 Q11 a). It is wrong for any figure whose mirror lines are not
  // multiples of 45: a five-pointed star's are at 18, 54, 90, 126 and 162 degrees, so four of the five
  // were dragged onto an axis and both drawn and marked wrong. Hand wobble belongs in the grader's
  // angle_tol_deg (6 degrees by default), which the author can widen per question — not in an input
  // snap wide enough to turn a right answer into a different one.
  // Keep in sync with completeFreehandSymmetryLine in interactiveDiagramMobile.ts.
  const completeFreehandSymmetryLine = (start, end) => {
    if (!quickSymmetryLines || !start || !end) return end;
    if (grid || spec?.match_endpoints || kind === "tessellation-grid") return end;
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const len = Math.hypot(dx, dy);
    const minDrag = 0.07;
    const reach = 0.62;
    if (len < minDrag || len >= reach) return end;
    const scale = reach / len;
    const far = [start[0] + dx * scale, start[1] + dy * scale];
    const x0 = start[0];
    const y0 = start[1];
    const fdx = far[0] - x0;
    const fdy = far[1] - y0;
    let t0 = 0;
    let t1 = 1;
    const clip = (p, q) => {
      if (Math.abs(p) < 1e-12) return q >= -1e-9;
      const r = q / p;
      if (p < 0) {
        if (r > t1) return false;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return false;
        if (r < t1) t1 = r;
      }
      return true;
    };
    if (clip(-fdx, x0) && clip(fdx, 1 - x0) && clip(-fdy, y0) && clip(fdy, 1 - y0) && t1 >= t0) {
      return [x0 + t1 * fdx, y0 + t1 * fdy];
    }
    return [Math.max(0, Math.min(1, far[0])), Math.max(0, Math.min(1, far[1]))];
  };
  const emitDrawnLine = (a, b) => {
    if (!a || !b) return false;
    // Never removes a line; a duplicate segment is ignored.
    const r = appendDrawnLine(placedLines, a, b);
    if (r.tooShort) return false;
    if (r.changed) emitLines(r.lines);
    setPendingPt(null);
    pendingPtRef.current = null;
    return true;
  };
  const tapLine = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    if (lineSkipClickRef.current) {
      lineSkipClickRef.current = false;
      return;
    }
    const f = snapLinePoint(clientX, clientY);
    if (!f) return;
    // Clicking the same dot / an endpoint / a line never removes a line (and the same dot twice
    // no longer cancels the start). Removal is only via Clear last line.
    const pending = pendingPtRef.current;
    const next = nextLineTapState(placedLines, pending, f);
    if (next.changed) emitLines(next.lines);
    pendingPtRef.current = next.pending;
    setPendingPt(next.pending);
  };
  const onLinePointerDown = (e) => {
    if (readOnly || !onChange) return;
    if (e.button != null && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    lineGestureRef.current = { x: e.clientX, y: e.clientY, dragged: false };
  };
  const onLinePointerMove = (e) => {
    const g = lineGestureRef.current;
    if (!g) return;
    if (Math.hypot(e.clientX - g.x, e.clientY - g.y) > 10) g.dragged = true;
    if (!g.dragged) return;
    if (!pendingPtRef.current) {
      const start = snapLinePoint(g.x, g.y);
      if (start) {
        pendingPtRef.current = start;
        setPendingPt(start);
      }
    }
    const raw = snapLinePoint(e.clientX, e.clientY);
    const start = pendingPtRef.current;
    if (raw && start) setDragLineEnd(completeFreehandSymmetryLine(start, raw));
  };
  const onLinePointerUp = (e) => {
    const g = lineGestureRef.current;
    lineGestureRef.current = null;
    setDragLineEnd(null);
    if (!g?.dragged) return;
    lineSkipClickRef.current = true;
    const start = pendingPtRef.current || snapLinePoint(g.x, g.y);
    const end = snapLinePoint(e.clientX, e.clientY);
    emitDrawnLine(start, completeFreehandSymmetryLine(start, end));
  };

  // ---- angle interaction (tap to swing each arm out from the printed vertex) ----
  // The vertex travels with the value as well as the spec so the grader can read a submission on its
  // own, and so a re-authored vertex never silently re-scores an answer drawn against the old one.
  const emitAngle = (rays) => {
    if (!onChange) return;
    onChange({ vertex: angleVertex, rays });
  };
  const tapAngle = (clientX, clientY) => {
    if (readOnly || !onChange || !angleVertex) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    // A tap on the vertex itself gives no direction — ignore it rather than emitting a zero-length arm.
    if (Math.hypot(f[0] - angleVertex[0], f[1] - angleVertex[1]) < 0.01) return;
    // Once both arms are out, the next tap starts again rather than silently doing nothing.
    emitAngle(angleRays.length >= angleRayCount ? [f] : [...angleRays, f]);
  };
  const clearLastAngleRay = () => {
    if (readOnly || !onChange) return;
    emitAngle(angleRays.slice(0, -1));
  };
  // Degrees between the two arms, measured the non-reflex way — the same reading gradeInteractiveAngle
  // compares against target_angle, shown live so the student can check their protractor work.
  const angleReading = (rays) => {
    if (!angleVertex) return null;
    const arms = angleGivenRay && rays.length < 2 ? [angleGivenRay, ...rays] : rays;
    if (arms.length < 2) return null;
    // Fractional x/y are not isotropic — the saved image aspect restores true geometry so the readout
    // is the number of degrees a protractor measures on screen. Mirrors gradeInteractiveAngle.
    const aspect = Number(spec?.aspect) > 0 ? Number(spec.aspect) : 1;
    const dir = (p) => Math.atan2(-(p[1] - angleVertex[1]), (p[0] - angleVertex[0]) * aspect);
    let deg = (dir(arms[1]) - dir(arms[0])) * 180 / Math.PI;
    if (deg < 0) deg += 360;
    return Math.round(deg > 180 ? 360 - deg : deg);
  };
  const clearLastLine = () => {
    if (readOnly || !onChange) return;
    if (pendingPt) { setPendingPt(null); return; }
    emitLines(placedLines.slice(0, -1));
  };

  // ---- cut-drag interaction (tap two points to cut, then drag the pieces) ----
  const tapCut = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    if (!pendingPt) { setPendingPt(f); return; }
    onChange({ cut: [pendingPt, f], offsets: [[0, 0], [0, 0]] });
    setPendingPt(null);
  };
  const removeCut = () => { if (readOnly || !onChange) return; setPendingPt(null); onChange(null); };
  const resetPieces = () => { if (readOnly || !onChange || !cdValue) return; onChange({ ...cdValue, offsets: [[0, 0], [0, 0]] }); };
  const onPiecePointerDown = (i) => (e) => {
    if (readOnly || !onChange || !cdValue) return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({
      startX: e.clientX,
      startY: e.clientY,
      base: cdOffsets.map((o) => [Number(o?.[0]) || 0, Number(o?.[1]) || 0]),
      piece: i,
    });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  // ---- segments interaction (join two fixed nodes) ----
  const sameSeg = (s, a, b) => (eqPt(s[0], a) && eqPt(s[1], b)) || (eqPt(s[0], b) && eqPt(s[1], a));
  const tapNode = (i) => {
    if (readOnly || !onChange) return;
    const node = nodes[i];
    if (!node) return;
    if (pendingNode === null) { setPendingNode(i); return; }
    if (pendingNode === i) { setPendingNode(null); return; }
    const a = nodes[pendingNode], b = node;
    const existing = placedSegs.findIndex((s) => sameSeg(s, a, b));
    if (existing >= 0) onChange(placedSegs.filter((_, j) => j !== existing));
    else onChange([...placedSegs, [a, b]]);
    setPendingNode(null);
  };
  const removeSeg = (i, e) => {
    e.stopPropagation();
    if (readOnly || !onChange) return;
    onChange(placedSegs.filter((_, j) => j !== i));
  };
  const clearLastSeg = () => {
    if (readOnly || !onChange) return;
    if (pendingNode !== null) { setPendingNode(null); return; }
    onChange(placedSegs.slice(0, -1));
  };

  // ---- pictogram interaction (cycle each icon: empty → full → ¾ → ½ → ¼ → empty) ----
  const cycleIcon = (i) => {
    if (readOnly || !onChange) return;
    const cur = Array.from({ length: pictoCount }, (_, j) => normFill(pictoStates[j]));
    cur[i] = PICTO_CYCLE[(PICTO_CYCLE.indexOf(cur[i]) + 1) % PICTO_CYCLE.length];
    onChange(cur);
  };

  const setTallyCountAt = (index, n) => {
    if (readOnly || !onChange || index < 0 || index >= tallyCells.length) return;
    const max = tallyCells[index].max_marks;
    const next = tallyCells.map((_, i) => (i === index
      ? Math.max(0, Math.min(max, Math.round(Number(n) || 0)))
      : (tallyCountsRef.current[i] ?? 0)));
    tallyCountsRef.current = next;
    tallyLastIndexRef.current = index;
    onChange(next);
  };

  const removeOneTally = () => {
    if (readOnly || !onChange) return;
    let idx = tallyLastIndexRef.current;
    if ((tallyCountsRef.current[idx] ?? 0) <= 0) {
      idx = -1;
      for (let i = tallyCountsRef.current.length - 1; i >= 0; i--) {
        if ((tallyCountsRef.current[i] ?? 0) > 0) { idx = i; break; }
      }
    }
    if (idx < 0) return;
    setTallyCountAt(idx, (tallyCountsRef.current[idx] ?? 0) - 1);
  };

  // Single tap/click adds one mark; double tap/click removes one. Deferred single-click so the
  // second click of a double-click does not also increment.
  const onTallyActivate = (index, e) => {
    if (readOnly || !onChange) return;
    e.stopPropagation();
    const now = Date.now();
    const DOUBLE_MS = 300;
    if (tallyClickTimerRef.current) {
      clearTimeout(tallyClickTimerRef.current);
      tallyClickTimerRef.current = null;
    }
    const last = tallyLastTapRef.current;
    if (last.index === index && now - last.t < DOUBLE_MS) {
      tallyLastTapRef.current = { t: 0, index: -1 };
      setTallyCountAt(index, (tallyCountsRef.current[index] ?? 0) - 1);
      return;
    }
    tallyLastTapRef.current = { t: now, index };
    tallyClickTimerRef.current = setTimeout(() => {
      tallyClickTimerRef.current = null;
      setTallyCountAt(index, (tallyCountsRef.current[index] ?? 0) + 1);
    }, DOUBLE_MS);
  };

  const r4 = (n) => Math.round(n * 1e4) / 1e4;
  const commitTallyCell = (index, next) => {
    if (!onCellChange) return;
    const x = r4(Math.max(0, Math.min(1, Number(next.x) || 0)));
    const y = r4(Math.max(0, Math.min(1, Number(next.y) || 0)));
    const w = r4(Math.max(0.02, Math.min(1 - x, Number(next.w) || 0.02)));
    const h = r4(Math.max(0.02, Math.min(1 - y, Number(next.h) || 0.02)));
    onCellChange(index, { x, y, w, h });
  };

  // Admin: drag a cell to move it, drag a corner to resize, or (place mode) drag on the image to
  // append a new cell. A pointer gesture that never moves past the threshold is a normal tally click.
  const beginTallyCellDrag = (mode, index, e, corner = null) => {
    if (!onCellChange || readOnly) return;
    e.stopPropagation();
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    const cell = (index >= 0 && tallyCells[index]) || { x: 0.84, y: 0.3, w: 0.15, h: 0.55 };
    const startFrac = clientToFrac(e.clientX, e.clientY);
    tallyCellDragRef.current = {
      mode,
      index,
      corner,
      startX: e.clientX,
      startY: e.clientY,
      startFrac,
      orig: { x: Number(cell.x) || 0, y: Number(cell.y) || 0, w: Number(cell.w) || 0.12, h: Number(cell.h) || 0.3 },
      moved: false,
      appendIndex: null,
    };
    const move = (ev) => {
      const d = tallyCellDragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (!d.moved && Math.hypot(dx, dy) < 5) return;
      d.moved = true;
      if (tallyClickTimerRef.current) {
        clearTimeout(tallyClickTimerRef.current);
        tallyClickTimerRef.current = null;
      }
      const f = clientToFrac(ev.clientX, ev.clientY);
      if (!f) return;
      if (d.mode === "move") {
        const ox = d.startFrac ? f[0] - d.startFrac[0] : 0;
        const oy = d.startFrac ? f[1] - d.startFrac[1] : 0;
        commitTallyCell(d.index, { x: d.orig.x + ox, y: d.orig.y + oy, w: d.orig.w, h: d.orig.h });
        return;
      }
      if (d.mode === "draw") {
        const x0 = d.startFrac ? d.startFrac[0] : f[0];
        const y0 = d.startFrac ? d.startFrac[1] : f[1];
        const rect = {
          x: Math.min(x0, f[0]),
          y: Math.min(y0, f[1]),
          w: Math.max(0.02, Math.abs(f[0] - x0)),
          h: Math.max(0.02, Math.abs(f[1] - y0)),
        };
        if (d.appendIndex == null) {
          d.appendIndex = tallyCells.length;
          commitTallyCell(-1, rect);
        } else {
          commitTallyCell(d.appendIndex, rect);
        }
        return;
      }
      const right = d.orig.x + d.orig.w;
      const bottom = d.orig.y + d.orig.h;
      let x = d.orig.x, y = d.orig.y, w = d.orig.w, h = d.orig.h;
      if (d.corner?.includes("w")) { x = Math.min(f[0], right - 0.02); w = right - x; }
      if (d.corner?.includes("e")) { w = Math.max(0.02, f[0] - d.orig.x); }
      if (d.corner?.includes("n")) { y = Math.min(f[1], bottom - 0.02); h = bottom - y; }
      if (d.corner?.includes("s")) { h = Math.max(0.02, f[1] - d.orig.y); }
      commitTallyCell(d.index, { x, y, w, h });
    };
    const up = (ev) => {
      el.releasePointerCapture?.(ev.pointerId);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      const d = tallyCellDragRef.current;
      tallyCellDragRef.current = null;
      if (d && !d.moved && d.mode === "move" && !cellPlaceMode && d.index >= 0) {
        onTallyActivate(d.index, ev);
      }
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  };

  useEffect(() => () => {
    if (tallyClickTimerRef.current) clearTimeout(tallyClickTimerRef.current);
  }, []);

  // ---- clock interaction (drag hour + minute hands) ----
  const round4 = (n) => Math.round(n * 1e4) / 1e4;
  const clockCenter = Array.isArray(spec?.center) && spec.center.length >= 2 ? [Number(spec.center[0]), Number(spec.center[1])] : [0.5, 0.5];
  const clockRadius = Number(spec?.radius) || 0.4;
  const HAND_LEN = { hour: clockRadius * 0.55, minute: clockRadius * 0.92 };
  const defaultHands = [[clockCenter[0], clockCenter[1] - HAND_LEN.hour], [clockCenter[0], clockCenter[1] - HAND_LEN.minute]];
  const clockHands = isClock && Array.isArray(value) && value.length >= 2 && Array.isArray(value[0]) && Array.isArray(value[1]) ? value : null;
  const revealHands = isClock && Array.isArray(revealValue) && revealValue.length >= 2 && Array.isArray(revealValue[0]) ? revealValue : null;
  const setHand = (which, clientX, clientY) => {
    if (readOnly || !onChange) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    let dx = f[0] - clockCenter[0], dy = f[1] - clockCenter[1];
    const L = Math.hypot(dx, dy) || 1;
    const r = HAND_LEN[which];
    const ep = [round4(clockCenter[0] + (dx / L) * r), round4(clockCenter[1] + (dy / L) * r)];
    const cur = clockHands ? [clockHands[0], clockHands[1]] : [defaultHands[0], defaultHands[1]];
    cur[which === "hour" ? 0 : 1] = ep;
    onChange(cur);
  };
  const startHand = (which) => (e) => {
    if (readOnly || !onChange) return;
    e.preventDefault(); e.stopPropagation();
    setClockDrag(which);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  // ---- marker interaction (tap a value on a number line / scale / bar axis) ----
  const markerAxis = Array.isArray(spec?.axis) && spec.axis.length >= 2 && Array.isArray(spec.axis[0]) && Array.isArray(spec.axis[1]) ? spec.axis : null;
  const markerPivot = Array.isArray(spec?.pivot) && spec.pivot.length >= 2 ? spec.pivot : null;
  const markerArc = isMarker && Array.isArray(spec?.arc) && spec.arc.length >= 2 ? spec.arc : null;
  const isDial = isMarker && (spec?.style === "dial" || !!markerPivot || !!markerArc);
  const markerStyle = spec?.style === "bar" ? "bar" : "arrow";
  const markerBarWidth = Number(spec?.bar_width) || 0.06;
  const markerFit = isMarker && markerAxis && isDial
    ? fitMarkerScale(spec, (box.w > 0 && box.h > 0) ? box.w / box.h : 1)
    : null;
  const projectToAxis = (f) => {
    const [a, b] = markerAxis;
    if (markerFit) return markerScalePointAt(markerFit, markerScaleT(markerFit, f)).map(round4);
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const l2 = dx * dx + dy * dy || 1;
    let t = ((f[0] - a[0]) * dx + (f[1] - a[1]) * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    // Snap the reading onto a sticky value grid (parity with mobile) so finger/mouse placement
    // lands on even chance / scale ticks without microscopic precision.
    const v0 = Number(spec?.v0 ?? 0);
    const v1 = Number(spec?.v1 ?? 1);
    const span = v1 - v0;
    if (Number.isFinite(span) && Math.abs(span) > 1e-12) {
      const lo = Math.min(v0, v1);
      const hi = Math.max(v0, v1);
      let tol = Number(spec?.tol ?? 0);
      if (!(tol > 0)) tol = Math.abs(span) * 0.03;
      const maxStep = Math.min(tol, Math.abs(span) / 4);
      let step = maxStep;
      if (maxStep > 0 && Number.isFinite(maxStep)) {
        const exp = Math.floor(Math.log10(maxStep));
        step = maxStep;
        for (let e = exp; e >= exp - 8; e -= 1) {
          const base = 10 ** e;
          let found = false;
          for (const m of [5, 2, 1]) {
            const s = m * base;
            if (s <= maxStep + 1e-12) { step = s; found = true; break; }
          }
          if (found) break;
        }
      }
      const raw = v0 + t * span;
      const clamped = Math.max(lo, Math.min(hi, raw));
      const absSpan = hi - lo;
      const candidates = new Set();
      const steps = Math.max(1, Math.round(absSpan / step));
      for (let i = 0; i <= steps; i += 1) candidates.add(Math.round((lo + (i * absSpan) / steps) * 1e4) / 1e4);
      candidates.add(Math.round(lo * 1e4) / 1e4);
      candidates.add(Math.round(hi * 1e4) / 1e4);
      candidates.add(Math.round(((lo + hi) / 2) * 1e4) / 1e4);
      for (let i = 0; i <= 10; i += 1) candidates.add(Math.round((lo + (i / 10) * absSpan) * 1e4) / 1e4);
      let best = Math.round(clamped * 1e4) / 1e4;
      let bestDist = Infinity;
      candidates.forEach((c) => {
        if (c < lo - 1e-9 || c > hi + 1e-9) return;
        const d = Math.abs(c - clamped);
        if (d < bestDist - 1e-12) { best = c; bestDist = d; }
        else if (Math.abs(d - bestDist) <= 1e-12 && String(c).length < String(best).length) best = c;
      });
      t = absSpan === 0 ? 0 : (best - v0) / span;
      t = Math.max(0, Math.min(1, t));
    }
    return [round4(a[0] + t * dx), round4(a[1] + t * dy)];
  };
  // "Complete the bar chart" — several columns to draw against one shared value axis. Each bar is
  // {x: centre as an image fraction, label}; a tap is snapped into the column it lands in and its height
  // read off the axis, so the bars can be drawn in any order and any one of them redrawn. The columns sit
  // beside the axis rather than on it, so this assumes the value axis is the vertical one (as every bar
  // chart's is) — the height comes from the axis projection, the x from the column.
  // `bars_add_on_tap` is the authoring flag: a tap that lands in no existing column appends a new one at
  // that x instead of being ignored, so an admin builds the chart's columns by tapping them.
  const markerBars = isMarker && Array.isArray(spec?.bars) && (spec.bars.length > 0 || spec?.bars_add_on_tap)
    ? spec.bars.filter((b) => b && Number.isFinite(Number(b.x))).map((b) => ({
        x: Number(b.x),
        label: b.label ?? null,
        studentLabel: Boolean(b.student_label) || Boolean(spec?.bars_require_labels),
      }))
    : null;
  const nearestBar = (fx) => {
    if (!markerBars) return -1;
    const reach = Math.max(markerBarWidth, 0.02) * 1.5;
    let bi = -1, bd = reach;
    markerBars.forEach((b, i) => { const d = Math.abs(fx - b.x); if (d <= bd) { bd = d; bi = i; } });
    return bi;
  };
  const markerBarPoints = (v) => {
    if (!markerBars || !Array.isArray(v)) return [];
    const out = [];
    v.forEach((p) => {
      if (!Array.isArray(p)) return;
      const bi = nearestBar(Number(p[0]));
      if (bi >= 0) {
        const lab = typeof p[2] === "string" ? p[2] : undefined;
        out[bi] = lab != null && lab !== "" ? [markerBars[bi].x, Number(p[1]), lab] : [markerBars[bi].x, Number(p[1])];
      }
    });
    return out;
  };
  const barsNeedStudentLabels = Boolean(markerBars?.some((b) => b.studentLabel));
  const barLabelsFromValue = (() => {
    if (!markerBars) return [];
    const out = markerBars.map(() => "");
    if (Array.isArray(value)) {
      value.forEach((p) => {
        if (!Array.isArray(p) || p.length < 2) return;
        const bi = nearestBar(Number(p[0]));
        if (bi >= 0 && typeof p[2] === "string") out[bi] = p[2];
      });
    }
    return out;
  })();
  const setBarLabel = (bi, text) => {
    if (readOnly || !onChange || !markerBars) return;
    const next = markerBarPoints(value);
    markerBars.forEach((b, i) => {
      const existing = next[i];
      const y = existing ? Number(existing[1]) : (markerAxis ? Number(markerAxis[0][1]) : 0.9);
      const lab = i === bi ? text : (typeof existing?.[2] === "string" ? existing[2] : (barLabelsFromValue[i] || ""));
      next[i] = lab ? [b.x, y, lab] : [b.x, y];
    });
    onChange(markerBars.map((_, i) => next[i]).filter(Boolean));
  };
  const tapMarker = (clientX, clientY) => {
    if (readOnly || !onChange || !markerAxis) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    // Authoring only: the admin is placing the curve's middle tick or the needle hub, which are points
    // OFF the scale being defined — snapping them onto it would make them impossible to set.
    if (spec?.free_tap) return onChange([[round4(f[0]), round4(f[1])]]);
    if (markerBars) {
      const onAxis = projectToAxis(f);
      const bi = nearestBar(f[0]);
      if (bi < 0) {
        if (!spec?.bars_add_on_tap) return;
        onChange([...markerBarPoints(value).filter(Boolean), [round4(f[0]), onAxis[1]]]);
        return;
      }
      const next = markerBarPoints(value);
      const prevLab = typeof next[bi]?.[2] === "string" ? next[bi][2] : (barLabelsFromValue[bi] || "");
      next[bi] = prevLab
        ? [round4(markerBars[bi].x), onAxis[1], prevLab]
        : [round4(markerBars[bi].x), onAxis[1]];
      onChange(markerBars.map((_, i) => next[i]).filter(Boolean));
      return;
    }
    onChange([projectToAxis(f)]);
  };
  const markerPt = isMarker && !markerBars && Array.isArray(value) && Array.isArray(value[0]) ? value[0] : null;
  const revealMarkerPt = isMarker && !markerBars && Array.isArray(revealValue) && Array.isArray(revealValue[0]) ? revealValue[0] : null;
  const markerBarsDrawn = markerBars ? markerBarPoints(value) : [];
  const markerBarsRevealed = markerBars ? markerBarPoints(revealValue) : [];

  // ---- glyph interaction (place / type / rotate / flip letters) ----
  // An answer placed before the flips became mutually exclusive can still carry BOTH — which is a 180°
  // rotation, not a mirror, and grades as wrong. For a symmetric letter (O, W) that is invisible on screen,
  // so the student sees a picture identical to the model answer and no reason for the mark. Collapse the
  // stale combination to the single mirror it was meant to be, so an old answer heals instead of silently
  // failing. (Deliberate rotations use `rot`, which is untouched.)
  const placedGlyphs = isGlyph && Array.isArray(value)
    ? value
      .filter((g) => g && typeof g === "object" && !Array.isArray(g))
      .map((g) => (g.flipH && g.flipV ? { ...g, flipH: false } : g))
    : [];
  const revealGlyphs = isGlyph && Array.isArray(revealValue) ? revealValue.filter((g) => g && typeof g === "object" && !Array.isArray(g)) : null;
  const snapFrac = (fx, fy) => {
    if (!grid) return [Math.round(fx * 1e4) / 1e4, Math.round(fy * 1e4) / 1e4];
    const gx = Math.round((fx - gx0) / gdx), gy = Math.round((fy - gy0) / gdy);
    return [Math.round((gx0 + gx * gdx) * 1e4) / 1e4, Math.round((gy0 + gy * gdy) * 1e4) / 1e4];
  };
  const addGlyph = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    const [x, y] = snapFrac(f[0], f[1]);
    const palette = Array.isArray(spec?.symbol_palette) ? spec.symbol_palette.filter(Boolean) : [];
    const defaultChar = palette.length > 0 ? String(palette[0]) : "";
    onChange([...placedGlyphs, { char: defaultChar, pos: [x, y], flipH: false, flipV: false, rot: 0 }]);
    setSelGlyph(placedGlyphs.length);
  };
  const updateGlyph = (i, patch) => { if (readOnly || !onChange) return; onChange(placedGlyphs.map((g, j) => (j === i ? { ...g, ...patch } : g))); };
  const removeGlyph = (i) => { if (readOnly || !onChange) return; onChange(placedGlyphs.filter((_, j) => j !== i)); setSelGlyph(null); };

  // Placing a letter by tapping is hard to do precisely — "reflect the word SOW" needs each letter within
  // pos_tol of its mirrored position, and the tap lands wherever the finger lands. So a placed letter can
  // be DRAGGED to nudge it into place (and moved with the arrow keys, for fine adjustment). Dragging only
  // rewrites `pos`, so the typed character, rotation and flips all survive the move.
  const dragGlyph = (i, e) => {
    if (readOnly || !onChange) return;
    e.stopPropagation();
    e.preventDefault();
    setSelGlyph(i);
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    let moved = false;
    const move = (ev) => {
      const f = clientToFrac(ev.clientX, ev.clientY);
      if (!f) return;
      moved = true;
      const [x, y] = snapFrac(f[0], f[1]);
      updateGlyph(i, { pos: [x, y] });
    };
    const up = (ev) => {
      el.releasePointerCapture?.(ev.pointerId);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      // A tap that never moved is a plain select — leave the position untouched.
      if (!moved) setSelGlyph(i);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  };
  // Arrow keys nudge the selected letter by one grid step (or 0.5% of the image on a gridless figure).
  const nudgeGlyph = (i, dx, dy) => {
    if (readOnly || !onChange) return;
    const g = placedGlyphs[i];
    if (!g) return;
    const stepX = grid ? gdx : 0.005;
    const stepY = grid ? gdy : 0.005;
    const x = Math.max(0, Math.min(1, (g.pos?.[0] ?? 0) + dx * stepX));
    const y = Math.max(0, Math.min(1, (g.pos?.[1] ?? 0) + dy * stepY));
    updateGlyph(i, { pos: [Math.round(x * 1e4) / 1e4, Math.round(y * 1e4) / 1e4] });
  };
  // The glyph the toolbar edits. ONLY an explicitly selected letter — it used to fall back to the last
  // placed one, so the toolbar was permanently on screen (a floating bar at the bottom of the canvas) even
  // when nothing was selected. It now appears beside the letter you click and disappears when you click
  // away, which is what a selection actually means.
  const activeGlyph = selGlyph != null && selGlyph < placedGlyphs.length ? selGlyph : null;

  // ---- drag-shape interaction ----
  const placedShape = placedPts.length ? placedPts : original;
  const onShapePointerDown = (e) => {
    if (readOnly || !onChange || kind !== "drag-shape") return;
    e.preventDefault();
    setDrag({ startX: e.clientX, startY: e.clientY, base: placedShape });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (clockDrag) { setHand(clockDrag, e.clientX, e.clientY); return; }
    if (gridDrag && onMoveGrid) {
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r || r.width === 0) return;
      onMoveGrid((e.clientX - gridDrag.lastX) / r.width, (e.clientY - gridDrag.lastY) / r.height);
      setGridDrag({ lastX: e.clientX, lastY: e.clientY });
      return;
    }
    if (!drag) return;
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    // cut-drag: move one piece freely (fractional, no grid), snapping to "home" and to the
    // assembled position (relative displacement between the pieces = the shape's base vector).
    if (drag.piece != null) {
      if (!cdValue) return;
      const i = drag.piece, j = 1 - i;
      let off = [
        drag.base[i][0] + (e.clientX - drag.startX) / r.width,
        drag.base[i][1] + (e.clientY - drag.startY) / r.height,
      ];
      const assembled = i === 0
        ? [drag.base[j][0] + cdBase[0], drag.base[j][1] + cdBase[1]]
        : [drag.base[j][0] - cdBase[0], drag.base[j][1] - cdBase[1]];
      for (const c of [[0, 0], assembled]) {
        if (Math.hypot(off[0] - c[0], off[1] - c[1]) <= cdSnapTol) { off = c; break; }
      }
      const next = drag.base.map((o, k) => (k === i ? [Math.round(off[0] * 1e4) / 1e4, Math.round(off[1] * 1e4) / 1e4] : o));
      onChange({ ...cdValue, offsets: next });
      return;
    }
    const dgx = Math.round(((e.clientX - drag.startX) / r.width) / gdx);
    const dgy = Math.round(((e.clientY - drag.startY) / r.height) / gdy);
    const moved = drag.base.map(([x, y]) => [x + dgx, y + dgy]);
    emitValue(moved);
  };
  const onPointerUp = () => { setDrag(null); setGridDrag(null); setClockDrag(null); };

  // ---- whole-grid drag (admin authoring only) ----
  const onGridDragStart = (e) => {
    if (!gridMovable || !onMoveGrid) return;
    e.preventDefault();
    setGridDrag({ lastX: e.clientX, lastY: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const pts = (arr) => arr.map(([x, y]) => `${fx(x, y) * 100},${fy(y) * 100}`).join(" ");
  const rectCorners = (a, b) => {
    const x0 = Math.min(a[0], b[0]), x1 = Math.max(a[0], b[0]);
    const y0 = Math.min(a[1], b[1]), y1 = Math.max(a[1], b[1]);
    return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
  };
  // SVG viewBox is 0..100 square with preserveAspectRatio="none", so a <circle r> becomes a flat
  // screen ellipse on wide figures (probability scales are ~5–10:1) and the dots vanish. Size in
  // pixels and split rx/ry so dots stay round at any canvas aspect. Large dots only on 1-row
  // number/probability lines (rows===0); dense 2D grids (nets, cm paper) stay small.
  const pxRx = (px) => (box.w > 0 ? (px * 100) / box.w : px * 0.25);
  const pxRy = (px) => (box.h > 0 ? (px * 100) / box.h : px * 0.25);
  const isLineGrid = !!grid && rows === 0;
  const cornerRPx = isLineGrid ? 7 : 4.5;
  const cornerPendRPx = isLineGrid ? 8.5 : 5.5;
  const cornerRingRPx = isLineGrid ? 11 : 7.5;
  const delBadgeRPx = isLineGrid ? 7 : 5.5;
  const delBadgeOffPx = isLineGrid ? 11 : 8;
  // A label lifted above its dot reads as "next to that point" on a scattered lattice (A, B, C on cm
  // paper), but a SCALE is a stack of numbers one cell apart up a single line — "write a scale on the
  // vertical axis". There the same lift puts every number against the gridline above its own mark, and
  // the reader sees 0 floating clear of the axis it belongs to. Collinear points enclose nothing, so
  // they are never a shape whose corners need naming: centre their labels on the dot instead.
  // Mirrors InteractiveDiagramGrading::pointListIsCollinear.
  const pointsAreCollinear = (pts) => {
    const p = (Array.isArray(pts) ? pts : []).filter(
      (q) => Array.isArray(q) && q.length >= 2 && Number.isFinite(Number(q[0])) && Number.isFinite(Number(q[1]))
    );
    if (p.length < 3) return true;
    const [ax, ay] = [Number(p[0][0]), Number(p[0][1])];
    for (const q of p) {
      const dx = Number(q[0]) - ax;
      const dy = Number(q[1]) - ay;
      if (dx === 0 && dy === 0) continue;
      return p.every((r) => Math.abs(dx * (Number(r[1]) - ay) - dy * (Number(r[0]) - ax)) < 1e-9);
    }
    return true;
  };
  const scaleLabels = Array.isArray(spec?.target_points) && spec.target_points.length >= 3
    && pointsAreCollinear(spec.target_points);
  const labelDy = (v) => (scaleLabels ? v : v - pxRy(6));
  const labelBaseline = scaleLabels ? "central" : undefined;

  if (!src || imgFailed) {
    return (
      <Box sx={{ p: 2, border: "1px dashed", borderColor: "divider", borderRadius: 1, color: "text.secondary", fontSize: "0.85rem" }}>
        This interactive question needs its diagram image. Please ask your tutor to re-add the image.
      </Box>
    );
  }

  return (
    // Block-level with a definite width so the wrapper's `min(100%, …px)` cap resolves against the
    // container (not the image's intrinsic width). align-items:flex-start keeps children hugging their
    // content (no stretch), so the wrapper stays exactly the image size and the SVG overlay aligns.
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.75, width: "100%", maxWidth: "100%" }}>
      {isCutDrag && !readOnly && cdValue && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <ToggleButton
            size="small" value="removecut" selected={false} onChange={removeCut}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
          >
            Remove cut
          </ToggleButton>
          <ToggleButton
            size="small" value="resetpieces" selected={false} onChange={resetPieces}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
          >
            Reset pieces
          </ToggleButton>
        </Box>
      )}
      {/* Clear-last controls for the lines / segments tools. These sit ABOVE the image, in normal flow:
          as an absolute chip in the picture's top-right corner the button covered the corner grid dot,
          so a line of symmetry that ends there could not be drawn at all. */}
      {shadeLineTool && !readOnly && onChange && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <ToggleButton
            size="small" value="shade" selected={shadeTool === "shade"} onChange={() => { setPendingPt(null); setShadeTool("shade"); }}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
          >
            Shade squares
          </ToggleButton>
          <ToggleButton
            size="small" value="line" selected={shadeTool === "line"} onChange={() => setShadeTool("line")}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
          >
            Draw line
          </ToggleButton>
          {(placedLines.length > 0 || pendingPt) && (
            <ToggleButton
              size="small" value="clearlast" selected={false} onChange={clearLastLine}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              {pendingPt ? "Cancel point" : "Clear last line"}
            </ToggleButton>
          )}
        </Box>
      )}
      {((isLines && (placedLines.length > 0 || pendingPt || noneAllowed)) || (isSegments && (placedSegs.length > 0 || pendingNode !== null))) && !readOnly && onChange && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {(placedLines.length > 0 || pendingPt || isSegments) && (
            <ToggleButton
              size="small" value="clearlast" selected={false}
              onChange={isLines ? clearLastLine : clearLastSeg}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              {isLines
                ? (pendingPt ? "Cancel point" : "Clear last line")
                : (pendingNode !== null ? "Cancel" : "Clear last line")}
            </ToggleButton>
          )}
          {noneAllowed && placedLines.length === 0 && (
            <ToggleButton
              size="small" value="none" selected={declaredNone} onChange={toggleNoneDeclared}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              {noneLabel}
            </ToggleButton>
          )}
        </Box>
      )}
      {isAngle && !readOnly && onChange && angleRays.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <ToggleButton
            size="small" value="clearlastray" selected={false} onChange={clearLastAngleRay}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
          >
            Clear last arm
          </ToggleButton>
        </Box>
      )}
      {isTally && !readOnly && onChange && tallyCounts.some((n) => n > 0) && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <ToggleButton
            size="small"
            value="removetally"
            selected={false}
            onChange={removeOneTally}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
          >
            Remove one mark
          </ToggleButton>
        </Box>
      )}
      {isManualFill && !readOnly && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {!edgesAvailable && (
            <ToggleButton
              size="small" value="connect" selected={showConnect} disabled={placedPts.length < 2}
              onChange={() => setShowConnect((v) => !v)}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              Connect corners
            </ToggleButton>
          )}
          {fillEligible && (
            <ToggleButton
              size="small" value="fill" selected={manualFill} disabled={!canFill}
              onChange={() => setManualFill((v) => {
                const next = !v;
                if (next && requireShapeLabel && !currentShapeLabel && !shapeLabelPromptedRef.current) {
                  shapeLabelPromptedRef.current = true;
                  setShapeLabelPrompt({ value: "" });
                }
                return next;
              })}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              Fill in
            </ToggleButton>
          )}
          {edgesAvailable && manualEdges.length > 0 && (
            <ToggleButton
              size="small" value="clearedges" selected={false}
              onChange={() => { setManualEdges([]); setEdgePending(null); }}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              Clear edges
            </ToggleButton>
          )}
          {edgesAvailable && (
            <Box component="span" sx={{ fontSize: "0.72rem", color: ACTIVE_STROKE, fontWeight: 600 }}>
              {edgePending === null
                ? (spec?.edge_mode === "pairs"
                  ? "Tap two dots to draw a line between them. Tap a line to remove it."
                  : "Tap empty dots to place corners — each new corner joins the last and fills when the outline closes.")
                : (spec?.edge_mode === "pairs"
                  ? "Now tap the other end of the line — or tap the × to delete this dot, or tap it again to cancel."
                  : "Now tap the corner you want to join it to — or tap the × to delete this corner, or tap it again to cancel.")}
            </Box>
          )}
        </Box>
      )}
      <Box
        ref={wrapRef}
        sx={{ position: "relative", display: "inline-block", maxWidth: maxWidth ? `min(100%, ${maxWidth}px)` : "100%", lineHeight: 0, touchAction: "none", userSelect: "none" }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
      <img
        src={activeSrc}
        alt="Question diagram"
        style={{ maxWidth: "100%", display: "block" }}
        onError={() => {
          if (!usedRaw && resolved !== src && src) setUsedRaw(true);
          else setImgFailed(true);
        }}
      />
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
      >
        {/* SHADE regions — polygons (points[]) or circles (cx/cy/r, e.g. a tap-to-fill circle lattice) */}
        {kind === "shade" && regions.map((rg) => {
          const id = String(rg.id);
          // `given` regions are pre-coloured context (already shaded in the picture) — always shown
          // filled, never tappable, and not part of the student's selection.
          const given = !!rg.given;
          const givenMode = !!onToggleGiven; // editor is flagging pre-coloured cells
          const isShaded = given || (revealIds ? revealIds.includes(id) : shadedIds.includes(id));
          // Multi-group: the colour comes from the category the student (or, on reveal, the mark
          // scheme) assigned this region to — that's what makes each sector read as its own activity.
          // A `given` region stays plain grey: it's the worked example already printed in the picture.
          const assignedGroup = isGroupShade && !given
            ? (revealIds ? revealGroupOfRegion[id] : groupOfRegion[id])
            : null;
          const groupColor = assignedGroup ? groupColorByKey[assignedGroup] : null;
          const fill = isShaded
            ? (given ? GIVEN_FILL : (groupColor?.fill || (revealIds ? REVEAL_FILL : SHADE_FILL)))
            : "rgba(0,0,0,0)";
          const stroke = groupColor?.stroke || (revealIds ? REVEAL_STROKE : activeStroke);
          // Dense cell grids: faint idle borders so printed black patterns stay visible;
          // shaded fill remains the primary cue. Sparse pies keep the stronger guide stroke.
          // (Web uses non-scaling-stroke → widths are ~CSS px; opacity is what fades the lattice.)
          const denseShade = regions.length >= DENSE_SHADE_REGION_MIN;
          let strokeWidth = 0.4;
          let strokeOpacity = 1;
          if (denseShade) {
            if (isShaded || groupColor) {
              strokeWidth = 0.5;
              strokeOpacity = 0.85;
            } else {
              strokeWidth = 0.35;
              strokeOpacity = 0.28;
            }
          }
          // In given-mode every region stays tappable (so the admin can toggle the flag on/off);
          // otherwise a given region is locked and inert for the student — except in `label_only`
          // mode, where a labelled given region still accepts a dropped label chip directly on the
          // wedge (as well as on its floating label box).
          const locked = given && !givenMode;
          const dropTarget = labelOnly && locked && isLabelable(rg);
          const common = {
            fill,
            stroke,
            strokeWidth,
            strokeOpacity,
            vectorEffect: "non-scaling-stroke",
            style: {
              cursor: (readOnly || (locked && !dropTarget)) ? "default" : "pointer",
              pointerEvents: (locked && !dropTarget) ? "none" : "all",
            },
            onClick: locked ? undefined : () => (givenMode ? onToggleGiven(id) : toggleRegion(id)),
            onPointerDown: locked || givenMode ? undefined : (e) => {
              if (e.button != null && e.button !== 0) return;
              // Capture so drag continues across neighbouring cells without needing a wrap hit-target.
              e.currentTarget.setPointerCapture?.(e.pointerId);
              shadeSkipClickRef.current = true; // suppress the leftover click that would undo this paint
              paintShadeRegion(id, { startPaint: true });
            },
            onPointerMove: locked || givenMode ? undefined : (e) => {
              if (!shadePaintRef.current) return;
              const f = clientToFrac(e.clientX, e.clientY);
              if (!f) return;
              const nextId = findShadeRegionAtFrac(f[0], f[1]);
              if (nextId) paintShadeRegion(nextId);
            },
            onPointerUp: locked || givenMode ? undefined : () => {
              shadePaintRef.current = null;
            },
            onPointerCancel: locked || givenMode ? undefined : () => {
              shadePaintRef.current = null;
            },
            ...(dropTarget ? {
              onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
              onDrop: (e) => {
                e.preventDefault();
                const dragged = e.dataTransfer.getData("text/plain");
                if (dragged) setRegionLabel(id, dragged);
              },
            } : {}),
          };
          return rg.r != null ? (
            <circle key={id} cx={Number(rg.cx) * 100} cy={Number(rg.cy) * 100} r={Number(rg.r) * 100} {...common} />
          ) : (
            <polygon key={id} points={(rg.points || []).map(([x, y]) => `${x * 100},${y * 100}`).join(" ")} {...common} />
          );
        })}

        {showGridLines && grid && cols > 0 && (
          <g pointerEvents="none" stroke="#e91e63" strokeOpacity={0.55} strokeWidth={1} vectorEffect="non-scaling-stroke">
            {Array.from({ length: cols + 1 }).map((_, i) => (
              <line key={`gv${i}`} x1={(gx0 + i * gdx) * 100} y1={gy0 * 100} x2={(gx0 + i * gdx) * 100} y2={(gy0 + rows * gdy) * 100} vectorEffect="non-scaling-stroke" />
            ))}
            {Array.from({ length: rows + 1 }).map((_, j) => (
              <line key={`gh${j}`} x1={gx0 * 100} y1={(gy0 + j * gdy) * 100} x2={(gx0 + cols * gdx) * 100} y2={(gy0 + j * gdy) * 100} vectorEffect="non-scaling-stroke" />
            ))}
          </g>
        )}

        {/* POINTS / DRAG / RECT / LINES-with-grid dots — sized in screen pixels (aspect-corrected ellipses) so
            dots stay round. 1-row lines (probability scales) use larger dots; dense 2D grids stay small. */}
        {(kind === "points" || kind === "drag-shape" || isRect || isPoly || isTransform || (lineToolActive && grid && rows > 0)) && !readOnly && grid && (() => {
          const tappable = kind === "points" || isRect || isPoly || isTransform;
          const spacingPxX = gdx * (box.w || 400);
          const dyAuthored = Number(grid?.dy);
          const spacingPxY = (dyAuthored > 0 ? gdy : gdx) * (box.h || 200);
          const spacingPx = Math.max(8, Math.min(spacingPxX, spacingPxY) || spacingPxX);
          const visPx = isLineGrid
            ? Math.max(5.5, Math.min(7.5, spacingPx * 0.22))
            : Math.max(2.5, Math.min(3.5, spacingPx * 0.14));
          const hitPx = isLineGrid
            ? Math.max(visPx + 6, Math.min(22, spacingPx * 0.45))
            : Math.max(visPx + 4, Math.min(14, spacingPx * 0.4));
          // Dotty-paper lines: printed dots are already in the stem image — paint idle markers faintly
          // (capture rect snaps taps to the lattice; no extra hit targets needed).
          const idlePx = lineToolActive ? 1.5 : (isLineGrid ? 4 : 2.2);
          return Array.from({ length: rows + 1 }).flatMap((_, j) =>
            Array.from({ length: cols + 1 }).map((__, i) => (!onParity(i, j) ? null : (
              <g key={`g${i}-${j}`}>
                {tappable && !edgesAvailable && (
                  <ellipse cx={fx(i, j) * 100} cy={fy(j) * 100} rx={pxRx(hitPx)} ry={pxRy(hitPx)} fill="rgba(0,0,0,0)"
                    style={{ cursor: "pointer", pointerEvents: "all" }}
                    onClick={(e) => tapGrid(e.clientX, e.clientY)} />
                )}
                <ellipse cx={fx(i, j) * 100} cy={fy(j) * 100} rx={pxRx(tappable ? visPx : idlePx)} ry={pxRy(tappable ? visPx : idlePx)}
                  fill={lineToolActive
                    ? "rgba(25,118,210,0.12)"
                    : (tappable ? (isLineGrid ? "rgba(25,118,210,0.55)" : "rgba(25,118,210,0.30)") : "rgba(0,0,0,0.18)")}
                  style={{ pointerEvents: "none" }} />
              </g>
            )))
          );
        })()}

        {/* EDGE-DRAWING capture layer (reflect/rotate/closed polygon, student-editable). One rect over
            the whole canvas resolves every tap in JS (tapEdgeCanvas): snap to a grid cell, then
            occupied cell → arm/join, empty cell → place. It replaces the per-dot grid hit-circles
            (disabled above when edgesAvailable) so corner joins never depend on SVG paint order — the
            cause of horizontal/vertical joins failing while diagonals worked. The armed corner's × and
            the edge-remove hit-lines render AFTER this rect (later siblings paint on top), so those
            taps still win over the capture. */}
        {edgesAvailable && !readOnly && grid && (
          <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
            style={{ cursor: "pointer", pointerEvents: "all" }}
            onClick={(e) => tapEdgeCanvas(e.clientX, e.clientY)} />
        )}

        {/* RECT rectangle (placed + reveal) */}
        {isRect && (() => {
          const show = revealPts && revealPts.length >= 2 ? revealPts : placedPts;
          const stroke = revealPts ? REVEAL_STROKE : activeStroke;
          const fillPoly = revealPts ? REVEAL_FILL : "rgba(25,118,210,0.20)";
          return (
            <>
              {show.length >= 2 && (
                <polygon points={pts(rectCorners(show[0], show[1]))} fill={fillPoly} stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
              )}
              {show.slice(0, 2).map(([x, y], i) => (
                <ellipse key={`rc${i}`} cx={fx(x, y) * 100} cy={fy(y) * 100} rx={pxRx(cornerRPx)} ry={pxRy(cornerRPx)} fill={stroke} style={{ pointerEvents: "none" }} />
              ))}
              {/* First corner placed, second still pending — a small "1 of 2" badge clarifies that
                  one more tap (the opposite corner) completes the rectangle. */}
              {!readOnly && show.length === 1 && (
                <text x={fx(show[0][0], show[0][1]) * 100 + 2.5} y={fy(show[0][1]) * 100 - 2.5} fontSize="3.2" fill={stroke} style={{ pointerEvents: "none" }}>
                  1 of 2
                </text>
              )}
            </>
          );
        })()}

        {/* original shape ghost (drag-shape) */}
        {kind === "drag-shape" && original.length >= 2 && (
          <polygon points={pts(original)} fill="rgba(0,0,0,0.05)" stroke="#999" strokeWidth={0.4} strokeDasharray="2 2" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
        )}

        {/* reflect / rotate guide: the shape to transform (solid dark outline) + the mirror line
            (reflect) or rotation centre (rotate). The student places the transformed vertices as points. */}
        {/* All transform guides are tap-transparent: they render AFTER the grid hit-circles, and SVG's
            default visiblePainted pointer-events would let their painted fill/stroke swallow taps on
            every intersection they cover (the 45° mirror passes exactly through lattice points, which
            blocked placing/joining the corners under it). */}
        {/* An open figure (closed:false) is a POLYLINE — its two ends stay apart, usually because the
            mirror line itself closes it on the paper. Drawing it as a polygon invented a side that is not
            printed and shaded an area the shape does not enclose, so the guide no longer matched the
            figure the student is asked to reflect. */}
        {isTransform && original.length >= 2 && (closed ? (
          <polygon points={pts(original)} fill="rgba(0,0,0,0.06)" stroke="#333" strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
        ) : (
          <polyline points={pts(original)} fill="none" stroke="#333" strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
        ))}
        {isTransform && original.length >= 1 && typeof spec?.original_shape_label === "string" && spec.original_shape_label.trim() !== "" && (() => {
          const cx = original.reduce((s, p) => s + Number(p[0]), 0) / original.length;
          const cy = original.reduce((s, p) => s + Number(p[1]), 0) / original.length;
          return (
            <text x={fx(cx, cy) * 100} y={fy(cy) * 100} fontSize={4.2} fontWeight="800" fill="#333"
              textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: "none" }}>
              {spec.original_shape_label.trim()}
            </text>
          );
        })()}
        {isReflect && Array.isArray(spec?.mirror) && spec.mirror.length >= 2 && (() => {
          const [a, b] = spec.mirror;
          const dx = b[0] - a[0], dy = b[1] - a[1];
          const ex = a[0] - dx * 2, ey = a[1] - dy * 2, fx2 = b[0] + dx * 2, fy2 = b[1] + dy * 2;
          return <line x1={ex * 100} y1={ey * 100} x2={fx2 * 100} y2={fy2 * 100} stroke="#c62828" strokeWidth={0.6} strokeDasharray="2.5 2" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />;
        })()}
        {isRotate && Array.isArray(spec?.center) && spec.center.length >= 2 && (() => {
          const cx = spec.center[0] * 100, cy = spec.center[1] * 100;
          return (
            <g style={{ pointerEvents: "none" }}>
              <line x1={cx - 2} y1={cy} x2={cx + 2} y2={cy} stroke="#c62828" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
              <line x1={cx} y1={cy - 2} x2={cx} y2={cy + 2} stroke="#c62828" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
            </g>
          );
        })()}

        {/* context guide: points/shape carried over from an earlier part sharing this grid (grey, inert).
            Drawn as a light outline when ≥3 corners (so the triangle from a plot-part shows on the
            reflect-part) plus a labelled dot per point. Never interactive, never graded. */}
        {contextPoints.length > 0 && (kind === "points" || kind === "drag-shape" || kind === "lines" || isPoly || isTransform) && (
          <g>
            {/* The auto-drawn guide outline is suppressed on an edge-drawing question: joining the corners
                IS the task ("draw the parallelogram ABCD"), so pre-drawing A→B→C both gives away part of
                the answer and reads as a shape the student didn't draw. */}
            {contextPoints.length >= 3 && !edgesAvailable && (
              <polygon points={pts(cyclicBoundary(contextPoints))} fill="rgba(0,0,0,0.05)" stroke="#8a8a8a" strokeWidth={0.5} strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
            )}
            {contextPoints.map((pt, i) => {
              const [x, y] = pt;
              const lbl = typeof pt[2] === "string" ? pt[2] : "";
              // Given corners are joinable when edges are in play, so they get the same armed ring as a
              // placed corner. They are never removable — no × — since the question supplied them.
              const isPend = edgesAvailable && edgePending === placedPts.length + i;
              // A 4th element carries the colour of the sub-question that plotted this point on a shared
              // grid, so the student can tell whose mark it is. Absent -> the usual inert grey.
              const own = typeof pt[3] === "string" && pt[3] ? pt[3] : null;
              return (
                <g key={`ctx${i}`} style={{ pointerEvents: "none" }}>
                  {isPend && <ellipse cx={fx(x, y) * 100} cy={fy(y) * 100} rx={pxRx(cornerRingRPx)} ry={pxRy(cornerRingRPx)} fill="none" stroke="#0d47a1" strokeWidth={1} vectorEffect="non-scaling-stroke" />}
                  <ellipse cx={fx(x, y) * 100} cy={fy(y) * 100} rx={pxRx(isPend ? cornerPendRPx : cornerRPx)} ry={pxRy(isPend ? cornerPendRPx : cornerRPx)} fill={isPend ? "#0d47a1" : (own || "#8a8a8a")} />
                  {lbl ? (
                    <text x={fx(x, y) * 100 + pxRx(6)} y={labelDy(fy(y) * 100)} dominantBaseline={labelBaseline} fontSize={3.4} fontWeight="700" fill={isPend ? "#0d47a1" : (own || "#6a6a6a")}>{lbl}</text>
                  ) : null}
                </g>
              );
            })}
          </g>
        )}

        {/* Work committed on OTHER parts of a shared grid, one colour per part. Read-only guides: every
            element sets pointerEvents none, or they swallow taps on the grid hit-circles underneath. */}
        {Array.isArray(contextLayers) && contextLayers.length > 0 && (
          <g style={{ pointerEvents: "none" }}>
            {contextLayers.map((layer, li) => {
              const c = layer?.color || "#8a8a8a";
              return (
                <g key={`cl${li}`} opacity={0.7}>
                  {(Array.isArray(layer?.edges) ? layer.edges : []).map((e, i) => (
                    <line key={`cle${i}`}
                      x1={fx(e[0][0], e[0][1]) * 100} y1={fy(e[0][1]) * 100}
                      x2={fx(e[1][0], e[1][1]) * 100} y2={fy(e[1][1]) * 100}
                      stroke={c} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                  ))}
                  {(Array.isArray(layer?.lines) ? layer.lines : []).map((l, i) => (
                    <line key={`cll${i}`}
                      x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                      stroke={c} strokeWidth={0.8} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
                  ))}
                  {/* Bars another part of the same chart has already drawn — the columns stay on the
                      chart while the scale part holds the tools, and vice versa. */}
                  {(Array.isArray(layer?.marker?.bars) ? layer.marker.bars : []).map((bar, i) => {
                    const wU = (Number(layer.marker.width) || 0.05) * 100;
                    const base = Number(layer.marker.base) * 100;
                    const top = Number(bar[1]) * 100;
                    return (
                      <rect key={`clm${i}`} x={Number(bar[0]) * 100 - wU} y={Math.min(base, top)}
                        width={wU * 2} height={Math.abs(base - top)}
                        fill={c} fillOpacity={0.18} stroke={c} strokeWidth={0.5}
                        vectorEffect="non-scaling-stroke" />
                    );
                  })}
                  {(() => {
                    // Judged on THIS layer's own marks: the active part may be a marker (no target_points
                    // of its own), and it is the layer's scale that has to sit on the gridlines.
                    const layerScale = pointsAreCollinear(layer?.points);
                    return (Array.isArray(layer?.points) ? layer.points : []).map((p, i) => (
                      <g key={`clp${i}`}>
                        <ellipse cx={fx(p[0], p[1]) * 100} cy={fy(p[1]) * 100} rx={pxRx(cornerRPx)} ry={pxRy(cornerRPx)} fill={c} />
                        {typeof p[2] === "string" && p[2] ? (
                          <text x={fx(p[0], p[1]) * 100 + pxRx(6)}
                            y={layerScale ? fy(p[1]) * 100 : fy(p[1]) * 100 - pxRy(6)}
                            dominantBaseline={layerScale ? "central" : undefined}
                            fontSize={3.2} fontWeight="700" fill={c}>{p[2]}</text>
                        ) : null}
                      </g>
                    ));
                  })()}
                  {/* The letter naming this part's shape ("label the new shape B"). It is part of that
                      part's answer, so it stays on the figure when another part takes the tools —
                      switching parts used to leave every earlier shape anonymous. */}
                  {(() => {
                    const lbl = typeof layer?.shape_label === "string" ? layer.shape_label.trim() : "";
                    if (!lbl) return null;
                    const corners = (Array.isArray(layer?.label_points) && layer.label_points.length > 0
                      ? layer.label_points
                      : (Array.isArray(layer?.edges) ? layer.edges.flat() : [])
                    ).filter((p) => Array.isArray(p) && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])));
                    if (corners.length === 0) return null;
                    const cx = corners.reduce((s, p) => s + Number(p[0]), 0) / corners.length;
                    const cy = corners.reduce((s, p) => s + Number(p[1]), 0) / corners.length;
                    return (
                      <text x={fx(cx, cy) * 100} y={fy(cy) * 100} fontSize={4.2} fontWeight="800" fill={c}
                        textAnchor="middle" dominantBaseline="central">{lbl}</text>
                    );
                  })()}
                </g>
              );
            })}
          </g>
        )}

        {/* placed / reveal polygon + vertices */}
        {(kind === "points" || kind === "drag-shape" || isPoly || isTransform) && (() => {
          const showPts = revealPts ? revealPts : (kind === "drag-shape" ? placedShape : placedPts);
          const stroke = revealPts ? REVEAL_STROKE : activeStroke;
          // A completed closed shape (all required corners placed) fills solidly so it reads as a drawn
          // bar/shape; while still in progress it stays lighter to signal it isn't finished yet.
          const complete = pointCount > 0 && showPts.length >= pointCount;
          // The model answer is drawn as an OUTLINE, never a solid. A multi-shape reveal ("reflect both
          // shapes") fills only the run that resolves to a closed loop, so one shape came out solid blue
          // and its partner bare — the student reads that as "only this one counts". Nothing about these
          // questions is a shading answer, so the fill carries no meaning to lose.
          const isRevealDraw = !!revealPts || !!revealEdges;
          const fillPoly = isRevealDraw ? "none" : (complete ? "rgba(25,118,210,0.42)" : "rgba(25,118,210,0.18)");
          const asPolygon = showPts.length >= 3 && (isPoly || closed);
          // Manual-fill kinds (points/polygon/reflect/rotate) while the student is answering: nothing is
          // drawn as points are placed — the outline appears only when the student taps "Connect corners"
          // and the colour fill only when they tap "Fill in", so there's never a half-drawn bowtie and no
          // wrong auto-fill. For reflect/rotate the boundary is joined in the derived reflected/rotated
          // order; for points/polygon it follows the tap order the student traced.
          // While the student answers, the outline/fill follow the Connect/Fill toggles. On a readOnly
          // review of a submitted answer (no reveal overlay) show the joined outline; fill only if
          // they tapped Fill in (auto-filling one of two Chigwell shapes looked half-marked).
          const manualAnswering = isManualFill && !revealPts;
          // On edge-drawing shapes the student draws the outline by hand (manual-edge branch above), so
          // the auto-connect outline is suppressed while answering — only the manual edges show. The
          // reveal/readOnly review path still auto-joins so the marker sees a finished shape.
          const showOutline = manualAnswering && !edgesAvailable && (readOnly || showConnect);
          const showFill = manualAnswering && !edgesAvailable && manualFill && showPts.length >= 3;
          const orderCCW = (g) => {
            if (g.length < 3) return g;
            const cx = g.reduce((s, p) => s + p[0], 0) / g.length;
            const cy = g.reduce((s, p) => s + p[1], 0) / g.length;
            return [...g].sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
          };
          // A CLOSED single shape drawn from bare corners (no hand-drawn edges) must be joined round its
          // boundary, not in tap order — otherwise the same four correct corners render as a self-crossing
          // bowtie purely because of the order they were tapped in, while the reveal (stored order) draws
          // the real quadrilateral. That mismatch is what makes a right answer LOOK wrong on review.
          // Grading is unaffected (it is order-independent for these); this is presentation only.
          const orderedShow = isTransform
            ? (isRevealDraw ? showPts : orderTransformPts(showPts))
            : (closed && shapeCount === 1 && showPts.length >= 3 && !manualEdges.length
                ? orderCCW(showPts)
                : showPts);
          // Multi-shape polygon ("sketch two rectangles"): break the placed corners into consecutive
          // runs of cornersPerShape and draw/fill each as its own quad, so two rectangles never join
          // into one bowtie. Each fully-placed run is ordered CCW round its centroid to fill cleanly.
          const shapeGroups = shapeCount > 1 && cornersPerShape > 0
            ? Array.from({ length: Math.ceil(orderedShow.length / cornersPerShape) }, (_, i) =>
                orderedShow.slice(i * cornersPerShape, i * cornersPerShape + cornersPerShape))
            : [orderedShow];
          // Manual edges (student-drawn): resolve each undirected corner-pair key back to the two placed
          // points and draw them as hand-placed segments. When they form a single closed loop, fill it
          // in the loop order the student traced (concave-safe — no centroid sort).
          // Given (context) corners are joinable on an edge-drawing question, so they must resolve here
          // too — otherwise every edge touching A/B/C fails the byCorner lookup and is silently dropped,
          // and the outline can never be drawn. The review page replays the student's submitted drawing
          // through revealValue ({points, edges}), so those edges resolve here as well — without this the
          // marker sees only dots and the drawing looks lost.
          // On a read-only replay, edges stored on the value ARE the student's drawing — draw them even
          // when this spec would not offer the edge tool (a review row whose spec no longer resolves to
          // a join-to-context shape), or the lines they drew disappear from the marked answer.
          const edgesToDraw = revealEdges ?? ((manualAnswering || readOnly) ? manualEdges : []);
          const byCorner = new Map(
            [...showPts, ...(edgesAvailable || revealEdges ? contextPoints : [])]
              .map((p) => [cornerKey(p), p])
          );
          // A reveal's edges ARE the model answer's geometry, and they name corners the reveal never
          // plots itself: "plot D, then join A-B, B-C, C-D, D-A" targets only D, while A/B/C are the
          // student's own marks from an earlier part and so appear on neither showPts nor
          // context_points. Every edge touching them failed the lookup and was dropped, leaving the
          // correct solution drawn as a lone dot with no shape. The key IS the grid coordinate, so
          // read the corner straight off it. Student-drawn edges keep the strict lookup — an edge
          // they could not have drawn must still be dropped rather than invented.
          // A submitted answer being REVIEWED gets the same relaxation. Its edges are a record of what
          // the student drew, and a corner they anchored to can be unresolvable by then: the shared-grid
          // carry-forward drops a given point whose label an earlier part owns whenever that part's mark
          // is not to hand, and every edge touching it was then discarded — Merchant Taylors 2024 P1
          // Q28(b) showed the plotted S alone with no kite, St Albans 2025 Q22(b) one side of a drawn
          // triangle. Read the corner off the edge key so the drawing survives. Still strict while
          // answering, where an edge to a corner that is not on the canvas must not be invented.
          const cornerAt = (k) => {
            const known = byCorner.get(k);
            if (known || !(revealEdges || readOnly)) return known;
            const [gx, gy] = String(k).split(",").map(Number);
            return Number.isFinite(gx) && Number.isFinite(gy) ? [gx, gy] : undefined;
          };
          const manualSegs = edgesToDraw.length
            ? edgesToDraw.map((k) => k.split("|")).map(([a, b]) => [cornerAt(a), cornerAt(b)]).filter(([a, b]) => a && b)
            : [];
          const manualLoops = (() => {
            if (manualSegs.length < 3) return [];
            const adj = new Map();
            edgesToDraw.forEach((k) => {
              const [a, b] = k.split("|");
              if (!cornerAt(a) || !cornerAt(b)) return;
              adj.set(a, [...(adj.get(a) || []), b]);
              adj.set(b, [...(adj.get(b) || []), a]);
            });
            const keys = [...adj.keys()];
            // Every vertex must have exactly 2 edges, or nothing here closes into a ring. A half-drawn
            // outline is the normal state of a question mid-answer, so this returns NO loops — never
            // null, which both readers below then dereferenced ("Cannot read properties of null").
            if (keys.length !== manualSegs.length) return [];
            if (![...adj.values()].every((n) => n.length === 2)) return [];
            // Walk each ring separately. Two DISJOINT rings ("reflect both shapes on this grid" — five
            // corners each, ten edges, every degree 2) pass both checks above, and walking from one
            // start alone went round the first shape twice: one shape got filled, its partner got
            // nothing. Take each unvisited vertex as its own start, and require every ring to close.
            const loops = []; const seen = new Set();
            for (const start of keys) {
              if (seen.has(start)) continue;
              const loop = []; let prev = null, cur = start;
              do {
                seen.add(cur);
                loop.push(cornerAt(cur));
                const [n1, n2] = adj.get(cur);
                const nxt = n1 === prev ? n2 : n1;
                prev = cur; cur = nxt;
              } while (cur !== start && loop.length <= keys.length);
              if (cur !== start || loop.length < 3) return [];
              loops.push(loop);
            }
            return loops;
          })();
          if (edgesToDraw.length) {
            // Fill only when the student tapped Fill in — never auto-fill on readOnly review
            // (Chigwell Q24: two shapes looked half-filled when a buggy dual-cycle walk shaded one).
            // Dual closed components each get their own fill polygon.
            // Gated on `closed`: an open spec (disjoint lines) has no enclosed area.
            // Fill only a ring the student actually closed. Falling back to the convex hull of every
            // placed corner invented sides they never drew, then the tap-order chain still painted on
            // top — a half-finished pentagon grew a diagonal through a filled quadrilateral.
            const fillPolys = closed && manualLoops.length ? manualLoops : [];
            return (
              <>
                {!isRevealDraw && (readOnly || manualFill) && fillPolys.map((poly, i) => (
                  <polygon key={`mf${i}`} points={pts(poly)} fill="rgba(25,118,210,0.42)" stroke="none" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                ))}
                {manualSegs.map(([a, b], i) => (
                  <line key={`me${i}`} x1={fx(a[0], a[1]) * 100} y1={fy(a[1]) * 100} x2={fx(b[0], b[1]) * 100} y2={fy(b[1]) * 100}
                    stroke={stroke} strokeWidth={1} vectorEffect="non-scaling-stroke" strokeLinecap="round" style={{ pointerEvents: "none" }} />
                ))}
                {/* Tap an edge to remove it — the wide invisible hit line is inset from both endpoints
                    so it never covers the corners (a corner tap must arm/join, not delete the edge). */}
                {!readOnly && manualEdges.map((k, i) => {
                  const [ka, kb] = k.split("|");
                  const a = byCorner.get(ka), b = byCorner.get(kb);
                  if (!a || !b) return null;
                  const ax = fx(a[0], a[1]) * 100, ay = fy(a[1]) * 100;
                  const bx = fx(b[0], b[1]) * 100, by = fy(b[1]) * 100;
                  const t0 = 0.22, t1 = 0.78;
                  return (
                    <line key={`meh${i}`}
                      x1={ax + (bx - ax) * t0} y1={ay + (by - ay) * t0}
                      x2={ax + (bx - ax) * t1} y2={ay + (by - ay) * t1}
                      stroke="rgba(0,0,0,0)" strokeWidth={3} vectorEffect="non-scaling-stroke"
                      style={{ cursor: "pointer", pointerEvents: "stroke" }}
                      onClick={(e) => { e.stopPropagation(); setManualEdges((es) => es.filter((x) => x !== k)); }} />
                  );
                })}
                {showPts.map((pt, i) => {
                  const [x, y] = pt;
                  const isPend = edgesAvailable && edgePending === i;
                  const typed = (typeof pt[2] === "string" && pt[2] !== "") ? pt[2] : "";
                  const lbl = typed || ((spec?.require_point_labels && !revealPts) ? "" : pointLabels[i]);
                  return (
                    <g key={`p${i}`}>
                      {isPend && <ellipse cx={fx(x, y) * 100} cy={fy(y) * 100} rx={pxRx(cornerRingRPx)} ry={pxRy(cornerRingRPx)} fill="none" stroke="#0d47a1" strokeWidth={1} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />}
                      <ellipse cx={fx(x, y) * 100} cy={fy(y) * 100} rx={pxRx(isPend ? cornerPendRPx : cornerRPx)} ry={pxRy(isPend ? cornerPendRPx : cornerRPx)} fill={isPend ? "#0d47a1" : stroke} style={{ pointerEvents: "none" }} />
                      {/* Delete × shows ONLY on the armed corner, so it never clutters the grid and can't
                          collide with the tap rhythm of working round the shape (tap a corner to end an
                          edge, tap it again to start the next). Tap the corner to arm it → its × appears. */}
                      {isPend && !readOnly && (
                        <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); removeCorner(i); }}>
                          <ellipse cx={fx(x, y) * 100 + pxRx(delBadgeOffPx)} cy={fy(y) * 100 - pxRy(delBadgeOffPx)} rx={pxRx(delBadgeRPx)} ry={pxRy(delBadgeRPx)} fill="#fff" stroke="#c62828" strokeWidth={0.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "all" }} />
                          <text x={fx(x, y) * 100 + pxRx(delBadgeOffPx)} y={fy(y) * 100 - pxRy(delBadgeOffPx)} fontSize={3} fontWeight={700} fill="#c62828" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: "none", userSelect: "none" }}>×</text>
                        </g>
                      )}
                      {lbl && !labelOverlayActive ? <text x={fx(x, y) * 100 + pxRx(6)} y={labelDy(fy(y) * 100)} dominantBaseline={labelBaseline} fontSize={3.4} fontWeight="700" fill={stroke} style={{ pointerEvents: "none", userSelect: "none" }}>{lbl}</text> : null}
                    </g>
                  );
                })}
              </>
            );
          }
          return (
            <>
              {manualAnswering
                ? ((showOutline || showFill) && showPts.length >= 2 && shapeGroups.map((g, gi) => (
                    g.length >= 2 ? (
                      <polygon key={`sg${gi}`} points={pts(orderCCW(g))}
                        fill={showFill && g.length >= 3 ? "rgba(25,118,210,0.42)" : "none"}
                        stroke={showOutline ? stroke : "none"} strokeWidth={0.6}
                        vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                    ) : null
                  )))
                : (connect && showPts.length >= 2 && (
                    shapeCount > 1
                      ? shapeGroups.map((g, gi) => (
                          g.length >= 2 ? (
                            g.length >= 3 && closed
                              ? <polygon key={`sg${gi}`} points={pts(orderCCW(g))} fill={fillPoly} stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                              : <polyline key={`sg${gi}`} points={pts(g)} fill="none" stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                          ) : null
                        ))
                      : asPolygon
                        ? <polygon points={pts(showPts)} fill={fillPoly} stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                        : <polyline points={pts(showPts)} fill="none" stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                  ))}
              {showPts.map((pt, i) => {
                const [x, y] = pt;
                // Prefer a label the student typed on the point itself (3rd element), then fall back
                // to the admin's positional point_labels guidance. But when the label is REQUIRED and
                // graded (require_point_labels) and we're not showing the reveal, never draw the
                // expected letter as guidance — that would hand the student the answer and look like it
                // auto-filled. Only their own typed label shows; the empty input box prompts them.
                const typed = (typeof pt[2] === "string" && pt[2] !== "") ? pt[2] : "";
                const lbl = typed || ((spec?.require_point_labels && !revealPts) ? "" : pointLabels[i]);
                const isPend = edgesAvailable && edgePending === i;
                return (
                  <g key={`p${i}`}>
                    {isPend && <ellipse cx={fx(x, y) * 100} cy={fy(y) * 100} rx={pxRx(cornerRingRPx)} ry={pxRy(cornerRingRPx)} fill="none" stroke="#0d47a1" strokeWidth={1} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />}
                    <ellipse
                      cx={fx(x, y) * 100}
                      cy={fy(y) * 100}
                      rx={pxRx(isPend ? cornerPendRPx : cornerRPx)}
                      ry={pxRy(isPend ? cornerPendRPx : cornerRPx)}
                      fill={isPend ? "#0d47a1" : stroke}
                      style={{ cursor: kind === "drag-shape" && !readOnly ? "grab" : "default", pointerEvents: kind === "drag-shape" && !readOnly ? "all" : "none" }}
                      onPointerDown={kind === "drag-shape" ? onShapePointerDown : undefined}
                    />
                    {/* Delete × on the armed corner only (see manual-edge branch) — tap a corner to arm
                        it, its × appears; no always-on badge, no collision with the edge-drawing rhythm. */}
                    {isPend && !readOnly && (
                      <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); removeCorner(i); }}>
                        <ellipse cx={fx(x, y) * 100 + pxRx(delBadgeOffPx)} cy={fy(y) * 100 - pxRy(delBadgeOffPx)} rx={pxRx(delBadgeRPx)} ry={pxRy(delBadgeRPx)} fill="#fff" stroke="#c62828" strokeWidth={0.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "all" }} />
                        <text x={fx(x, y) * 100 + pxRx(delBadgeOffPx)} y={fy(y) * 100 - pxRy(delBadgeOffPx)} fontSize={3} fontWeight={700} fill="#c62828" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: "none", userSelect: "none" }}>×</text>
                      </g>
                    )}
                    {lbl && !labelOverlayActive ? (
                      <text x={fx(x, y) * 100 + pxRx(6)} y={labelDy(fy(y) * 100)} dominantBaseline={labelBaseline} fontSize={3.4} fontWeight="700" fill={stroke} style={{ pointerEvents: "none", userSelect: "none" }}>
                        {lbl}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </>
          );
        })()}

        {/* LINES — tap two points (no grid) to draw each line; repeat for several. The capture rect
            sits first so taps start a new line; per-line hit lines paint on top to allow removal. */}
        {(isLines || shadeLinesSpec) && (() => {
          const showLines = revealLines ? revealLines : placedLines;
          const stroke = revealLines ? REVEAL_STROKE : activeStroke;
          // "Mark the parallel lines with an arrow" — the standard maths notation is a chevron drawn ON the
          // side, not a plain segment. A `lines` spec can opt into it (line_style: "arrow"); the geometry,
          // tapping and grading are identical, only the glyph differs.
          const arrowLines = spec?.line_style === "arrow";
          const chevron = (l, i, colour) => {
            const [[x1, y1], [x2, y2]] = l;
            const mx = (x1 + x2) / 2 * 100;
            const my = (y1 + y2) / 2 * 100;
            const ang = Math.atan2(y2 - y1, x2 - x1);
            const size = 2.6;
            // Two strokes swept back from the midpoint, forming a ">" pointing along the line.
            const p = (spread) => [
              mx - size * Math.cos(ang - spread),
              my - size * Math.sin(ang - spread),
            ];
            const [ax, ay] = p(0.55);
            const [bx, by] = p(-0.55);
            return (
              <polyline key={`ar${i}`} points={`${ax},${ay} ${mx},${my} ${bx},${by}`}
                fill="none" stroke={colour} strokeWidth={1.1} strokeLinecap="round" strokeLinejoin="round"
                vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
            );
          };
          return (
            <>
              {!readOnly && (shadeLinesSpec ? (shadeLineTool && shadeTool === "line") : true) && (
                <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
                  style={{ cursor: "crosshair", pointerEvents: "all", touchAction: "none" }}
                  onPointerDown={onLinePointerDown}
                  onPointerMove={onLinePointerMove}
                  onPointerUp={onLinePointerUp}
                  onClick={(e) => tapLine(e.clientX, e.clientY)} />
              )}
              {showLines.map((l, i) => (
                <line key={`ln${i}`} x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                  stroke={stroke} strokeWidth={1.6} strokeLinecap="round" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              ))}
              {arrowLines && showLines.map((l, i) => chevron(l, i, stroke))}
              {/* The end of a drawn line is a tick, not a plotted point: at the old 6px radius the two
                  dots were far heavier than the 0.8px line between them, so a symmetry line read as a
                  pair of points and students aimed at the dots instead of along the line (North London
                  2013 Q20a). The line is now the heavy mark and the ends only anchor it. */}
              {showLines.flatMap((l, i) => l.slice(0, 2).map(([x, y], k) => (
                <ellipse key={`le${i}-${k}`} cx={x * 100} cy={y * 100} rx={pxRx(3)} ry={pxRy(3)} fill={stroke} style={{ pointerEvents: "none" }} />
              )))}
              {!readOnly && pendingPt && (
                <ellipse cx={pendingPt[0] * 100} cy={pendingPt[1] * 100} rx={pxRx(7)} ry={pxRy(7)} fill="rgba(25,118,210,0.6)" style={{ pointerEvents: "none" }} />
              )}
              {!readOnly && pendingPt && dragLineEnd && (
                <line x1={pendingPt[0] * 100} y1={pendingPt[1] * 100}
                  x2={dragLineEnd[0] * 100} y2={dragLineEnd[1] * 100}
                  stroke={ACTIVE_STROKE} strokeWidth={0.7} strokeDasharray="2.2 1.6"
                  strokeLinecap="round" vectorEffect="non-scaling-stroke"
                  style={{ pointerEvents: "none" }} />
              )}
            </>
          );
        })()}

        {/* CUT-DRAG — tap two points to cut the shape; the pieces become draggable so the student can
            slide one to reassemble the target shape. Reveal shows the canonical cut + assembled pieces. */}
        {isCutDrag && (() => {
          const fills = cdReveal ? [REVEAL_FILL, REVEAL_FILL] : ["rgba(25,118,210,0.30)", "rgba(237,108,2,0.30)"];
          const strokes = cdReveal ? [REVEAL_STROKE, REVEAL_STROKE] : ["#1976d2", "#ed6c02"];
          return (
            <>
              {!readOnly && !cdValue && !cdReveal && (
                <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
                  style={{ cursor: "crosshair", pointerEvents: "all" }}
                  onClick={(e) => tapCut(e.clientX, e.clientY)} />
              )}
              {!readOnly && !cdValue && pendingPt && (
                <circle cx={pendingPt[0] * 100} cy={pendingPt[1] * 100} r={1.8} fill="rgba(25,118,210,0.6)" style={{ pointerEvents: "none" }} />
              )}
              {/* Cut drawn but missing the shape — show it so the student can remove and retry. */}
              {cdShow && !cdPieces && (
                <line x1={cdShow.cut[0][0] * 100} y1={cdShow.cut[0][1] * 100} x2={cdShow.cut[1][0] * 100} y2={cdShow.cut[1][1] * 100}
                  stroke={cdReveal ? REVEAL_STROKE : activeStroke} strokeWidth={0.9} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              )}
              {/* Where the cut was made, dashed, under the (possibly moved) pieces. */}
              {cdPieces && (
                <line x1={cdShow.cut[0][0] * 100} y1={cdShow.cut[0][1] * 100} x2={cdShow.cut[1][0] * 100} y2={cdShow.cut[1][1] * 100}
                  stroke={cdReveal ? REVEAL_STROKE : "#888"} strokeWidth={0.6} strokeDasharray="2 2" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              )}
              {cdPieces && cdPieces.map((poly, i) => {
                const ox = Number(cdOffsets[i]?.[0]) || 0, oy = Number(cdOffsets[i]?.[1]) || 0;
                return (
                  <polygon key={`cd${i}`}
                    points={poly.map(([x, y]) => `${(x + ox) * 100},${(y + oy) * 100}`).join(" ")}
                    fill={fills[i]} stroke={strokes[i]} strokeWidth={0.7} vectorEffect="non-scaling-stroke"
                    style={{ cursor: readOnly ? "default" : "grab", pointerEvents: readOnly ? "none" : "all", touchAction: "none" }}
                    onPointerDown={readOnly ? undefined : onPiecePointerDown(i)} />
                );
              })}
            </>
          );
        })()}

        {/* SEGMENTS — join two of the fixed nodes (crosses) with a straight line. Tap a node to start,
            tap another to draw; tap a drawn line to remove it. */}
        {isSegments && (() => {
          const showSegs = revealSegs ? revealSegs : placedSegs;
          const stroke = revealSegs ? REVEAL_STROKE : activeStroke;
          return (
            <>
              {showSegs.map((l, i) => (
                <line key={`sg${i}`} x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                  stroke={stroke} strokeWidth={0.8} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              ))}
              {!readOnly && placedSegs.map((l, i) => (
                <line key={`sh${i}`} x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                  stroke="rgba(0,0,0,0)" strokeWidth={3} vectorEffect="non-scaling-stroke"
                  style={{ cursor: "pointer", pointerEvents: "stroke" }} onClick={(e) => removeSeg(i, e)} />
              ))}
              {nodes.map((n, i) => {
                const cx = Number(n[0]) * 100, cy = Number(n[1]) * 100, on = pendingNode === i;
                return (
                  <g key={`nd${i}`}>
                    <line x1={cx - 1.2} y1={cy - 1.2} x2={cx + 1.2} y2={cy + 1.2} stroke="#111" strokeWidth={0.7} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                    <line x1={cx - 1.2} y1={cy + 1.2} x2={cx + 1.2} y2={cy - 1.2} stroke="#111" strokeWidth={0.7} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                    {on && <circle cx={cx} cy={cy} r={2.4} fill="none" stroke={ACTIVE_STROKE} strokeWidth={0.8} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />}
                    {!readOnly && (
                      <circle cx={cx} cy={cy} r={3} fill="rgba(0,0,0,0)" style={{ cursor: "pointer", pointerEvents: "all" }} onClick={() => tapNode(i)} />
                    )}
                  </g>
                );
              })}
            </>
          );
        })()}

        {/* GLYPH tap-capture — a tap on empty space adds a new glyph. The letters themselves render as
            HTML overlays (below) so they keep correct proportions despite the non-uniform viewBox. */}
        {isGlyph && !readOnly && (
          <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
            style={{ cursor: "text", pointerEvents: "all" }}
            // Clicking empty canvas while a letter is selected DESELECTS it (closing the toolbar) rather
            // than dropping another letter — otherwise there is no way to dismiss the toolbar without
            // adding an unwanted glyph. Click again on empty canvas to add the next one.
            onClick={(e) => {
              if (activeGlyph != null) {
                setSelGlyph(null);
                return;
              }
              addGlyph(e.clientX, e.clientY);
            }} />
        )}

        {/* ANGLE — tap to swing an arm out from the printed vertex. Arms are drawn to the edge of the
            figure rather than to the tap, so arm LENGTH never reads as part of the answer, and a small
            arc plus a live degree readout shows the angle being measured. */}
        {isAngle && angleVertex && (() => {
          const showRays = readOnly && revealAngleRays.length > 0 ? revealAngleRays : angleRays;
          const stroke = readOnly && revealAngleRays.length > 0 ? REVEAL_STROKE : activeStroke;
          const [vx, vy] = [angleVertex[0] * 100, angleVertex[1] * 100];
          // Extend an arm from the vertex through the tapped point, out to a fixed radius, so every arm
          // is drawn the same length whatever the student tapped.
          const armEnd = (p, r = 34) => {
            const dx = p[0] * 100 - vx;
            const dy = p[1] * 100 - vy;
            const d = Math.hypot(dx, dy) || 1;
            return [vx + (dx / d) * r, vy + (dy / d) * r];
          };
          const arms = angleGivenRay && showRays.length < 2 ? [angleGivenRay, ...showRays] : showRays;
          const reading = angleReading(showRays);
          return (
            <>
              {!readOnly && (
                <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
                  style={{ cursor: "crosshair", pointerEvents: "all" }}
                  onClick={(e) => tapAngle(e.clientX, e.clientY)} />
              )}
              {/* The printed arm, in the figure's own ink rather than the answer colour — it is not
                  something the student drew and must not read as part of their answer. */}
              {angleGivenRay && (() => {
                const [ex, ey] = armEnd(angleGivenRay);
                return (
                  <line x1={vx} y1={vy} x2={ex} y2={ey} stroke="rgba(0,0,0,0.45)" strokeWidth={0.8}
                    strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                );
              })()}
              {showRays.map((p, i) => {
                const [ex, ey] = armEnd(p);
                return (
                  <g key={`ang${i}`} style={{ pointerEvents: "none" }}>
                    <line x1={vx} y1={vy} x2={ex} y2={ey} stroke={stroke} strokeWidth={1}
                      vectorEffect="non-scaling-stroke" />
                    <ellipse cx={ex} cy={ey} rx={pxRx(5)} ry={pxRy(5)} fill={stroke} />
                  </g>
                );
              })}
              {arms.length >= 2 && (() => {
                const [a0x, a0y] = armEnd(arms[0], 12);
                const [a1x, a1y] = armEnd(arms[1], 12);
                // sweepFlag is irrelevant to the reading — the small arc is a cue, not the measurement.
                return (
                  <path d={`M ${a0x} ${a0y} A 12 12 0 0 1 ${a1x} ${a1y}`} fill="none" stroke={stroke}
                    strokeWidth={0.6} strokeOpacity={0.7} vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: "none" }} />
                );
              })()}
              <ellipse cx={vx} cy={vy} rx={pxRx(4)} ry={pxRy(4)} fill={stroke} style={{ pointerEvents: "none" }} />
              {reading !== null && (
                <text x={vx} y={vy - 4} textAnchor="middle" fontSize={4} fill={stroke} fontWeight={700}
                  style={{ pointerEvents: "none" }}>{reading}°</text>
              )}
            </>
          );
        })()}

        {/* MARKER — tap along an axis (number line / scale / bar chart) to mark a value. The capture
            rect projects the tap onto the axis; the placed mark renders as an arrow or a filled bar. */}
        {isMarker && markerAxis && (() => {
          const a = markerAxis[0], b = markerAxis[1];
          const show = revealMarkerPt || markerPt;
          const stroke = revealMarkerPt ? REVEAL_STROKE : activeStroke;
          const dx = (b[0] - a[0]) * 100, dy = (b[1] - a[1]) * 100;
          const dl = Math.hypot(dx, dy) || 1;
          const px = -dy / dl, py = dx / dl; // unit perpendicular in viewBox units
          const wU = markerBarWidth * 100;
          return (
            <>
              {!readOnly && (
                <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
                  style={{ cursor: "pointer", pointerEvents: "all" }} onClick={(e) => tapMarker(e.clientX, e.clientY)} />
              )}
              {!isDial && (
                <line x1={a[0] * 100} y1={a[1] * 100} x2={b[0] * 100} y2={b[1] * 100}
                  stroke="rgba(25,118,210,0.25)" strokeWidth={0.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              )}
              {/* A fitted arc traces the printed curve, so the student can see the scale their tap will
                  snap to — a straight chord would run through blank paper well below it. */}
              {markerFit && markerArc && (
                <polyline
                  points={Array.from({ length: 41 }, (_, i) => markerScalePointAt(markerFit, i / 40))
                    .map((p) => `${p[0] * 100},${p[1] * 100}`).join(" ")}
                  fill="none" stroke="rgba(25,118,210,0.25)" strokeWidth={0.5}
                  vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              )}
              {/* Multi-bar: one column per spec.bars entry, each growing from the axis baseline. The empty
                  columns are outlined so the student can see WHERE a bar is expected before tapping. */}
              {markerBars && markerBars.map((bar, i) => {
                const drawn = markerBarsRevealed[i] || markerBarsDrawn[i];
                const isReveal = Boolean(markerBarsRevealed[i]);
                const bStroke = isReveal ? REVEAL_STROKE : activeStroke;
                const cx = bar.x * 100;
                const base = a[1] * 100;
                if (!drawn) {
                  return readOnly ? null : (
                    <rect key={`bar-slot-${i}`} x={cx - wU} y={Math.min(base, b[1] * 100)} width={wU * 2} height={Math.abs(base - b[1] * 100)}
                      fill="rgba(25,118,210,0.05)" stroke="rgba(25,118,210,0.35)" strokeWidth={0.4} strokeDasharray="1.5 1.5"
                      vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                  );
                }
                const top = drawn[1] * 100;
                return (
                  <rect key={`bar-${i}`} x={cx - wU} y={Math.min(base, top)} width={wU * 2} height={Math.abs(base - top)}
                    fill={isReveal ? REVEAL_FILL : "rgba(25,118,210,0.35)"} stroke={bStroke} strokeWidth={0.5}
                    vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                );
              })}
              {show && markerStyle === "bar" && (
                <polygon
                  points={[[a[0] * 100 + px * wU, a[1] * 100 + py * wU], [show[0] * 100 + px * wU, show[1] * 100 + py * wU], [show[0] * 100 - px * wU, show[1] * 100 - py * wU], [a[0] * 100 - px * wU, a[1] * 100 - py * wU]].map((p) => p.join(",")).join(" ")}
                  fill={revealMarkerPt ? REVEAL_FILL : "rgba(25,118,210,0.35)"} stroke={stroke} strokeWidth={0.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              )}
              {show && markerStyle !== "bar" && (
                <g style={{ pointerEvents: "none" }}>
                  {isDial && markerPivot && (
                    <line
                      x1={Number(markerPivot[0]) * 100} y1={Number(markerPivot[1]) * 100}
                      x2={show[0] * 100} y2={show[1] * 100}
                      stroke={stroke} strokeWidth={1.1} strokeLinecap="round" vectorEffect="non-scaling-stroke"
                    />
                  )}
                  <line x1={show[0] * 100 - px * 4} y1={show[1] * 100 - py * 4} x2={show[0] * 100 + px * 4} y2={show[1] * 100 + py * 4}
                    stroke={stroke} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
                  <ellipse cx={show[0] * 100} cy={show[1] * 100} rx={pxRx(7)} ry={pxRy(7)} fill={stroke} />
                </g>
              )}
            </>
          );
        })()}

        {/* CLOCK — drag the hour (short) and minute (long) hands about the clock centre. */}
        {isClock && (() => {
          const hands = revealHands || clockHands || (readOnly ? null : defaultHands);
          if (!hands) return null;
          const stroke = revealHands ? REVEAL_STROKE : activeStroke;
          const cx = clockCenter[0] * 100, cy = clockCenter[1] * 100;
          return (
            <g>
              <circle cx={cx} cy={cy} r={1.4} fill={stroke} style={{ pointerEvents: "none" }} />
              {[0, 1].map((idx) => {
                const h = hands[idx];
                if (!Array.isArray(h)) return null;
                const which = idx === 0 ? "hour" : "minute";
                return (
                  <g key={which}>
                    <line x1={cx} y1={cy} x2={h[0] * 100} y2={h[1] * 100}
                      stroke={stroke} strokeWidth={idx === 0 ? 1.4 : 0.9} strokeLinecap="round" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                    {!readOnly && !revealHands && (
                      <circle cx={h[0] * 100} cy={h[1] * 100} r={2.6} fill={idx === 0 ? "rgba(25,118,210,0.85)" : "rgba(25,118,210,0.55)"}
                        style={{ cursor: "grab", pointerEvents: "all" }} onPointerDown={startHand(which)} />
                    )}
                  </g>
                );
              })}
            </g>
          );
        })()}

        {/* whole-grid drag layer (admin authoring) — sits on top so a drag moves the entire grid
            instead of toggling/placing; toggle off to tap individual cells/points again. */}
        {gridMovable && onMoveGrid && (
          <rect
            x={-50} y={-50} width={200} height={200}
            fill="rgba(25,118,210,0.05)"
            style={{ cursor: gridDrag ? "grabbing" : "grab", pointerEvents: "all", touchAction: "none" }}
            onPointerDown={onGridDragStart}
          />
        )}
      </svg>

      {/* Marker label — "Mark with letter A the likelihood that…". The letter is fixed by the question (it
          is never typed, so nothing extra is graded), and it is drawn just above the mark.
          Rendered as an HTML overlay, NOT SVG <text>: the overlay viewBox is 0..100 in both axes with
          preserveAspectRatio="none", so on a wide figure (this scale is 587x107, ~5.5:1) any SVG text is
          stretched 5.5x horizontally into an unreadable smear. The pictogram icons dodge the same trap the
          same way. */}
      {/* Nothing at all is drawn on the scale until the student taps, so there is no cue as to WHICH letter
          they are placing. Show it up-front as a chip in the corner ("Place: C"), which then disappears
          once the mark is down and the letter is drawn on the scale itself. */}
      {isMarker && markerAxis && spec?.marker_label && !readOnly && !markerPt && !revealMarkerPt && (
        <Box
          sx={{
            position: "absolute",
            left: 6,
            top: 4,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.92)",
            border: "1px solid",
            borderColor: ACTIVE_STROKE,
            fontSize: 11,
            fontWeight: 600,
            color: ACTIVE_STROKE,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          Place:
          <Box component="span" sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>
            {String(spec.marker_label)}
          </Box>
        </Box>
      )}

      {isMarker && markerAxis && spec?.marker_label && (() => {
        const show = revealMarkerPt || markerPt;
        if (!show) return null;
        const colour = revealMarkerPt ? REVEAL_STROKE : activeStroke;
        return (
          <Box
            sx={{
              position: "absolute",
              left: `${show[0] * 100}%`,
              top: `${show[1] * 100}%`,
              transform: "translate(-50%, -175%)", // sit clear above the tick
              fontSize: 18,
              fontWeight: 800,
              lineHeight: 1,
              color: colour,
              // The letter is an annotation on the printed scale (whose own captions are ~11-12px), so it
              // needs to read clearly as the student's mark rather than blend into the artwork.
              textShadow: "0 0 3px #fff, 0 0 3px #fff",
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            {String(spec.marker_label)}
          </Box>
        );
      })()}

      {/* Point labels (A/B/C …) as crisp HTML overlays for the reveal + read-only review, so the letters
          stay legible on a wide figure where SVG <text> would be smeared flat by the non-uniform viewBox
          (same reason the marker letter and pictogram icons are HTML). The SVG <text> path above is
          skipped while this is active. The live-answering student keeps the inline input box / chip. */}
      {labelOverlayActive && (kind === "points" || kind === "drag-shape" || isPoly || isTransform) && (() => {
        const labelPts = revealPts ? revealPts : placedPts;
        const colour = revealPts ? REVEAL_STROKE : activeStroke;
        return labelPts.map((pt, i) => {
          const typed = (typeof pt[2] === "string" && pt[2] !== "") ? pt[2] : "";
          const lbl = typed || ((spec?.require_point_labels && !revealPts) ? "" : pointLabels[i]);
          if (!lbl) return null;
          const [x, y] = pt;
          return (
            <Box
              key={`plbl${i}`}
              sx={{
                position: "absolute",
                left: `${fx(x, y) * 100}%`,
                top: `${fy(y) * 100}%`,
                transform: "translate(-50%, -155%)", // centred just above the point/tick
                fontSize: 16,
                fontWeight: 800,
                lineHeight: 1,
                color: colour,
                textShadow: "0 0 3px #fff, 0 0 3px #fff",
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              {lbl}
            </Box>
          );
        });
      })()}

      {/* Required-label points: the corner tap now arms an edge (so the shape can still be drawn), so
          re-labelling needs its own target. This chip shows the typed letter — or "label" when the point
          is still unnamed — and re-opens the modal prompt on click. */}
      {labelRequired && !readOnly && onChange && placedPts.map((pt, i) => {
        const [x, y] = pt;
        const typed = typeof pt[2] === "string" ? pt[2].trim() : "";
        return (
          <Box
            key={`rlbl${i}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setLabelPrompt({ index: i, value: typed, isNew: false });
            }}
            sx={{
              position: "absolute",
              left: `${fx(x, y) * 100}%`,
              top: `${fy(y) * 100}%`,
              transform: "translate(8px, -50%)",
              px: 0.6,
              py: 0.125,
              fontSize: "0.75rem",
              fontWeight: 700,
              lineHeight: 1.5,
              cursor: "pointer",
              color: typed ? ACTIVE_STROKE : "#b45309",
              border: "1px dashed",
              borderColor: typed ? ACTIVE_STROKE : "#f59e0b",
              borderRadius: 0.5,
              bgcolor: "rgba(255,255,255,0.92)",
              zIndex: 5,
            }}
          >
            {typed || "label"}
          </Box>
        );
      })}

      {/* Whole-shape label ("Label the triangle A"): shown at the centroid. While answering, a chip
          re-opens the forced popup; on reveal/review the letter is painted as a crisp HTML overlay. */}
      {(() => {
        const displayLabel = revealShapeLabel || currentShapeLabel;
        // Edges-only questions ("join them in order to form a triangle. Label the triangle A"): every
        // corner is already printed, so no_new_points keeps placedPts empty and the centroid had no
        // points to sit on — the label chip never rendered, leaving no way to open the popup and no
        // place to paint a typed or revealed letter. The printed corners ARE the shape here.
        // given_points before contextPoints: on a shared grid contextPoints also carries the marks other
        // parts made (part e's translated triangle), which would drag the centroid off this shape.
        const printedCorners = Array.isArray(spec?.given_points) && spec.given_points.length >= 3
          ? spec.given_points
          : contextPoints;
        // A shape can be part printed, part plotted: ISEB 2008-2009 Q8(a) reflects a three-corner
        // pattern into a four-corner kite, so the student places ONE corner and the paper already
        // carries the other three. Averaging the placed points alone dropped the A onto that single
        // corner instead of the middle of the shape, so the printed corners join in whenever the
        // student's own points cannot make up a shape by themselves.
        const givenCorners = Array.isArray(spec?.given_points) ? spec.given_points : [];
        const withPrintedCorners = (pts) => {
          if (!Array.isArray(pts) || pts.length === 0) return null;
          if (pts.length >= 3) return pts;
          return givenCorners.length > 0 ? [...givenCorners, ...pts] : null;
        };
        const ptsForCentroid = withPrintedCorners(revealPts)
          || withPrintedCorners(placedPts)
          || (spec?.no_new_points ? printedCorners : []);
        if (ptsForCentroid.length < 3) return null;
        if (!displayLabel && !(requireShapeLabel && !readOnly && onChange)) return null;
        const cx = ptsForCentroid.reduce((s, p) => s + Number(p[0]), 0) / ptsForCentroid.length;
        const cy = ptsForCentroid.reduce((s, p) => s + Number(p[1]), 0) / ptsForCentroid.length;
        if (labelOverlayActive && displayLabel) {
          return (
            <Box
              key="shape-lbl-reveal"
              sx={{
                position: "absolute",
                left: `${fx(cx, cy) * 100}%`,
                top: `${fy(cy) * 100}%`,
                transform: "translate(-50%, -50%)",
                fontSize: 20,
                fontWeight: 800,
                lineHeight: 1,
                color: revealPts ? REVEAL_STROKE : activeStroke,
                textShadow: "0 0 3px #fff, 0 0 3px #fff",
                pointerEvents: "none",
                userSelect: "none",
                zIndex: 6,
              }}
            >
              {displayLabel}
            </Box>
          );
        }
        if (!requireShapeLabel || readOnly || !onChange) return null;
        // An opaque chip parked on the centroid sits exactly where the corners the student still has to
        // join are, and it swallows their taps (z-index 6, its own click handler). Anchor it just
        // outside the shape instead — above the topmost corner, on the shape's horizontal centre — so
        // the whole interior stays tappable while the outline is being drawn. Once the letter is typed
        // it is small and no longer in the way, but it stays outside for consistency.
        const ys = ptsForCentroid.map((p) => Number(p[1]));
        const topY = Math.min(...ys);
        const botY = Math.max(...ys);
        // A shape drawn against the top of the grid would push the chip off the canvas, so fall back to
        // sitting it under the shape instead.
        const above = fy(topY) > 0.12;
        const anchorY = above ? topY : botY;
        const chipX = fx(cx, anchorY) * 100;
        const chipY = fy(anchorY) * 100;
        // Anchoring it outside the shape is only as good as the shape SO FAR: mid-drawing the chip parks
        // above the corners placed so far, which is often exactly where the next one goes, and its own
        // handlers swallow that tap (ISEB 2009-2010 Q7 — "cannot mark the point behind the label").
        // While corners are still outstanding the chip is a caption, not a control: taps pass straight
        // through to the grid, and the prompt still opens by itself the moment the shape is complete.
        const cornersOutstanding = pointCount > 0 && placedPts.length < pointCount;
        return (
          <Box
            key="shape-lbl-chip"
            onPointerDown={cornersOutstanding ? undefined : (e) => e.stopPropagation()}
            onClick={cornersOutstanding ? undefined : (e) => {
              e.stopPropagation();
              setShapeLabelPrompt({ value: currentShapeLabel });
            }}
            sx={{
              position: "absolute",
              pointerEvents: cornersOutstanding ? "none" : "auto",
              cursor: cornersOutstanding ? "default" : "pointer",
              left: `${chipX}%`,
              top: `${chipY}%`,
              transform: above ? "translate(-50%, calc(-100% - 6px))" : "translate(-50%, 6px)",
              px: 0.75,
              py: 0.25,
              fontSize: "0.85rem",
              fontWeight: 800,
              lineHeight: 1.4,
              color: currentShapeLabel ? ACTIVE_STROKE : "#b45309",
              border: "1px dashed",
              borderColor: currentShapeLabel ? ACTIVE_STROKE : "#f59e0b",
              borderRadius: 0.5,
              bgcolor: "rgba(255,255,255,0.94)",
              zIndex: 6,
            }}
          >
            {currentShapeLabel || "label shape"}
          </Box>
        );
      })()}

      {/* Editable per-point labels — student (or admin) types a label next to each plotted point.
          Stored as the 3rd element of the point so deterministic coordinate grading is unaffected.
          When the label is REQUIRED the modal prompt (below) is the entry path instead of this inline
          box, so the student can't overlook it. */}
      {allowLabels && !labelRequired && (kind === "points" || kind === "drag-shape" || isTransform || isPoly) && !readOnly && onChange && placedPts.map((pt, i) => {
        const [x, y] = pt;
        return (
          <Box
            key={`lbl${i}`}
            component="input"
            value={typeof pt[2] === "string" ? pt[2] : ""}
            placeholder="label"
            onChange={(e) => setPointLabel(i, e.target.value)}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              left: `${fx(x, y) * 100}%`,
              top: `${fy(y) * 100}%`,
              transform: "translate(8px, -50%)",
              width: 52,
              px: 0.5,
              py: 0.125,
              fontSize: "0.75rem",
              fontWeight: 700,
              color: ACTIVE_STROKE,
              border: "1px solid",
              borderColor: ACTIVE_STROKE,
              borderRadius: 0.5,
              bgcolor: "rgba(255,255,255,0.9)",
              outline: "none",
              zIndex: 5,
            }}
          />
        );
      })}

      {/* Multi-group sector captions — each category's name is painted once, at the centre of the run
          of regions the student coloured for it, so the finished chart reads like the worked "sleeping"
          example (a coloured sector with its activity named on it). Purely derived from the assignment:
          colouring the sector labels it, so there is nothing extra to type. */}
      {isGroupShade && shadeGroups.map((g) => {
        const key = String(g.key);
        const src = revealIds ? revealGroupOfRegion : groupOfRegion;
        const mine = regions.filter((rg) => src[String(rg.id)] === key);
        if (mine.length === 0) return null;
        const cs = mine.map(regionCentroid);
        const cx = cs.reduce((s, p) => s + p[0], 0) / cs.length;
        const cy = cs.reduce((s, p) => s + p[1], 0) / cs.length;
        const c = groupColorByKey[key];
        return (
          <Box
            key={`grp${key}`}
            sx={{
              position: "absolute",
              left: `${cx * 100}%`,
              top: `${cy * 100}%`,
              transform: "translate(-50%, -50%)",
              fontSize: "0.8rem",
              fontWeight: 700,
              color: c.stroke,
              pointerEvents: "none",
              bgcolor: "rgba(255,255,255,0.82)",
              px: 0.5,
              borderRadius: 0.5,
              whiteSpace: "nowrap",
              zIndex: 5,
            }}
          >
            {g.label || key}
          </Box>
        );
      })}

      {/* Shaded-region typed labels (require_labels / label_only shade questions, e.g. a proportional
          pie chart where each shaded sector must also be captioned). Editable while answering; static
          text while read-only (reviewing the student's own answer, or the green reveal overlay).
          `label_only` regions are `given` (pre-shaded) so their box shows unconditionally — there is
          no shading step gating it. */}
      {(requireLabels || labelOnly || letterRegionIds.size > 0) && regions.map((rg) => {
        const id = String(rg.id);
        const isLetterCell = letterRegionIds.has(id);
        const shown = isLetterCell
          || (labelOnly
            ? isLabelable(rg)
            : (requireLabels && (revealIds ? revealIds.includes(id) : shadedIds.includes(id))));
        if (!shown) return null;
        const [cx, cy] = regionCentroid(rg);
        const groupColor = labelGroupColorOf(rg);
        const style = letterRegionStyle[id];
        const boxColor = style?.color || groupColor?.stroke || (revealIds ? REVEAL_STROKE : activeStroke);
        const boxWidth = style?.size || 92;
        const boxFontSize = `${Math.max(0.65, boxWidth / 115)}rem`;
        // Letter-entry cells: no persistent input box (students couldn't tell which shape a floating
        // box belonged to when several sat close together). Tapping the SHAPE itself opens a forced
        // popup (the letterPrompt dialog below) — this overlay is just a tap-through hint/answer
        // display, so pointer events must stay off or it would shadow the polygon's onClick.
        if (isLetterCell) {
          const text = revealIds ? (rg.label || letterRegionChar[id] || "") : (shadeLabels[id] || "");
          const interactive = !readOnly && !!onChange;
          if (!text && !interactive) return null;
          return (
            <Box
              key={`rlbl${id}`}
              sx={{
                position: "absolute",
                left: `${cx * 100}%`,
                top: `${cy * 100}%`,
                transform: "translate(-50%, -50%)",
                minWidth: text ? undefined : boxWidth * 0.45,
                minHeight: text ? undefined : boxWidth * 0.45,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 0.5,
                py: 0.25,
                fontSize: boxFontSize,
                fontWeight: 700,
                color: revealIds ? REVEAL_STROKE : boxColor,
                border: text ? "none" : "1.5px dashed",
                borderColor: boxColor,
                borderRadius: text ? 0.5 : "50%",
                bgcolor: text ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.55)",
                pointerEvents: "none",
                zIndex: 5,
              }}
            >
              {text || (interactive ? "?" : "")}
            </Box>
          );
        }
        if (!readOnly && onChange) {
          return (
            <Box
              key={`rlbl${id}`}
              component="input"
              value={shadeLabels[id] || ""}
              placeholder="drop or type label"
              onChange={(e) => setRegionLabel(id, e.target.value)}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => {
                e.preventDefault();
                const dragged = e.dataTransfer.getData("text/plain");
                if (dragged) setRegionLabel(id, dragged);
              }}
              sx={{
                position: "absolute",
                left: `${cx * 100}%`,
                top: `${cy * 100}%`,
                transform: "translate(-50%, -50%)",
                width: boxWidth,
                textAlign: "center",
                px: 0.5,
                py: 0.25,
                fontSize: boxFontSize,
                fontWeight: 700,
                color: boxColor,
                border: "1.5px dashed",
                borderColor: boxColor,
                borderRadius: 0.5,
                bgcolor: "rgba(255,255,255,0.92)",
                outline: "none",
                zIndex: 5,
              }}
            />
          );
        }
        const text = revealIds ? (rg.label || letterRegionChar[id] || "") : (shadeLabels[id] || "");
        if (!text) return null;
        return (
          <Box
            key={`rlbl${id}`}
            sx={{
              position: "absolute",
              left: `${cx * 100}%`,
              top: `${cy * 100}%`,
              transform: "translate(-50%, -50%)",
              fontSize: boxFontSize,
              fontWeight: 700,
              color: revealIds ? REVEAL_STROKE : boxColor,
              pointerEvents: "none",
              bgcolor: "rgba(255,255,255,0.85)",
              px: 0.5,
              borderRadius: 0.5,
              zIndex: 5,
            }}
          >
            {text}
          </Box>
        );
      })}

      {/* Pictogram icons — a row of icons (bottle / lorry / …, per spec.icon) the student fills in
          quarters. Rendered as HTML overlays (fixed aspect) so the icon isn't distorted by the
          non-uniform image viewBox. Tap an icon to cycle empty → full → ¾ → ½ → ¼ → empty. */}
      {isPictogram && pictoCount > 0 && (() => {
        const states = revealPicto || pictoStates;
        const color = revealPicto ? REVEAL_STROKE : activeStroke;
        const rx0 = Number(pictoRow.x0) || 0, ry = Number(pictoRow.y) || 0.5, rdx = Number(pictoRow.dx) || 0.1;
        const icon = pictoIcon(spec?.icon);
        const vbH = Number((icon.vb.split(" ")[3]) || 60);
        const wPx = Math.max(9, rdx * box.w * 0.66);
        const hPx = wPx * icon.aspect;
        return Array.from({ length: pictoCount }).map((_, i) => {
          const st = states[i] || 0;
          const partRects = pictoClipRects(st, icon.halfW, vbH / 2);
          return (
            <Box key={`pic${i}`}
              onClick={readOnly ? undefined : (e) => { e.stopPropagation(); cycleIcon(i); }}
              sx={{
                position: "absolute", left: `${(rx0 + i * rdx) * 100}%`, top: `${ry * 100}%`,
                transform: "translate(-50%, -50%)", width: wPx, height: hPx,
                cursor: readOnly ? "default" : "pointer", pointerEvents: readOnly ? "none" : "all", zIndex: 4,
              }}>
              <svg viewBox={icon.vb} width={wPx} height={hPx} style={{ display: "block" }}>
                {partRects && (
                  <defs>
                    <clipPath id={`h-${uid}-${i}`}>
                      {partRects.map(([x, y, w, h], r) => <rect key={r} x={x} y={y} width={w} height={h} />)}
                    </clipPath>
                  </defs>
                )}
                {st === 1 && <path d={icon.d} fill={color} fillOpacity="0.5" />}
                {partRects && <path d={icon.d} fill={color} fillOpacity="0.5" clipPath={`url(#h-${uid}-${i})`} />}
                <path d={icon.d} fill="none" stroke={color} strokeWidth="1.6" />
              </svg>
            </Box>
          );
        });
      })()}

      {/* Tally cells — tap to add marks; double-tap to remove. Admin can drag each box / corners,
          or drag on the image in place mode to append a new cell. */}
      {isTally && tallyCells.map((tallyCell, cellIndex) => {
        const marks = revealTallyCounts != null ? revealTallyCounts[cellIndex] : tallyCounts[cellIndex];
        const color = revealTallyCounts != null ? REVEAL_STROKE : activeStroke;
        const cx = Number(tallyCell.x) || 0;
        const cy = Number(tallyCell.y) || 0;
        const cw = Math.max(0.02, Number(tallyCell.w) || 0.12);
        const ch = Math.max(0.02, Number(tallyCell.h) || 0.3);
        const groups = Math.ceil(marks / tallyGroup) || 1;
        const strokeH = Math.max(14, ch * box.h * 0.55);
        const strokeGap = Math.max(3, cw * box.w * 0.06);
        const groupGap = Math.max(8, cw * box.w * 0.12);
        const strokes = [];
        for (let m = 0; m < marks; m++) {
          const g = Math.floor(m / tallyGroup);
          const i = m % tallyGroup;
          const isFifth = i === tallyGroup - 1;
          const x0 = g * (tallyGroup * strokeGap + groupGap) + i * strokeGap;
          strokes.push({ key: m, x0, isFifth });
        }
        const contentW = Math.max(
          cw * box.w * 0.85,
          groups * (tallyGroup * strokeGap + groupGap)
        );
        const canPlace = !!onCellChange && !readOnly;
        const corners = ["nw", "ne", "sw", "se"];
        const cornerStyle = (c) => ({
          position: "absolute",
          width: 12,
          height: 12,
          bgcolor: ACTIVE_STROKE,
          border: "2px solid #fff",
          borderRadius: "2px",
          zIndex: 5,
          touchAction: "none",
          cursor: c === "nw" || c === "se" ? "nwse-resize" : "nesw-resize",
          ...(c.includes("n") ? { top: -6 } : { bottom: -6 }),
          ...(c.includes("w") ? { left: -6 } : { right: -6 }),
        });
        return (
          <Box
            key={`tally-cell-${cellIndex}`}
            onClick={canPlace || readOnly ? undefined : (e) => onTallyActivate(cellIndex, e)}
            onPointerDown={canPlace ? (e) => beginTallyCellDrag("move", cellIndex, e) : undefined}
            onDoubleClick={readOnly ? undefined : (e) => {
              e.stopPropagation();
              e.preventDefault();
              if (tallyClickTimerRef.current) {
                clearTimeout(tallyClickTimerRef.current);
                tallyClickTimerRef.current = null;
              }
            }}
            sx={{
              position: "absolute",
              left: `${cx * 100}%`,
              top: `${cy * 100}%`,
              width: `${cw * 100}%`,
              height: `${ch * 100}%`,
              cursor: readOnly ? "default" : (canPlace ? "move" : "pointer"),
              pointerEvents: readOnly ? "none" : "all",
              zIndex: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: readOnly ? "transparent" : "rgba(25, 118, 210, 0.06)",
              outline: readOnly ? "none" : "1px dashed rgba(25, 118, 210, 0.35)",
              outlineOffset: -2,
              userSelect: "none",
              WebkitUserSelect: "none",
              touchAction: canPlace ? "none" : undefined,
            }}
          >
            <svg width={contentW} height={strokeH} style={{ overflow: "visible", display: "block", pointerEvents: "none" }}>
              {strokes.map(({ key, x0, isFifth }) => (
                isFifth ? (
                  <line
                    key={key}
                    x1={x0 - strokeGap * (tallyGroup - 2)}
                    y1={strokeH * 0.85}
                    x2={x0 + strokeGap * 0.4}
                    y2={strokeH * 0.15}
                    stroke={color}
                    strokeWidth={2.4}
                    strokeLinecap="round"
                  />
                ) : (
                  <line
                    key={key}
                    x1={x0}
                    y1={strokeH * 0.12}
                    x2={x0}
                    y2={strokeH * 0.88}
                    stroke={color}
                    strokeWidth={2.4}
                    strokeLinecap="round"
                  />
                )
              ))}
            </svg>
            {canPlace && corners.map((c) => (
              <Box
                key={c}
                sx={cornerStyle(c)}
                onPointerDown={(e) => beginTallyCellDrag("resize", cellIndex, e, c)}
              />
            ))}
          </Box>
        );
      })}

      {/* Admin place mode: drag anywhere on the figure to append a new tally cell rectangle. */}
      {isTally && cellPlaceMode && onCellChange && !readOnly && (
        <Box
          onPointerDown={(e) => beginTallyCellDrag("draw", -1, e)}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 6,
            cursor: "crosshair",
            touchAction: "none",
            bgcolor: "rgba(25, 118, 210, 0.04)",
          }}
        />
      )}

      {/* Letters other parts of a shared figure have placed, each in that part's colour — read-only, so
          "mark a reflex angle with R" and "mark the acute angle with A" are answered on ONE picture. */}
      {isGlyph && Array.isArray(contextLayers) && contextLayers.length > 0 && (() => {
        // Same sizing the active part uses below — with the old 0.3 default here, a letter GREW the
        // moment its part stopped being the active one (Merchant Taylors SP1 2025 Q5 a-c).
        const fontPx = grid
          ? Math.max(16, Math.min(gdx * box.w, gdy * box.h) * 0.72)
          : Math.max(18, (Number(spec?.glyph_size) || 0.09) * box.h);
        return contextLayers.flatMap((layer, li) =>
          (Array.isArray(layer?.glyphs) ? layer.glyphs : []).map((g, i) => {
            const ch = String(g?.char || "");
            if (!ch) return null;
            return (
              <Box key={`clg${li}_${i}`}
                sx={{
                  position: "absolute",
                  left: `${(g.pos?.[0] ?? 0) * 100}%`, top: `${(g.pos?.[1] ?? 0) * 100}%`,
                  transform: `translate(-50%, -50%) rotate(${g.rot || 0}deg) scale(${g.flipH ? -1 : 1}, ${g.flipV ? -1 : 1})`,
                  fontSize: `${fontPx}px`, fontWeight: 700, lineHeight: 1,
                  color: layer?.color || "#8a8a8a", opacity: 0.7,
                  fontFamily: "Arial, sans-serif", pointerEvents: "none",
                  userSelect: "none", whiteSpace: "nowrap", px: 0.25,
                }}>
                {/^[a-z]$/i.test(ch) ? ch.toUpperCase() : ch}
              </Box>
            );
          }).filter(Boolean)
        );
      })()}

      {/* Glyph letters — rendered as HTML overlays (not SVG text) so they keep correct proportions on
          non-square images. Shown for the student's answer and for the correct-answer reveal. */}
      {isGlyph && (() => {
        const show = revealGlyphs ? revealGlyphs : placedGlyphs;
        const color = revealGlyphs ? REVEAL_STROKE : activeStroke;
        // A typed letter ANNOTATES the printed figure ("mark the reflex angle with an R"), so it should
        // sit next to it at about the size of the letters already on the paper. The old default of 0.3
        // of the image height drew an R taller than the shape it was marking (Merchant Taylors SP1 2025
        // Q5). Motif symbols are a different job — they stand in for a drawn shape — so the image
        // branch below keeps the larger default; an authored glyph_size still wins for both.
        const glyphFrac = Number(spec?.glyph_size) || 0;
        const fontPx = grid
          ? Math.max(16, Math.min(gdx * box.w, gdy * box.h) * 0.72)
          : Math.max(18, (glyphFrac || 0.09) * box.h);
        const imgSize = Math.max(28, (grid ? fontPx : Math.max(12, (glyphFrac || 0.3) * box.h)) * 1.15);
        const symbolImages = (spec?.symbol_images && typeof spec.symbol_images === "object")
          ? spec.symbol_images
          : {};
        const editable = !revealGlyphs && !readOnly && onChange;
        const glyphLabel = (ch) => {
          const s = String(ch || "");
          // Letter answers are case-insensitive; unicode motif ids must not be uppercased.
          return /^[a-z]$/i.test(s) ? s.toUpperCase() : s;
        };
        return show.map((g, i) => {
          const left = (g.pos?.[0] ?? 0) * 100, top = (g.pos?.[1] ?? 0) * 100;
          const sx = g.flipH ? -1 : 1, sy = g.flipV ? -1 : 1;
          const isSel = editable && i === activeGlyph;
          const imgUrl = symbolImages[g.char] || symbolImages[glyphLabel(g.char)] || null;
          return (
            <Box key={`glh${i}`}
              tabIndex={editable ? 0 : undefined}
              onPointerDown={editable ? (e) => dragGlyph(i, e) : undefined}
              onClick={editable ? (e) => { e.stopPropagation(); setSelGlyph(i); } : undefined}
              onKeyDown={editable ? (e) => {
                const step = e.shiftKey ? 4 : 1;
                const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
                if (!d) return;
                e.preventDefault();
                e.stopPropagation();
                nudgeGlyph(i, d[0], d[1]);
              } : undefined}
              sx={{
                position: "absolute", left: `${left}%`, top: `${top}%`,
                transform: `translate(-50%, -50%) rotate(${g.rot || 0}deg) scale(${sx}, ${sy})`,
                fontSize: `${fontPx}px`, fontWeight: 700, lineHeight: 1, color,
                fontFamily: "Arial, sans-serif", pointerEvents: editable ? "auto" : "none",
                cursor: editable ? "grab" : "default",
                "&:active": editable ? { cursor: "grabbing" } : undefined,
                touchAction: editable ? "none" : undefined,
                userSelect: "none", whiteSpace: "nowrap", px: 0.25,
                outline: isSel ? "2px solid rgba(25,118,210,0.7)" : "none", outlineOffset: "2px", borderRadius: 1,
              }}>
              {imgUrl ? (
                <Box component="img" src={imgUrl} alt={glyphLabel(g.char) || "symbol"}
                  draggable={false}
                  sx={{
                    width: imgSize, height: imgSize, objectFit: "contain", display: "block",
                    // Paper motif PNGs already carry their own colours. The green CSS filter turns
                    // them into solid green squares on review/result — leave motifs untinted.
                    filter: "none",
                  }} />
              ) : (
                glyphLabel(g.char) || (editable ? "?" : "")
              )}
            </Box>
          );
        });
      })()}

      {/* Glyph toolbar — edits the SELECTED letter: type it, rotate 90°, flip, delete.
          It is anchored beside the letter it edits (not parked at the bottom of the canvas), and it only
          exists while a letter is selected — click empty canvas and it disappears. Near the right/bottom
          edge it flips to the other side so it never runs off the figure. */}
      {isGlyph && !readOnly && onChange && activeGlyph != null && (() => {
        const g = placedGlyphs[activeGlyph];
        // Rotation/mirror only change how a symbol looks if it isn't already symmetric under them —
        // a + or ○ reads identically no matter how it's turned, so offering ⟳/⇋/⤢ on those just adds
        // clutter with no visible effect. Restrict the buttons to symbols where they actually do something.
        const canOrient = g && !ROTATION_INVARIANT_CHARS.has(String(g.char || "").trim());
        // A question authored to test only rotation (or only reflection) hides the other transform's
        // buttons — spec.glyph_transforms lists which of "rotate"/"mirrorH"/"mirrorV" apply; unset means
        // every question authored before this existed, so all three still show.
        const allowedTransforms = Array.isArray(spec?.glyph_transforms) ? spec.glyph_transforms : ["rotate", "mirrorH", "mirrorV"];
        const showRotate = allowedTransforms.includes("rotate");
        const showMirrorH = allowedTransforms.includes("mirrorH");
        const showMirrorV = allowedTransforms.includes("mirrorV");
        const gx = (g?.pos?.[0] ?? 0.5) * 100;
        const gy = (g?.pos?.[1] ?? 0.5) * 100;
        const flipLeft = gx > 62;   // too close to the right edge — hang the bar to the left instead
        const above = gy > 62;      // too close to the bottom — sit above the letter instead
        const btn = { minWidth: 0, width: 32, height: 32, p: 0, fontSize: "1.1rem", lineHeight: 1, borderRadius: 1, border: "1px solid", borderColor: "rgba(25,118,210,0.35)", bgcolor: "#fff", cursor: "pointer", color: ACTIVE_STROKE, display: "flex", alignItems: "center", justifyContent: "center" };
        const btnOn = { ...btn, bgcolor: "rgba(25,118,210,0.14)", borderColor: ACTIVE_STROKE };
        return (
          <Box
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              left: `${gx}%`,
              top: `${gy}%`,
              transform: `translate(${flipLeft ? "-100%" : "0%"}, ${above ? "-140%" : "40%"})`,
              display: "flex", gap: 0.5, alignItems: "center", zIndex: 7,
              bgcolor: "rgba(255,255,255,0.98)", border: "1px solid", borderColor: ACTIVE_STROKE, borderRadius: 1.5, p: 0.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
          >
            {/* Symbol-palette questions (e.g. reflect/rotate a tile of +, ○, △, ↔) let the student tap a
                fixed symbol instead of typing — some symbols aren't on a keyboard. Falls back to the free
                text input when no palette is set. */}
            {Array.isArray(spec?.symbol_palette) && spec.symbol_palette.length > 0 ? (
              <Box sx={{ display: "flex", gap: 0.25 }}>
                {spec.symbol_palette.map((sym) => {
                  const key = String(sym);
                  const img = spec?.symbol_images?.[key];
                  return (
                    <Box key={key} component="button" type="button" title={`Place ${key}`}
                      onClick={() => updateGlyph(activeGlyph, { char: key })}
                      sx={{
                        ...(g.char === key ? btnOn : btn),
                        width: img ? 40 : btn.width,
                        height: img ? 40 : btn.height,
                        p: img ? 0.25 : btn.p,
                      }}>
                      {img ? (
                        <Box component="img" src={img} alt={key} draggable={false}
                          sx={{ width: 32, height: 32, objectFit: "contain", display: "block" }} />
                      ) : key}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box component="input" autoFocus value={g.char || ""} maxLength={2} placeholder="?"
                onChange={(e) => updateGlyph(activeGlyph, { char: e.target.value.toUpperCase().slice(0, 2) })}
                // autoFocus puts the caret here the moment a letter is selected, so you can just type. That
                // takes focus off the glyph, so the arrow-key nudge has to work from the input too —
                // otherwise selecting a letter would silently disable moving it with the keyboard.
                onKeyDown={(e) => {
                  const step = e.shiftKey ? 4 : 1;
                  const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
                  if (d) {
                    e.preventDefault();
                    nudgeGlyph(activeGlyph, d[0], d[1]);
                    return;
                  }
                  if (e.key === "Escape") setSelGlyph(null);
                }}
                sx={{ width: 38, height: 32, textAlign: "center", fontWeight: 700, border: "1px solid", borderColor: "rgba(25,118,210,0.35)", borderRadius: 1, outline: "none", fontSize: "1.1rem", color: ACTIVE_STROKE, bgcolor: "#fff" }} />
            )}
            {canOrient && (
              <>
                {showRotate && (
                  <Box component="button" type="button" title="Rotate 90°" onClick={() => updateGlyph(activeGlyph, { rot: ((g.rot || 0) + 90) % 360 })} sx={btn}>⟳</Box>
                )}
                {/* The two flips are MUTUALLY EXCLUSIVE. Applying both is not a mirror image at all — it is a
                    180° rotation — but for symmetric letters (O, W) it looks identical to a single flip, so a
                    student who pressed both produced a picture indistinguishable from the model answer and was
                    still marked wrong, with nothing on screen to explain why. Turning one flip on now turns the
                    other off, so "flipped" always means a genuine reflection. Use ⟳ to rotate. */}
                {showMirrorH && (
                  <Box component="button" type="button" title="Mirror left-right (a reflection in a vertical line)"
                    onClick={() => updateGlyph(activeGlyph, { flipH: !g.flipH, flipV: false })}
                    sx={g.flipH ? btnOn : btn}>⇋</Box>
                )}
                {showMirrorV && (
                  <Box component="button" type="button" title="Mirror up-down (a reflection in a horizontal line)"
                    onClick={() => updateGlyph(activeGlyph, { flipV: !g.flipV, flipH: false })}
                    sx={g.flipV ? btnOn : btn}>⤢</Box>
                )}
              </>
            )}
            <Box component="button" type="button" title="Remove this letter" onClick={() => removeGlyph(activeGlyph)} sx={{ ...btn, color: "#c62828", borderColor: "rgba(198,40,40,0.4)" }}>✕</Box>
          </Box>
        );
      })()}
      </Box>

      {/* Multi-group shade picker — one swatch per category. Tap a swatch to make it active, then tap
          the chart's regions to colour them in that category. The running count is shown so the student
          can check they've coloured as many regions as the data table calls for (the count itself is
          never given — they work it out). Lives outside the image wrapper so it lays out in normal flow. */}
      {isGroupShade && !readOnly && onChange && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
            Pick an activity, then tap the chart to colour it in:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
            {shadeGroups.map((g) => {
              const key = String(g.key);
              const c = groupColorByKey[key];
              const active = currentGroup === key;
              const n = Object.values(groupOfRegion).filter((v) => v === key).length;
              return (
                <Box
                  key={key}
                  onClick={() => setActiveGroup(key)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75,
                    px: 1.25, py: 0.5, borderRadius: 1, cursor: "pointer", userSelect: "none",
                    fontSize: "0.8rem", fontWeight: 700, color: c.stroke,
                    bgcolor: active ? c.fill : "transparent",
                    border: active ? "2px solid" : "1.5px solid",
                    borderColor: c.stroke,
                    boxShadow: active ? `0 0 0 2px ${c.fill}` : "none",
                  }}
                >
                  <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: c.fill, border: "1px solid", borderColor: c.stroke }} />
                  {g.label || key}
                  {n > 0 && (
                    <Box component="span" sx={{ fontWeight: 600, opacity: 0.85 }}>({n})</Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Draggable label chips (label_only shade questions) — a word bank of the candidate captions.
          Drag one onto a part (its label box, or the part itself) to caption it; typing in the box
          still works. Lives OUTSIDE the image wrapper (which is position:relative / line-height:0 and
          hosts only absolutely-positioned overlays) so it lays out in normal flow beneath the diagram.
          A chip dims once its text is in use, but stays draggable so it can be moved to another part. */}
      {labelOnly && !readOnly && onChange && (() => {
        const chipLabels = labelOptions;
        if (chipLabels.length === 0) return null;
        const usedLabels = Object.values(shadeLabels).map((v) => String(v || "").trim().toLowerCase());
        return (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", mr: 0.5 }}>
              Drag a label onto a part:
            </Typography>
            {chipLabels.map((lbl) => {
              const used = usedLabels.includes(String(lbl).trim().toLowerCase());
              return (
                <Box
                  key={lbl}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", lbl)}
                  sx={{
                    px: 1.25, py: 0.5, borderRadius: 4, cursor: "grab", userSelect: "none",
                    fontSize: "0.8rem", fontWeight: 700,
                    color: used ? "text.disabled" : ACTIVE_STROKE,
                    bgcolor: used ? "rgba(0,0,0,0.04)" : "rgba(25,118,210,0.10)",
                    border: "1.5px solid",
                    borderColor: used ? "divider" : ACTIVE_STROKE,
                    opacity: used ? 0.55 : 1,
                  }}
                >
                  {lbl}
                </Box>
              );
            })}
          </Box>
        );
      })()}

      {/* Forced label prompt — graded-label plot questions open this on placing/tapping a point so the
          student can't leave a correctly-plotted point unlabelled (the on-grid inline box was missed).
          A blank confirm on a just-placed point removes it; on an existing point it keeps the point. */}
      {labelRequired && labelPrompt && !readOnly && onChange && (() => {
        const commit = (raw) => {
          const label = (raw || "").trim();
          // Only the points the spec actually names are graded on their label (e.g. "plot D and draw the
          // parallelogram ABCD" names D alone — A/B/C are corners of the shape, not answers). Dropping a
          // corner just because the student skipped its label would make the shape impossible to close,
          // so a blank label only discards the point when EVERY point of this question is named.
          const everyPointNamed = namedPointCount > 0 && pointCount > 0 && namedPointCount >= pointCount;
          if (labelPrompt.isNew && label === "" && everyPointNamed) {
            emitValue(placedPts.filter((_, i) => i !== labelPrompt.index), edgesAvailable ? manualEdges : undefined);
          } else {
            setPointLabel(labelPrompt.index, label);
          }
          setLabelPrompt(null);
        };
        const remove = () => {
          emitValue(placedPts.filter((_, i) => i !== labelPrompt.index), edgesAvailable ? manualEdges : undefined);
          setLabelPrompt(null);
        };
        const allNamed = namedPointCount > 0 && pointCount > 0 && namedPointCount >= pointCount;
        return (
          <Dialog open onClose={() => commit(labelPrompt.value)} maxWidth="xs" fullWidth>
            <DialogTitle>Label this point</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {/* Never list the expected labels here. On a "label the axis" question the point_labels
                    ARE the answer (0, 4, 8, 12 … the scale the student is asked to work out), so the
                    prompt was printing the mark scheme in the box the student had to fill in. */}
                Type the label that names this point. It’s part of the answer — a point on its own isn’t enough.
                {!allNamed && " Corners that aren’t named can be left blank — just tap Skip."}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Point label"
                value={labelPrompt.value}
                onChange={(e) => setLabelPrompt({ ...labelPrompt, value: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(labelPrompt.value); } }}
                inputProps={{ maxLength: 12 }}
              />
            </DialogContent>
            <DialogActions>
              <Button color="error" onClick={remove}>Remove point</Button>
              {!allNamed && (
                <Button onClick={() => commit("")}>Skip</Button>
              )}
              <Button variant="contained" onClick={() => commit(labelPrompt.value)}>Save label</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Forced shape-name prompt — "Label the triangle A" / "Label your shape B". Opens when the shape
          is complete (all corners placed, or Fill in) so the student can't leave a correct drawing
          unlabelled. Same modal pattern as the per-point label prompt above. */}
      {requireShapeLabel && shapeLabelPrompt && !readOnly && onChange && (
        <Dialog open onClose={() => { if ((shapeLabelPrompt.value || "").trim()) { setShapeLabel(shapeLabelPrompt.value); setShapeLabelPrompt(null); } }} maxWidth="xs" fullWidth>
          <DialogTitle>Label this shape</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Type the letter that names this shape (for example A, B or C). The label is part of the answer — drawing the shape alone isn’t enough.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Shape label"
              value={shapeLabelPrompt.value}
              onChange={(e) => setShapeLabelPrompt({ ...shapeLabelPrompt, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const label = (shapeLabelPrompt.value || "").trim();
                  if (!label) return;
                  setShapeLabel(label);
                  setShapeLabelPrompt(null);
                }
              }}
              inputProps={{ maxLength: 8 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShapeLabelPrompt(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!(shapeLabelPrompt.value || "").trim()}
              onClick={() => {
                setShapeLabel(shapeLabelPrompt.value);
                setShapeLabelPrompt(null);
              }}
            >
              Save label
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Forced letter-entry prompt — "write C in this section". Opens when the student taps a
          letter-entry cell's shape (see toggleRegion), instead of an always-visible floating input
          box that was too easy to mis-attribute to the wrong region in a crowded figure. */}
      {letterPrompt && !readOnly && onChange && (
        <Dialog open onClose={() => setLetterPrompt(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Label this shape</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Type the letter that goes in this section. The label is part of the answer.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Letter"
              value={letterPrompt.value}
              onChange={(e) => setLetterPrompt({ ...letterPrompt, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setRegionLabel(letterPrompt.id, letterPrompt.value);
                  setLetterPrompt(null);
                }
              }}
              inputProps={{ maxLength: 4 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLetterPrompt(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                setRegionLabel(letterPrompt.id, letterPrompt.value);
                setLetterPrompt(null);
              }}
            >
              Save letter
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {barsNeedStudentLabels && markerBars ? (
        <Box sx={{ display: "flex", gap: 1, mt: 0.5, px: 0.5, width: "100%", flexWrap: "nowrap", overflowX: "auto" }}>
          {markerBars.map((bar, i) => (
            <TextField
              key={`bar-lab-${i}`}
              size="small"
              label={readOnly ? (bar.label || `Column ${i + 1}`) : `Column ${i + 1}`}
              placeholder="flavour"
              value={readOnly && revealValue
                ? (Array.isArray(revealValue) && typeof revealValue.find?.((p) => Array.isArray(p) && Math.abs(Number(p[0]) - bar.x) < 0.02)?.[2] === "string"
                  ? revealValue.find((p) => Array.isArray(p) && Math.abs(Number(p[0]) - bar.x) < 0.02)[2]
                  : (bar.label || ""))
                : (barLabelsFromValue[i] || "")}
              onChange={(e) => setBarLabel(i, e.target.value)}
              disabled={readOnly}
              sx={{ minWidth: 90, flex: "1 1 90px" }}
              inputProps={{ "aria-label": `Bar column ${i + 1} name` }}
            />
          ))}
        </Box>
      ) : null}
      {/* An answer of "none" draws nothing, so on a read-only canvas (review, or the revealed solution
          for a shape that has no lines of symmetry) it has to be stated in words — an empty figure under
          the heading "Your shape" / "Correct solution" otherwise reads as a rendering failure. */}
      {kind === "lines" && (declaredNone || (readOnly && revealLines && revealLines.length === 0 && Number(spec?.target_count) === 0)) ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontWeight: 600, color: readOnly ? "success.main" : "primary.main" }}>
          {noneLabel}
        </Typography>
      ) : null}
      {/* Every part of a shared grid draws on ONE figure, so without a key the student cannot tell which
          marks are the part they have open and which belong to a part they finished earlier. */}
      {Array.isArray(contextLayers) && contextLayers.some((l) => l?.label) ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center", mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            On this grid:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: activeStroke }} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>this part</Typography>
          </Box>
          {contextLayers.filter((l) => l?.label).map((l) => (
            <Box key={`leg-${l.id}`} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: l.color || "#8a8a8a" }} />
              <Typography variant="caption" color="text.secondary">
                {`part ${String(l.label).replace(/[).]\s*$/, "")}`}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * `construction` answers are built on the calibrated JSXGraph board (1 unit = 1 cm) instead of an image
 * grid: compass arcs, exact lengths and angles. Every caller of the canvas — take pages, review, admin
 * preview — gets the board through this one switch.
 */
const InteractiveDiagramCanvas = (props) => {
  const { spec, value, onChange, readOnly = false, revealValue = null } = props;
  if (spec?.kind === "construction") {
    const shown = readOnly && revealValue ? revealValue : value;
    return (
      <ConstructGeometryBoard
        // Remount per question (two parts can share an identical student spec once the checks are
        // stripped, so board_id tells them apart) and between the student's work and the model answer.
        key={`${spec.board_id || JSON.stringify(spec)}|${readOnly && revealValue ? "reveal" : "work"}`}
        value={shown && typeof shown === "object" && !Array.isArray(shown) ? shown : null}
        onChange={readOnly ? null : onChange}
        readOnly={readOnly}
        given={{ points: spec.given_points, segments: spec.given_segments }}
        measures={spec.measures}
        extent={spec.board_extent_cm}
        title={readOnly ? null : "Construction board (1 unit = 1 cm)"}
      />
    );
  }
  return <InteractiveDiagramCanvasInner {...props} />;
};

export default InteractiveDiagramCanvas;
